// ============================================================
// WORK ORDER MANAGEMENT SYSTEM — Super Foto DG X Press
// All modules: WO, Estimates, Invoices (GST A5)
// ============================================================

import { useState, useEffect, useCallback, useRef } from "react";
import { createClient } from "@supabase/supabase-js";
import { PaymentsPage, LedgerPage, ReconPage, UnmatchedCreditsPage } from "./PaymentsLedger";
import { PurchasesPage } from "./Purchases";
import { HistoricalImportPage } from "./HistoricalImport";
import { SalesPage } from "./SalesPage";
import { DirectWorkPage, DirectWorkModal } from "./DirectWork";
import { AuthProvider, useAuth, canAccess, UsersPage } from "./Auth";
import { TempLedgerPage } from "./TempLedger";
import { JobSalesPage } from "./JobSales";
import { fmtDMY } from "./dateUtils";
import { matchItems } from "./matchItems";
import { INDIAN_STATES, stateCode } from "./indianStates";
import { buildEInvoiceJSON, downloadEInvoiceJSON } from "./einvoice";
const SUPABASE_URL  = process.env.REACT_APP_SUPABASE_URL  || "https://ryokwpjntufianxwdyqz.supabase.co";
const SUPABASE_ANON = process.env.REACT_APP_SUPABASE_ANON || "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InJ5b2t3cGpudHVmaWFueHdkeXF6Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzY0MDk1MTcsImV4cCI6MjA5MTk4NTUxN30.V9Bs4-Uijww6cB1jS1HBqL1RIHfRgs4WbiYP_mVmkFA";
const supabase = createClient(SUPABASE_URL, SUPABASE_ANON);

// Indian FY runs April–March. Was hardcoded as "FY 2025–26" everywhere,
// which silently went stale the moment FY 2026-27 started on 1 April —
// computed from today's date instead so it's never wrong again.
function getCurrentFYLabel() {
  const now = new Date();
  const y = now.getFullYear();
  const startYear = now.getMonth() >= 3 ? y : y - 1; // getMonth() 3 = April
  return `FY ${startYear}–${String(startYear+1).slice(-2)}`;
}

// Compact code form ("2627") for next_invoice_num — was hardcoded "2526",
// which silently mis-numbered every invoice once FY 2026-27 started because
// next_invoice_num had no row for any other fy and returned null.
function getCurrentFYCode() {
  const now = new Date();
  const y = now.getFullYear();
  const startYear = now.getMonth() >= 3 ? y : y - 1;
  return `${String(startYear).slice(-2)}${String(startYear+1).slice(-2)}`;
}

// ── PAGINATED FETCH HELPER ────────────────────────────────────
async function fetchAll(table, query = {}) {
  let all = [];
  let from = 0;
  const pageSize = 1000;
  while (true) {
    let req = supabase.from(table).select(query.select || "*");
    if (query.eq)     Object.entries(query.eq).forEach(([k,v]) => { req = req.eq(k,v); });
    if (query.neq)    Object.entries(query.neq).forEach(([k,v]) => { req = req.neq(k,v); });
    if (query.order)  req = req.order(query.order, { ascending: query.ascending ?? true });
    req = req.range(from, from + pageSize - 1);
    const { data, error } = await req;
    if (error || !data || data.length === 0) break;
    all = [...all, ...data];
    if (data.length < pageSize) break;
    from += pageSize;
  }
  return all;
}

const COMPANY = {
  name:    "Super Foto DG X Press",
  address: "70, 3rd Floor, 4th Main, KSSIDC,\nRajajinagar Industrial Estate,\nBengaluru 560010",
  gstin:   "29ADSFS6768A1ZF",
  phone:   "9901939263",
  email:   "superfotodgxpress@gmail.com",
  state:      "Karnataka",
  stateCode:  "29",
  addr1:   "70, 3rd Floor, 4th Main, KSSIDC, Rajajinagar Industrial Estate",
  loc:     "Bengaluru",
  pin:     "560010",
};

const MIX_FINISHES = [
  "Glossy","Matt","Velvet","Emboss","Transparent",
  "Metallic","Luster","Holo dot","Holo star","Sparkle","3D","Lenin","Canvas"
];
const IS_MIX_FINISH = (f) => f && f.endsWith("+Mix");

const BOOK_TYPE_DEFAULTS = {
  "Photobook": [
    { slotKey:"pad",    slotLabel:"Pad",         filterFn: p => p.name ? p.name.toUpperCase().startsWith("PAD") : false },
    { slotKey:"paper",  slotLabel:"Paper",        filterFn: p => p.name ? p.name.toLowerCase().startsWith("paper-") : false },
    { slotKey:"addons", slotLabel:"Add Ons",      filterFn: p => p.name ? p.name.toLowerCase().startsWith("add ons") : false },
  ],
  "Cake": [
    // Broad "starts with cake" match — the exact-name "Cake Charges" this
    // used to require doesn't exist in the product catalog (the real
    // product is named "CAKE"), which silently left this slot's search
    // box with nothing to select and no way to save a special price.
    { slotKey:"cake",   slotLabel:"Cake Charges", filterFn: p => p.name ? p.name.toLowerCase().startsWith("cake") : false },
    { slotKey:"paper",  slotLabel:"Paper",        filterFn: p => p.name ? p.name.toLowerCase().startsWith("paper-") : false },
    { slotKey:"addons", slotLabel:"Add Ons",      filterFn: p => p.name ? p.name.toLowerCase().startsWith("add ons") : false },
  ],
  "Print+Lamination": [
    { slotKey:"paper",  slotLabel:"Paper",        filterFn: p => p.name ? p.name.toLowerCase().startsWith("paper-") : false },
    { slotKey:"addons", slotLabel:"Add Ons",      filterFn: p => p.name ? p.name.toLowerCase().startsWith("add ons") : false },
  ],
  "Print Only": [
    { slotKey:"paper",  slotLabel:"Paper",        filterFn: p => p.name ? p.name.toLowerCase().startsWith("paper-") : false },
    { slotKey:"addons", slotLabel:"Add Ons",      filterFn: p => p.name ? p.name.toLowerCase().startsWith("add ons") : false },
  ],
};

function getItemsForLine(line, bookType, products) {
  if (!line.slotKey) return products;
  const slots = BOOK_TYPE_DEFAULTS[bookType] || [];
  const slot = slots.find(s => s.slotKey === line.slotKey);
  if (!slot) return products;
  return products.filter(p => p.name ? slot.filterFn(p) : false);
}
// ── PAPER VARIANT CONSTANTS ───────────────────────────────────
const PAPER_TYPES = [
  "NT Glossy","NT Matt","NT Velvet","Luster Paper","Tearable",
  "Canvas","Lenin","Emboss","Transparent","Metallic",
  "Holo Dot","Holo Star","Sparkle","3D","Photo Matt"
];
// Maps lamination finish / mix finish name → paper type variant key
const FINISH_TO_PAPER_TYPE = {
  // Lamination finish → paper type
  "NTG":          "NT Glossy",
  "Matt":         "NT Matt",
  "Velvet":       "NT Velvet",
  "Luster Paper": "Luster Paper",
  "Tearable":     "Tearable",
  "Canvas":       "Canvas",
  "Lenin":        "Lenin",
  "Photo Matt":   "Photo Matt",
  // Mix finish checkbox labels → paper type (exact match to PAPER_TYPES)
  "Glossy":       "NT Glossy",
  "Emboss":       "Emboss",
  "Transparent":  "Transparent",
  "Metallic":     "Metallic",
  "Luster":       "Luster Paper",
  "Holo dot":     "Holo Dot",
  "Holo star":    "Holo Star",
  "Sparkle":      "Sparkle",
  "3D":           "3D",
};
const COLOR_COUNTS = ["4 Color","7 Color"];
const MICRONS      = ["125 Micron","200 Micron"];
const SMALL_SIZES  = ["8x24","9x24","12x18"];
const SIZE_GROUPS  = ["small","standard"];

const getSizeGroup = (size) => SMALL_SIZES.includes(size) ? "small" : "standard";

// Distinct from getSizeGroup() above (which drives the existing paper-mix
// product_variants pricing, "small" vs "standard" only, unchanged). Regular
// per-product customer pricing (Pad, Sheets, etc.) uses a third bucket for
// 10x26 — it's priced separately from the other standard sizes.
const getProductSizeGroup = (sz) => {
  if (!sz) return null;
  if (SMALL_SIZES.includes(sz)) return "small";
  if (sz === "10x26") return "10x26";
  return "standard";
};

const SERIES = [
  {code:"OUT", label:"Outstation", prefix:"O"},
  {code:"CTR", label:"Counter",    prefix:"C"},
  {code:"TPF", label:"TPF",        prefix:"T"},
  {code:"PRV", label:"PRV",        prefix:"P"},
];

const fmt  = (n) => "₹" + parseFloat(n||0).toLocaleString("en-IN",{minimumFractionDigits:2,maximumFractionDigits:2});
const fmtN = (n) => parseFloat(n||0).toLocaleString("en-IN",{minimumFractionDigits:2,maximumFractionDigits:2});

// ── IS BOOK TYPE ──────────────────────────────────────────────
const isBookType = (bt) => bt === "Photobook" || bt === "Cake";

const css = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500&family=Fraunces:ital,opsz,wght@0,9..144,300;0,9..144,600;1,9..144,300&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  :root {
    /* "Crisp White" palette — picked from 5 options after staff found the
       original muted cream/olive palette dull and hard to read. Every text
       color here clears 4.5:1 contrast against its typical background
       (measured, not eyeballed) — --ink3, --warn and --accent2 in the
       previous palette did not. */
    --ink:#161616; --ink2:#454545; --ink3:#5c5c58;
    --paper:#ffffff; --paper2:#f2f2f0; --paper3:#e4e4e0;
    --accent:#1f6e4a; --accent2:#1c7d54; --accent-l:#dcf5e3;
    --danger:#b7101d; --danger-l:#fbe4e5;
    --warn:#9a4a00; --warn-l:#fdf0e2;
    --info:#0d5bb8; --info-l:#e1eefc;
    --radius:10px;
    --shadow:0 2px 8px rgba(0,0,0,0.08),0 1px 2px rgba(0,0,0,0.05);
    --shadow-lg:0 8px 32px rgba(0,0,0,0.12),0 2px 8px rgba(0,0,0,0.06);
    --mono:'DM Mono',monospace; --serif:'Fraunces',Georgia,serif;
  }
  body{background:var(--paper);color:var(--ink);font-family:var(--mono);font-size:14px;line-height:1.6;min-height:100vh;}
  .app{display:flex;min-height:100vh;}
 .sidebar{width:220px;min-height:100vh;background:#0c2d4a;display:flex;flex-direction:column;position:fixed;top:0;left:0;bottom:0;z-index:100;}display:flex;flex-direction:column;position:fixed;top:0;left:0;bottom:0;z-index:100;}
  .sidebar-brand{padding:28px 24px 20px;border-bottom:1px solid rgba(255,255,255,0.08);}
.sidebar-brand h1{font-family:var(--serif);font-size:19px;font-weight:600;color:#fff;line-height:1.2;letter-spacing:-0.3px;}
.sidebar-brand p{font-size:11px;color:rgba(122,179,212,0.7);margin-top:4px;letter-spacing:0.8px;text-transform:uppercase;}
.sidebar-nav{flex:1;padding:12px 0;overflow-y:auto;}
.nav-section{padding:8px 16px 4px;font-size:10px;letter-spacing:1.2px;text-transform:uppercase;color:#7ab3d4;}
.nav-item{display:flex;align-items:center;gap:10px;padding:9px 24px;color:rgba(255,255,255,0.65);cursor:pointer;font-size:13px;transition:all 0.15s;border-left:3px solid transparent;user-select:none;}
.nav-item:hover{background:rgba(122,179,212,0.1);color:#fff;}
.nav-item.active{background:rgba(122,179,212,0.15);color:#7ab3d4;border-left-color:#7ab3d4;}
  .nav-icon{font-size:15px;width:18px;text-align:center;}
.sidebar-footer{padding:16px 24px;font-size:11px;color:#7ab3d4;border-top:1px solid rgba(122,179,212,0.15);}
  .main{margin-left:220px;flex:1;padding:32px 36px;max-width:1200px;}
  .page-header{display:flex;align-items:flex-end;justify-content:space-between;margin-bottom:28px;padding-bottom:20px;border-bottom:1px solid var(--paper3);}
  .page-title{font-family:var(--serif);font-size:29px;font-weight:300;color:var(--ink);letter-spacing:-0.5px;line-height:1;}
  .page-subtitle{font-size:12px;color:var(--ink3);margin-top:6px;letter-spacing:0.3px;}
  .btn{display:inline-flex;align-items:center;gap:6px;padding:8px 16px;border-radius:var(--radius);font-family:var(--mono);font-size:13px;cursor:pointer;border:none;transition:all 0.15s;font-weight:500;white-space:nowrap;}
  .btn-primary{background:var(--accent);color:#fff;}
  .btn-primary:hover{background:var(--accent2);transform:translateY(-1px);box-shadow:var(--shadow);}
  .btn-primary:disabled{opacity:0.4;cursor:not-allowed;transform:none;}
  .btn-ghost{background:transparent;color:var(--ink2);border:1px solid var(--paper3);}
  .btn-ghost:hover{background:var(--paper2);}
  .btn-danger{background:transparent;color:var(--danger);border:1px solid var(--danger-l);}
  .btn-danger:hover{background:var(--danger-l);}
  .btn-info{background:var(--info);color:#fff;}
  .btn-info:hover{opacity:0.9;}
  .btn-sm{padding:5px 10px;font-size:12px;}
  .toolbar{display:flex;gap:10px;margin-bottom:20px;align-items:center;flex-wrap:wrap;}
  .search-wrap{position:relative;flex:1;max-width:320px;}
  .search-wrap input{width:100%;padding:8px 12px 8px 34px;border:1px solid var(--paper3);border-radius:var(--radius);background:#fff;font-family:var(--mono);font-size:13px;color:var(--ink);outline:none;transition:border 0.15s;}
  .search-wrap input:focus{border-color:var(--accent2);}
  .search-icon{position:absolute;left:10px;top:50%;transform:translateY(-50%);color:var(--ink3);font-size:14px;pointer-events:none;}
  .filter-pill{padding:6px 12px;border-radius:20px;font-size:12px;cursor:pointer;border:1px solid var(--paper3);background:#fff;color:var(--ink2);font-family:var(--mono);transition:all 0.15s;}
  .filter-pill.active{background:var(--accent);color:#fff;border-color:var(--accent);}
  .card{background:#fff;border-radius:var(--radius);box-shadow:var(--shadow);overflow:hidden;}
  table{width:100%;border-collapse:collapse;}
  thead tr{background:var(--paper2);border-bottom:1px solid var(--paper3);}
  th{padding:10px 16px;text-align:left;font-size:11px;letter-spacing:0.8px;text-transform:uppercase;color:var(--ink3);font-weight:500;white-space:nowrap;}
  tbody tr{border-bottom:1px solid var(--paper2);transition:background 0.1s;}
  tbody tr:last-child{border-bottom:none;}
  tbody tr:hover{background:var(--paper);}
  td{padding:12px 16px;font-size:13px;color:var(--ink);vertical-align:middle;}
  .badge{display:inline-block;padding:2px 8px;border-radius:12px;font-size:11px;font-weight:500;letter-spacing:0.3px;}
  .badge-green{background:var(--accent-l);color:var(--accent);}
  .badge-amber{background:var(--warn-l);color:var(--warn);}
  .badge-red{background:var(--danger-l);color:var(--danger);}
  .badge-blue{background:var(--info-l);color:var(--info);}
  .badge-gray{background:var(--paper2);color:var(--ink3);}
  .badge-purple{background:#f3e8ff;color:#6a1b9a;}
  .mono-text{font-family:var(--mono);color:var(--ink2);}
  .modal-overlay{position:fixed;inset:0;background:rgba(0,0,0,0.45);z-index:200;display:flex;align-items:center;justify-content:center;padding:20px;animation:fadeIn 0.15s ease;}
  @keyframes fadeIn{from{opacity:0}to{opacity:1}}
  .modal{background:#fff;border-radius:14px;box-shadow:var(--shadow-lg);width:100%;max-width:520px;max-height:90vh;overflow-y:auto;animation:slideUp 0.2s ease;}
  .modal-lg{max-width:940px;}
  .modal-xl{max-width:720px;}
  @keyframes slideUp{from{transform:translateY(16px);opacity:0}to{transform:translateY(0);opacity:1}}
  .modal-header{padding:24px 28px 0;display:flex;align-items:flex-start;justify-content:space-between;}
  .modal-title{font-family:var(--serif);font-size:21px;font-weight:300;color:var(--ink);letter-spacing:-0.3px;}
  .modal-close{background:none;border:none;cursor:pointer;font-size:19px;color:var(--ink3);padding:4px;line-height:1;transition:color 0.15s;}
  .modal-close:hover{color:var(--ink);}
  .modal-body{padding:20px 28px 28px;}
  .form-grid{display:grid;grid-template-columns:1fr 1fr;gap:16px;}
  .form-grid .span2{grid-column:span 2;}
  .form-group{display:flex;flex-direction:column;gap:5px;}
  label{font-size:11px;letter-spacing:0.6px;text-transform:uppercase;color:var(--ink3);font-weight:500;}
  input,select,textarea{padding:8px 12px;border:1px solid var(--paper3);border-radius:8px;font-family:var(--mono);font-size:13px;color:var(--ink);background:var(--paper);outline:none;transition:border 0.15s,background 0.15s;}
  input:focus,select:focus,textarea:focus{border-color:var(--accent2);background:#fff;}
  textarea{resize:vertical;min-height:72px;}
  .form-actions{display:flex;gap:10px;justify-content:flex-end;margin-top:24px;padding-top:20px;border-top:1px solid var(--paper2);}
  .toast-container{position:fixed;bottom:24px;right:24px;z-index:400;display:flex;flex-direction:column;gap:8px;}
  .toast{padding:12px 18px;border-radius:10px;font-size:13px;box-shadow:var(--shadow-lg);animation:toastIn 0.2s ease;max-width:320px;}
  @keyframes toastIn{from{transform:translateX(20px);opacity:0}}
  .toast-success{background:var(--accent);color:#fff;}
  .toast-error{background:var(--danger);color:#fff;}
  .toast-info{background:var(--info);color:#fff;}
  .empty-state{text-align:center;padding:60px 20px;color:var(--ink3);}
  .empty-state .icon{font-size:37px;margin-bottom:12px;opacity:0.5;}
  .empty-state p{font-size:14px;}
  .stats-row{display:grid;grid-template-columns:repeat(4,1fr);gap:16px;margin-bottom:28px;}
  .stat-card{background:#fff;border-radius:var(--radius);padding:18px 20px;box-shadow:var(--shadow);border-left:3px solid var(--accent2);}
  .stat-value{font-family:var(--serif);font-size:29px;font-weight:300;color:var(--ink);line-height:1;}
  .stat-label{font-size:11px;color:var(--ink3);letter-spacing:0.6px;text-transform:uppercase;margin-top:6px;}
  .spinner{display:inline-block;width:16px;height:16px;border:2px solid var(--paper3);border-top-color:var(--accent);border-radius:50%;animation:spin 0.7s linear infinite;}
  @keyframes spin{to{transform:rotate(360deg)}}
  .loading-row td{padding:40px;text-align:center;color:var(--ink3);}
  .wo-series-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-bottom:8px;}
  .series-card{border:2px solid var(--paper3);border-radius:10px;padding:14px 16px;cursor:pointer;transition:all 0.15s;background:#fff;}
  .series-card:hover{border-color:var(--accent2);}
  .series-card.selected{border-color:var(--accent);background:var(--accent-l);}
  .series-code{font-family:var(--mono);font-weight:500;font-size:14px;color:var(--ink);}
  .series-label{font-size:11px;color:var(--ink3);margin-top:3px;}
  .series-next{font-size:11px;color:var(--accent);margin-top:6px;font-weight:500;}
  .line-items-wrap{border:1px solid var(--paper3);border-radius:10px;overflow:hidden;margin-top:8px;}
  .line-items-header{display:grid;gap:8px;padding:8px 12px;background:var(--paper2);font-size:11px;letter-spacing:0.6px;text-transform:uppercase;color:var(--ink3);}
  .line-item-row{display:grid;gap:8px;padding:8px 12px;border-top:1px solid var(--paper2);align-items:center;}
  .line-item-row input,.line-item-row select{padding:6px 8px;font-size:13px;}
  .line-total{font-family:var(--mono);font-weight:500;font-size:13px;color:var(--ink);text-align:right;}
  .add-line-btn{display:flex;align-items:center;gap:8px;padding:10px 12px;cursor:pointer;color:var(--accent);font-size:13px;font-family:var(--mono);border-top:1px solid var(--paper2);background:var(--paper);transition:background 0.15s;}
  .add-line-btn:hover{background:var(--accent-l);}
  .totals-box{margin-top:16px;background:var(--paper);border-radius:10px;padding:16px 20px;border:1px solid var(--paper3);}
  .totals-row{display:flex;justify-content:space-between;align-items:center;padding:4px 0;font-size:13px;color:var(--ink2);}
  .totals-row.grand{border-top:1px solid var(--paper3);margin-top:8px;padding-top:12px;font-size:16px;font-weight:500;color:var(--ink);font-family:var(--serif);}
  .price-tag{display:inline-flex;align-items:center;gap:4px;font-size:11px;padding:2px 6px;border-radius:6px;}
  .price-tag.special{background:var(--accent-l);color:var(--accent);}
  .price-tag.base{background:var(--paper2);color:var(--ink3);}
  .price-tag.new{background:var(--warn-l);color:var(--warn);}
  .autocomplete-wrap{position:relative;}
  .autocomplete-dropdown{position:absolute;top:100%;left:0;right:0;background:#fff;border:1px solid var(--paper3);border-radius:8px;box-shadow:var(--shadow-lg);z-index:50;max-height:200px;overflow-y:auto;margin-top:2px;}
  .autocomplete-item{padding:9px 12px;font-size:13px;cursor:pointer;transition:background 0.1s;border-bottom:1px solid var(--paper2);}
  .autocomplete-item:last-child{border-bottom:none;}
  .autocomplete-item:hover{background:var(--paper);}
  .autocomplete-item.active{background:var(--paper2);}
  .autocomplete-item .item-sub{font-size:11px;color:var(--ink3);margin-top:2px;}
  .wo-number{font-family:var(--mono);font-size:12px;font-weight:500;background:var(--paper2);padding:3px 8px;border-radius:6px;color:var(--ink2);}
  .slot-badge{display:inline-block;padding:3px 8px;border-radius:12px;font-size:11px;font-weight:500;white-space:nowrap;background:var(--info-l);color:var(--info);}
  .slot-badge.extra{background:var(--paper2);color:var(--ink3);}
  .slot-badge.mix{background:#f3e8ff;color:#6a1b9a;}
  .mix-panel{background:var(--paper);border:1px solid var(--paper3);border-radius:10px;padding:16px;margin-top:12px;}
  .mix-panel-title{font-size:12px;font-weight:500;color:var(--ink2);margin-bottom:12px;display:flex;align-items:center;justify-content:space-between;}
  .mix-sheet-counter{font-size:12px;color:var(--ink3);}
  .mix-sheet-counter.ok{color:var(--accent);font-weight:500;}
  .mix-sheet-counter.over{color:var(--danger);font-weight:500;}
  .mix-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:8px;}
  .mix-item{display:flex;align-items:center;gap:8px;padding:8px 10px;background:#fff;border-radius:8px;border:1px solid var(--paper3);transition:border-color 0.15s;}
  .mix-item.checked{border-color:var(--accent2);background:var(--accent-l);}
  .mix-item input[type=checkbox]{width:14px;height:14px;cursor:pointer;accent-color:var(--accent);flex-shrink:0;}
  .mix-item-label{font-size:12px;color:var(--ink2);flex:1;cursor:pointer;}
  .mix-item.checked .mix-item-label{color:var(--accent);font-weight:500;}
  .mix-sheet-input{width:58px;padding:4px 6px;font-size:12px;border:1px solid var(--paper3);border-radius:6px;font-family:var(--mono);background:var(--paper);text-align:right;}
  .mix-sheet-input:focus{border-color:var(--accent2);background:#fff;outline:none;}
  .mix-remaining{display:flex;align-items:center;justify-content:space-between;margin-top:12px;padding-top:12px;border-top:1px solid var(--paper3);}
  .mix-remaining-label{font-size:12px;color:var(--ink3);}
  .mix-remaining-val{font-family:var(--mono);font-size:14px;font-weight:500;}
  .mix-remaining-val.zero{color:var(--accent);}
  .mix-remaining-val.neg{color:var(--danger);}
  .mix-ok-btn{padding:7px 18px;background:var(--accent);color:#fff;border:none;border-radius:8px;font-family:var(--mono);font-size:13px;cursor:pointer;font-weight:500;}
  .mix-ok-btn:disabled{opacity:0.35;cursor:not-allowed;}
  .mix-ok-btn:not(:disabled):hover{background:var(--accent2);}
  .mix-confirmed-tag{display:inline-flex;align-items:center;gap:4px;font-size:11px;padding:2px 8px;border-radius:6px;background:#f3e8ff;color:#6a1b9a;margin-top:4px;}
  .import-banner{background:var(--info-l);border:1px solid #b3d4f5;border-radius:10px;padding:14px 16px;margin-bottom:16px;}
  .import-banner-title{font-size:12px;font-weight:500;color:var(--info);margin-bottom:8px;}
  .est-option{display:flex;align-items:center;justify-content:space-between;padding:8px 12px;background:#fff;border-radius:8px;border:1px solid #b3d4f5;margin-bottom:6px;}
  .est-option:last-child{margin-bottom:0;}
  .est-option-info{font-size:13px;color:var(--ink2);}
  .est-option-meta{font-size:11px;color:var(--ink3);margin-top:2px;}
  .print-area{display:none;}
  @media print{
    @page{size:A5;margin:8mm;}
    /* visibility:hidden alone leaves every hidden element's box in the
       flow — with a long page (e.g. the work orders table) behind the
       print modal, that invisible height still gets paginated, scattering
       the one print-area across dozens of blank/fragmented pages. Collapse
       everything's box too, then restore it just for the print-area. */
    body *{visibility:hidden;height:0!important;overflow:hidden!important;}
    .print-area,.print-area *{visibility:visible;height:auto!important;overflow:visible!important;}
    .print-area{display:block;position:fixed;top:0;left:0;width:100%;}
  }
  .wo-select-item{display:flex;align-items:center;gap:10px;padding:10px 12px;border:1px solid var(--paper3);border-radius:8px;margin-bottom:6px;cursor:pointer;transition:all 0.15s;background:#fff;}
  .wo-select-item:hover{border-color:var(--accent2);background:var(--accent-l);}
  .wo-select-item.selected{border-color:var(--accent);background:var(--accent-l);}
  .wo-select-item input[type=checkbox]{width:14px;height:14px;accent-color:var(--accent);cursor:pointer;flex-shrink:0;}
`;

// ── TOAST ─────────────────────────────────────────────────────
let _toastId = 0;
function useToast() {
  const [toasts, setToasts] = useState([]);
  const show = useCallback((msg, type="success") => {
    const id = ++_toastId;
    setToasts(t => [...t, {id, msg, type}]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 3500);
  }, []);
  return { toasts, show };
}

// ── AUTOCOMPLETE ──────────────────────────────────────────────
function AutoComplete({ value, onChange, onSelect, items, placeholder, renderItem, getKey, getLabel }) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1); // keyboard-highlighted row
  const ref = useRef(null);
  const listRef = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const { list: filtered, total: matchTotal } = matchItems(items, value, getLabel, 50);
  const act = active < filtered.length ? active : -1;
  useEffect(() => {
    if (act >= 0 && listRef.current) listRef.current.children[act]?.scrollIntoView({ block: "nearest" });
  }, [act]);
  const pick = (item) => { onSelect(item); setOpen(false); setActive(-1); };
  // Arrow keys move through the suggestions, Enter picks the highlighted
  // one, Esc/Tab close the list, so entry never needs the mouse.
  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) { setOpen(true); setActive(filtered.length ? 0 : -1); }
      else if (filtered.length) setActive(a => (a + 1) % filtered.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (open && filtered.length) setActive(a => (a <= 0 ? filtered.length - 1 : a - 1));
    } else if (e.key === "Enter") {
      if (open && act >= 0 && filtered[act]) { e.preventDefault(); pick(filtered[act]); }
    } else if (e.key === "Escape") {
      if (open) { e.preventDefault(); setOpen(false); setActive(-1); }
    } else if (e.key === "Tab") {
      setOpen(false);
    }
  };
  return (
    <div className="autocomplete-wrap" ref={ref}>
      <input value={value||""} onChange={e=>{onChange(e.target.value);setOpen(true);setActive(-1);}} onFocus={()=>setOpen(true)} onKeyDown={onKeyDown} placeholder={placeholder}/>
      {open && filtered.length > 0 && (
        <div className="autocomplete-dropdown" ref={listRef}>
          {filtered.map((item, idx) => (
            <div key={getKey(item)} className={"autocomplete-item"+(idx===act?" active":"")} onMouseDown={()=>pick(item)}>
              {renderItem(item)}
            </div>
          ))}
          {matchTotal > filtered.length && (
            <div style={{padding:"8px 12px",fontSize:11,color:"var(--ink3)"}}>Showing first {filtered.length} of {matchTotal} — keep typing to narrow</div>
          )}
        </div>
      )}
    </div>
  );
}

// ── MIX PANEL ─────────────────────────────────────────────────
function MixPanel({ totalSheets, products, customer, onConfirm }) {
  const [sel, setSel] = useState(() => { const i={}; MIX_FINISHES.forEach(f=>{i[f]={checked:false,sheets:""}}); return i; });
  const total = parseInt(totalSheets)||0;
  const entered = Object.values(sel).reduce((s,v)=>s+(v.checked?parseInt(v.sheets)||0:0),0);
  const remaining = total - entered;
  const isMatch = total > 0 && remaining === 0;
  const selectedCount = Object.values(sel).filter(v=>v.checked).length;
  const toggle = (n) => setSel(p=>({...p,[n]:{...p[n],checked:!p[n].checked,sheets:!p[n].checked?p[n].sheets:""}}));
  const setSheets = (n,v) => setSel(p=>({...p,[n]:{...p[n],sheets:v}}));
  const handleConfirm = () => {
    const confirmed = MIX_FINISHES.filter(f=>sel[f].checked&&parseInt(sel[f].sheets)>0).map(f=>({finish:f,sheets:parseInt(sel[f].sheets)}));
    const mixLines = confirmed.map(({finish,sheets})=>{
      const mp = products.find(p=>p.name.toLowerCase().startsWith("paper-")&&p.name.toLowerCase().includes(finish.toLowerCase()))||null;
      return {slotKey:"paper-mix",slotLabel:finish,isMix:true,isDefault:true,mixSheets:sheets,product_id:mp?.id||"",productQ:mp?.name||"",productName:mp?.name||"",quantity:sheets,unit_price:parseFloat(mp?.base_price)||0,tax_rate:parseFloat(mp?.tax_rate)||0,unit:mp?.unit||"nos"};
    });
    onConfirm(mixLines);
  };
  return (
    <div className="mix-panel">
      <div className="mix-panel-title">
        <span>Select mix finishes & sheet counts</span>
        {total>0&&<span className={`mix-sheet-counter ${isMatch?"ok":entered>total?"over":""}`}>{entered} / {total} sheets</span>}
      </div>
      <div className="mix-grid">
        {MIX_FINISHES.map(name=>(
          <div key={name} className={`mix-item ${sel[name].checked?"checked":""}`}>
            <input type="checkbox" id={`mix-${name}`} checked={sel[name].checked} onChange={()=>toggle(name)}/>
            <label htmlFor={`mix-${name}`} className="mix-item-label">{name}</label>
            {sel[name].checked&&<input type="number" min="1" step="1" className="mix-sheet-input" placeholder="0" value={sel[name].sheets} onChange={e=>setSheets(name,e.target.value)} onClick={e=>e.stopPropagation()}/>}
          </div>
        ))}
      </div>
      <div className="mix-remaining">
        <div><div className="mix-remaining-label">Remaining sheets</div>{!total&&<div style={{fontSize:11,color:"var(--ink3)"}}>Enter total sheets first</div>}</div>
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          <span className={`mix-remaining-val ${remaining===0&&total>0?"zero":remaining<0?"neg":""}`}>{total>0?remaining:"—"}</span>
          <button className="mix-ok-btn" disabled={!isMatch||selectedCount===0} onClick={handleConfirm}>✓ OK</button>
        </div>
      </div>
    </div>
  );
}

// ── PRINT VIEW (Work Order) ───────────────────────────────────
function PrintView({ record, lines, onClose }) {
  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal modal-xl">
        <div className="modal-header">
          <div className="modal-title">Print preview — {record.wo_number}</div>
          <div style={{display:"flex",gap:8}}>
            <button className="btn btn-primary" onClick={()=>window.print()}>🖨 Print (A5)</button>
            <button className="modal-close" onClick={onClose}>✕</button>
          </div>
        </div>
        <div className="modal-body">
          <div style={{border:"1px solid var(--paper3)",borderRadius:8,padding:24,background:"#fff",fontFamily:"var(--mono)",fontSize:13}}>
            <div style={{textAlign:"center",marginBottom:16,paddingBottom:12,borderBottom:"2px solid var(--ink)"}}>
              <div style={{fontFamily:"var(--serif)",fontSize:21,fontWeight:600}}>{COMPANY.name}</div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:16}}>
              {[["Work order",record.wo_number],["Date",fmtDMY(record.order_date)],["Series",record.series_code],["Status",(record.status||"").replace("_"," ")],["Customer",record.customers?.name||"—"],["Phone",record.customers?.phone||"—"],["Book type",record.book_type||"—"],["Lamination",record.finish_type||"—"],["Size", record.size||"—"],
["Total sheets",record.total_sheets?record.total_sheets.toLocaleString("en-IN"):"—"],record.notes?["Notes",record.notes]:null].filter(Boolean).map(([lbl,val])=>(
                <div key={lbl}><div style={{fontSize:10,color:"var(--ink3)",textTransform:"uppercase",letterSpacing:"0.6px"}}>{lbl}</div><div style={{fontWeight:500}}>{val}</div></div>
              ))}
            </div>
            <table style={{width:"100%",borderCollapse:"collapse",marginBottom:12}}>
              <thead><tr style={{background:"var(--paper2)",borderBottom:"1px solid var(--paper3)"}}>
                <th style={{padding:"6px 8px",textAlign:"left",fontSize:10,textTransform:"uppercase",color:"var(--ink3)"}}>Item</th>
                <th style={{padding:"6px 8px",textAlign:"left",fontSize:10,textTransform:"uppercase",color:"var(--ink3)"}}>Product</th>
                <th style={{padding:"6px 8px",textAlign:"right",fontSize:10,textTransform:"uppercase",color:"var(--ink3)"}}>Qty</th>
              </tr></thead>
              <tbody>
                {lines.map((l,i)=>(
                  <tr key={i} style={{borderBottom:"1px solid var(--paper2)"}}>
                    <td style={{padding:"6px 8px",fontSize:12}}>{(l.isMix || l.slotLabel)
  ? <span style={{background:"var(--info-l)",color:"var(--info)",padding:"1px 6px",borderRadius:8,fontSize:11}}>{l.isMix ? "Paper" : l.slotLabel}</span>
  : <span style={{color:"var(--ink3)",fontSize:11}}>Extra</span>}</td>
                    <td style={{padding:"6px 8px",fontSize:12,fontWeight:500}}>{l.productName||"—"}</td>
                    <td style={{padding:"6px 8px",fontSize:12,textAlign:"right"}}>{l.quantity}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{borderTop:"1px solid var(--paper3)",paddingTop:8,fontSize:11,color:"var(--ink3)",textAlign:"center"}}>{COMPANY.name} · Work Order System</div>
          </div>
          <div className="print-area" style={{fontFamily:"Arial,sans-serif",fontSize:"9pt",color:"#000"}}>
            <div style={{textAlign:"center",marginBottom:"3mm",paddingBottom:"2mm",borderBottom:"2pt solid #000"}}>
              <div style={{fontSize:"14pt",fontWeight:"bold"}}>{COMPANY.name}</div>
            </div>
            {/* Top half: compact 4-column details. Bottom half: item table. */}
            <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:"1.5mm 3mm",marginBottom:"3mm",paddingBottom:"2.5mm",borderBottom:"1pt solid #000"}}>
              {[["Work Order",record.wo_number],["Date",fmtDMY(record.order_date)],["Series",record.series_code],["Status",(record.status||"").replace("_"," ")],
                ["Customer",record.customers?.name||"—",2],["Phone",record.customers?.phone||"—"],["Size",record.size||"—"],
                ["Book type",record.book_type||"—"],["Lamination",record.finish_type||"—"],["Total sheets",record.total_sheets?record.total_sheets.toLocaleString("en-IN"):"—"],
                record.notes?["Notes",record.notes,4]:null].filter(Boolean).map(([lbl,val,span])=>(
                <div key={lbl} style={span?{gridColumn:`span ${span}`}:undefined}><div style={{fontSize:"6.5pt",color:"#333",fontWeight:"bold",textTransform:"uppercase",lineHeight:1.2}}>{lbl}</div><div style={{fontWeight:"bold",fontSize:"9.5pt",lineHeight:1.2}}>{val}</div></div>
              ))}
            </div>
            <table style={{width:"100%",borderCollapse:"collapse",marginBottom:"2mm"}}>
              <thead><tr style={{borderBottom:"1.25pt solid #000"}}>
                <th style={{padding:"1mm",textAlign:"left",fontSize:"8pt"}}>Item</th>
                <th style={{padding:"1mm",textAlign:"left",fontSize:"8pt"}}>Product</th>
                <th style={{padding:"1mm",textAlign:"right",fontSize:"8pt"}}>Qty</th>
              </tr></thead>
              <tbody>
                {lines.map((l,i)=>(
                  <tr key={i} style={{borderBottom:"0.75pt solid #666",pageBreakInside:"avoid"}}>
                    <td style={{padding:"1.2mm 1mm",fontSize:"9pt"}}>{l.isMix ? "Paper" : (l.slotLabel||"Extra")}</td>
                    <td style={{padding:"1.2mm 1mm",fontSize:"9pt",fontWeight:"bold"}}>{l.productName||"—"}</td>
                    <td style={{padding:"1.2mm 1mm",fontSize:"9pt",textAlign:"right"}}>{l.quantity}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{borderTop:"1pt solid #000",paddingTop:"1.5mm",fontSize:"7pt",color:"#333",textAlign:"center"}}>{COMPANY.name}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── INVOICE PRINT VIEW ────────────────────────────────────────
function InvoicePrintView({ invoice, onClose }) {
  const lines = invoice.lines || [];
const grandTotal = invoice.total_override || lines.reduce((s,l)=>s+l.line_total,0);
const taxTotal   = lines.reduce((s,l)=>{const r=l.tax_rate||0;const lt=invoice.total_override?(l.line_total/lines.reduce((a,x)=>a+x.line_total,0))*grandTotal:l.line_total;return s+lt-(lt*100/(100+r));},0);
const baseTotal  = grandTotal - taxTotal;
// Place of supply is fixed at issuance (stored on the invoice), not
// recomputed from the customer's current state — older invoices without
// it fall back to the pre-existing 50/50 CGST+SGST assumption.
const isInterstate = invoice.is_interstate ?? false;
const cgst = isInterstate ? 0 : taxTotal/2;
const sgst = isInterstate ? 0 : taxTotal/2;
const igst = isInterstate ? taxTotal : 0;

  const content = (
    <div style={{fontFamily:"Arial,sans-serif",fontSize:"9pt",color:"#000"}}>
      <table style={{width:"100%",borderCollapse:"collapse",marginBottom:"4mm"}}>
        <tbody><tr>
          <td style={{verticalAlign:"top",width:"60%"}}>
            <div style={{fontSize:"14pt",fontWeight:"bold",marginBottom:"1mm"}}>{COMPANY.name}</div>
            <div style={{fontSize:"8pt",whiteSpace:"pre-line",color:"#333"}}>{COMPANY.address}</div>
            <div style={{fontSize:"8pt",color:"#333",marginTop:"1mm"}}>Ph: {COMPANY.phone} | {COMPANY.email}</div>
            <div style={{fontSize:"8pt",fontWeight:"bold",marginTop:"1mm"}}>GSTIN: {COMPANY.gstin}</div>
          </td>
          <td style={{verticalAlign:"top",textAlign:"right"}}>
            <div style={{fontSize:"13pt",fontWeight:"bold"}}>TAX INVOICE</div>
            <div style={{fontSize:"8pt",marginTop:"2mm"}}>
              <div><b>Invoice No:</b> {invoice.invoice_number}</div>
              <div><b>Date:</b> {fmtDMY(invoice.invoice_date)}</div>
              {invoice.due_date&&<div><b>Due:</b> {fmtDMY(invoice.due_date)}</div>}
            </div>
          </td>
        </tr></tbody>
      </table>
      <div style={{borderTop:"1.5pt solid #000",marginBottom:"3mm"}}/>
      <div style={{marginBottom:"3mm"}}>
        <div style={{fontSize:"7pt",textTransform:"uppercase",letterSpacing:"0.5pt",color:"#666",marginBottom:"1mm"}}>Bill to</div>
        <div style={{fontWeight:"bold",fontSize:"10pt"}}>{invoice.customers?.name}</div>
        {invoice.customers?.phone&&<div style={{fontSize:"8pt",color:"#333"}}>Ph: {invoice.customers.phone}</div>}
        {invoice.customers?.gstin&&<div style={{fontSize:"8pt",color:"#333"}}>GSTIN: {invoice.customers.gstin}</div>}
        {invoice.customers?.address_line1&&<div style={{fontSize:"8pt",color:"#333"}}>{invoice.customers.address_line1}</div>}
{invoice.customers?.address_line2&&<div style={{fontSize:"8pt",color:"#333"}}>{invoice.customers.address_line2}</div>}
{(invoice.customers?.city||invoice.customers?.state||invoice.customers?.pincode)&&(
  <div style={{fontSize:"8pt",color:"#333"}}>
    {[invoice.customers.city, invoice.customers.state, invoice.customers.pincode].filter(Boolean).join(", ")}
  </div>
)}
{invoice.customers?.country&&invoice.customers.country!=="India"&&<div style={{fontSize:"8pt",color:"#333"}}>{invoice.customers.country}</div>}
        {invoice.place_of_supply&&<div style={{fontSize:"8pt",color:"#333",marginTop:"1mm"}}><b>Place of Supply:</b> {invoice.place_of_supply}{invoice.place_of_supply_code?` (${invoice.place_of_supply_code})`:""}</div>}
      </div>

      <table style={{width:"100%",borderCollapse:"collapse",marginBottom:"3mm",fontSize:"8pt"}}>
        <thead><tr style={{background:"#f0f0f0",borderBottom:"1pt solid #000"}}>
          <th style={{padding:"2mm",textAlign:"left"}}>#</th>
          <th style={{padding:"2mm",textAlign:"left"}}>Description</th>
          <th style={{padding:"2mm",textAlign:"right"}}>HSN</th>
          <th style={{padding:"2mm",textAlign:"right"}}>Qty</th>
          <th style={{padding:"2mm",textAlign:"right"}}>Rate</th>
          <th style={{padding:"2mm",textAlign:"right"}}>GST%</th>
          <th style={{padding:"2mm",textAlign:"right"}}>Amount</th>
        </tr></thead>
        <tbody>
          {lines.map((l,i)=>(
            <tr key={i} style={{borderBottom:"0.5pt solid #ddd"}}>
              <td style={{padding:"1.5mm 2mm"}}>{i+1}</td>
             <td style={{padding:"1.5mm 2mm",fontWeight:"bold"}}>{l.product_name}</td>
              <td style={{padding:"1.5mm 2mm",textAlign:"right"}}>{l.hsn_code||"—"}</td>
              <td style={{padding:"1.5mm 2mm",textAlign:"right"}}>{l.quantity}</td>
              <td style={{padding:"1.5mm 2mm",textAlign:"right"}}>{fmtN(l.unit_price)}</td>
              <td style={{padding:"1.5mm 2mm",textAlign:"right"}}>{l.tax_rate}%</td>
              <td style={{padding:"1.5mm 2mm",textAlign:"right",fontWeight:"bold"}}>{fmtN(l.line_total)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <table style={{width:"100%",borderCollapse:"collapse",marginBottom:"3mm",fontSize:"8pt"}}>
        <tbody>
          <tr><td style={{textAlign:"right",padding:"1mm 2mm",width:"75%"}}>Taxable amount</td><td style={{textAlign:"right",padding:"1mm 2mm",borderLeft:"0.5pt solid #ddd"}}>{fmtN(baseTotal)}</td></tr>
          {isInterstate ? (
            <tr><td style={{textAlign:"right",padding:"1mm 2mm"}}>IGST ({baseTotal>0?(igst/baseTotal*100).toFixed(1):0}%)</td><td style={{textAlign:"right",padding:"1mm 2mm",borderLeft:"0.5pt solid #ddd"}}>{fmtN(igst)}</td></tr>
          ) : (
            <>
              <tr><td style={{textAlign:"right",padding:"1mm 2mm"}}>CGST ({baseTotal>0?(cgst/baseTotal*100).toFixed(1):0}%)</td><td style={{textAlign:"right",padding:"1mm 2mm",borderLeft:"0.5pt solid #ddd"}}>{fmtN(cgst)}</td></tr>
              <tr><td style={{textAlign:"right",padding:"1mm 2mm"}}>SGST ({baseTotal>0?(sgst/baseTotal*100).toFixed(1):0}%)</td><td style={{textAlign:"right",padding:"1mm 2mm",borderLeft:"0.5pt solid #ddd"}}>{fmtN(sgst)}</td></tr>
            </>
          )}
          <tr style={{borderTop:"1pt solid #000",fontWeight:"bold",fontSize:"9pt"}}>
            <td style={{textAlign:"right",padding:"2mm"}}>Total (₹)</td>
            <td style={{textAlign:"right",padding:"2mm",borderLeft:"0.5pt solid #ddd"}}>{fmtN(grandTotal)}</td>
          </tr>
          {invoice.amount_paid>0&&(
            <>
              <tr><td style={{textAlign:"right",padding:"1mm 2mm"}}>Amount paid</td><td style={{textAlign:"right",padding:"1mm 2mm",borderLeft:"0.5pt solid #ddd"}}>{fmtN(invoice.amount_paid)}</td></tr>
              <tr style={{fontWeight:"bold",color:invoice.balance_due>0?"#b7101d":"#1f6e4a"}}>
                <td style={{textAlign:"right",padding:"1mm 2mm"}}>Balance due</td>
                <td style={{textAlign:"right",padding:"1mm 2mm",borderLeft:"0.5pt solid #ddd"}}>{fmtN(invoice.balance_due)}</td>
              </tr>
            </>
          )}
        </tbody>
      </table>
      {invoice.notes&&<div style={{fontSize:"8pt",color:"#333",marginBottom:"3mm"}}><b>Notes:</b> {invoice.notes}</div>}
      <div style={{borderTop:"0.5pt solid #ccc",paddingTop:"2mm",fontSize:"7pt",color:"#666",textAlign:"center"}}>{COMPANY.name} · GSTIN: {COMPANY.gstin} · Computer generated invoice</div>
    </div>
  );

  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal modal-xl">
        <div className="modal-header">
          <div className="modal-title">Invoice — {invoice.invoice_number}</div>
          <div style={{display:"flex",gap:8}}>
            <button className="btn btn-primary" onClick={()=>window.print()}>🖨 Print (A5)</button>
            <button className="modal-close" onClick={onClose}>✕</button>
          </div>
        </div>
        <div className="modal-body">
          <div style={{border:"1px solid var(--paper3)",borderRadius:8,padding:20,background:"#fff"}}>{content}</div>
          <div className="print-area">{content}</div>
        </div>
      </div>
    </div>
  );
}

// ── CREATE INVOICE MODAL ──────────────────────────────────────
function CreateInvoiceModal({ onClose, onSave, toast }) {
  const [step, setStep]           = useState(1);
  const [customers, setCustomers] = useState([]);
  const [customerQ, setCustomerQ] = useState("");
  const [customer, setCustomer]   = useState(null);
  const [availableWOs, setAvailableWOs] = useState([]);
  const [selectedWOs, setSelectedWOs]   = useState([]);
  const [lines, setLines]         = useState([]);
  const [notes, setNotes]         = useState("");
  const [dueDate, setDueDate]     = useState("");
  const [saving, setSaving]       = useState(false);
  const [loading, setLoading]     = useState(false);

useEffect(() => {
  fetchAll("customers", {
    select:"id,name,type,phone,gstin,address_line1,address_line2,city,state,pincode,country",
    eq:{is_active:true}, order:"name"
  }).then(data => setCustomers(data));
}, []);

const loadWOs = async (customerId) => {
  setLoading(true);
  const [woRes, dwRes] = await Promise.all([
    supabase.from("work_orders")
      .select("id,wo_number,order_date,book_type,finish_type,total_sheets,status")
      .eq("customer_id",customerId).eq("is_deleted",false).neq("status","cancelled").neq("status","invoiced")
      .order("order_date",{ascending:false}),
    supabase.from("direct_work_orders")
      .select("id,dw_number,order_date,notes,status")
      .eq("customer_id",customerId).eq("is_deleted",false).neq("status","cancelled").neq("status","invoiced")
      .order("order_date",{ascending:false}),
  ]);
  // Tag each with source so we know how to process them
  const wos = (woRes.data||[]).map(w=>({...w, _source:"wo"}));
  const dws = (dwRes.data||[]).map(d=>({...d, _source:"dw", wo_number:d.dw_number, book_type:"Direct Work", finish_type:d.notes||""}));
  setAvailableWOs([...wos,...dws].sort((a,b)=>a.order_date<b.order_date?1:-1));
  setLoading(false);
};

  const toggleWO = (wo) => {
    setSelectedWOs(prev => prev.find(w=>w.id===wo.id) ? prev.filter(w=>w.id!==wo.id) : [...prev,wo]);
  };

 const loadLines = async () => {
  if (selectedWOs.length===0) return;
  setLoading(true);

  const woLines = await Promise.all(selectedWOs.map(async (wo) => {
    if (wo._source==="dw") {
      // Direct work — sum all line items, same "total print count" treatment
      // as a regular Print Only work order (not a single qty-1 line).
      const {data:items} = await supabase.from("dw_line_items")
        .select("quantity,unit_price")
        .eq("dw_order_id", wo.id);
      const woTotal  = (items||[]).reduce((s,l)=>s+(parseFloat(l.quantity)||0)*(parseFloat(l.unit_price)||0),0);
      const totalQty = (items||[]).reduce((s,l)=>s+(parseFloat(l.quantity)||0),0) || 1;
      return {
        wo_number:    wo.dw_number,
        product_name: "Print",
        hsn_code:     "49119100",
        tax_rate:     18,
        unit:         "nos",
        quantity:     totalQty,
        unit_price:   woTotal/totalQty,
        line_total:   woTotal,
        _source:      "dw",
      };
    }
    // Regular work order
    const {data:items} = await supabase.from("wo_line_items")
      .select("quantity,unit_price,products(tax_rate)")
      .eq("work_order_id", wo.id);
    const woTotal = (items||[]).reduce((s,l)=>s+(parseFloat(l.quantity)||0)*(parseFloat(l.unit_price)||0),0);
    const avgTaxRate = (items||[]).length>0
      ? (items||[]).reduce((s,l)=>s+(parseFloat(l.products?.tax_rate)||0),0)/items.length
      : 18;
    const isBook = isBookType(wo.book_type);
    const description = isBook ? "Photobook" : "Print";
    const quantity    = isBook ? 1 : (parseInt(wo.total_sheets)||1);
    const unitPrice   = isBook ? woTotal : (quantity>0?woTotal/quantity:woTotal);
    return {
      wo_number:    wo.wo_number,
      product_name: description,
      hsn_code:     "49119100",
      tax_rate:     Math.round(avgTaxRate),
      unit:         "nos",
      quantity,
      unit_price:   unitPrice,
      line_total:   woTotal,
      _source:      "wo",
    };
  }));

  setLines(woLines); setLoading(false); setStep(3);
};

  const grandTotal = lines.reduce((s,l)=>s+l.line_total,0);
  const taxTotal   = lines.reduce((s,l)=>{const r=l.tax_rate||0;return s+l.line_total-(l.line_total*100/(100+r));},0);
  const baseTotal  = grandTotal - taxTotal;
  // Place of supply drives CGST+SGST vs IGST — fixed at issuance so a
  // later edit to the customer's state can't retroactively change an
  // already-issued invoice's tax classification.
  const isInterstate = !!(customer?.state) && customer.state !== COMPANY.state;

  const save = async () => {
    if (!customer?.id||selectedWOs.length===0||lines.length===0) return;
    if (!customer?.state) { alert("This customer has no state set — add one before invoicing (needed for CGST/SGST vs IGST)."); return; }
    setSaving(true);
    try {
      const {data:numData} = await supabase.rpc("next_invoice_num",{p_fy:getCurrentFYCode()});
     const {data:inv,error:invErr} = await supabase.from("invoices").insert({
  invoice_number:  numData,
  // work_order_id is a foreign key to work_orders only, so a direct work
  // order's id can't go there — an invoice made purely of direct work
  // leaves it empty and relies on work_order_ids (which holds both kinds).
  work_order_id:   selectedWOs.find(w=>w._source!=="dw")?.id || null,
  work_order_ids:  selectedWOs.map(w => w.id),
  customer_id:     customer.id,
        subtotal:       baseTotal,
        tax_amount:     taxTotal,
        total:          grandTotal,
        amount_paid:    0,
        status:         "unpaid",
        invoice_date:   new Date().toISOString().slice(0,10),
        due_date:       dueDate||null,
        notes:          notes||null,
        invoiced_at:    new Date().toISOString(),
        place_of_supply:      customer.state,
        place_of_supply_code: stateCode(customer.state),
        is_interstate:        isInterstate,
        cgst_amount:  isInterstate ? 0 : taxTotal/2,
        sgst_amount:  isInterstate ? 0 : taxTotal/2,
        igst_amount:  isInterstate ? taxTotal : 0,
      }).select().single();
      if (invErr) throw invErr;
      const woIds = selectedWOs.filter(w=>w._source!=="dw").map(w=>w.id);
const dwIds = selectedWOs.filter(w=>w._source==="dw").map(w=>w.id);
if (woIds.length>0) await supabase.from("work_orders").update({status:"invoiced",updated_at:new Date().toISOString()}).in("id",woIds);
if (dwIds.length>0) await supabase.from("direct_work_orders").update({status:"invoiced",updated_at:new Date().toISOString()}).in("id",dwIds);
      toast(`Invoice ${numData} created!`,"success");
      onSave(inv.id);
    } catch(e) { alert("Error: "+e.message); } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal modal-xl">
        <div className="modal-header">
          <div className="modal-title">New invoice</div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div style={{display:"flex",gap:8,marginBottom:20}}>
            {["Select customer","Select work orders","Review & create"].map((s,i)=>(
              <div key={i} style={{display:"flex",alignItems:"center",gap:6,fontSize:12}}>
                <span style={{width:20,height:20,borderRadius:"50%",display:"inline-flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:500,background:step>i+1?"var(--accent)":step===i+1?"var(--info)":"var(--paper3)",color:step>=i+1?"#fff":"var(--ink3)"}}>{step>i+1?"✓":i+1}</span>
                <span style={{color:step===i+1?"var(--ink)":"var(--ink3)",fontWeight:step===i+1?500:400}}>{s}</span>
                {i<2&&<span style={{color:"var(--paper3)"}}>›</span>}
              </div>
            ))}
          </div>

          {step===1&&(
            <div>
              <div className="form-group" style={{marginBottom:16}}>
                <label>Customer *</label>
                <AutoComplete value={customerQ} onChange={v=>{setCustomerQ(v);if(!v)setCustomer(null);}} onSelect={c=>{setCustomer(c);setCustomerQ(c.name);}}
                  items={customers} placeholder="Type to search customers…" getKey={c=>c.id} getLabel={c=>c.name}
                  renderItem={c=><div><div style={{fontWeight:500}}>{c.name}</div><div className="item-sub">{c.type}{c.phone?" · "+c.phone:""}</div></div>}/>
              </div>
              <div className="form-actions">
                <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
                <button className="btn btn-primary" disabled={!customer} onClick={()=>{loadWOs(customer.id);setStep(2);}}>Next →</button>
              </div>
            </div>
          )}

          {step===2&&(
            <div>
              <div style={{marginBottom:12,fontSize:13,color:"var(--ink2)"}}>Select work orders for <strong>{customer?.name}</strong>:</div>
              {loading&&<div style={{textAlign:"center",padding:20}}><span className="spinner"/></div>}
              {!loading&&availableWOs.length===0&&<div className="empty-state"><div className="icon">📋</div><p>No work orders found for this customer.</p></div>}
              {!loading&&availableWOs.map(wo=>(
                <div key={wo.id} className={`wo-select-item ${selectedWOs.find(w=>w.id===wo.id)?"selected":""}`} onClick={()=>toggleWO(wo)}>
                  <input type="checkbox" checked={!!selectedWOs.find(w=>w.id===wo.id)} onChange={()=>toggleWO(wo)} onClick={e=>e.stopPropagation()}/>
<div style={{flex:1}}>
  <div style={{display:"flex",alignItems:"center",gap:6}}>
    <span style={{fontWeight:500,fontSize:13}}>{wo.wo_number||wo.dw_number}</span>
    {wo._source==="dw"&&<span style={{display:"inline-block",padding:"1px 6px",borderRadius:8,fontSize:10,fontWeight:500,background:"#f3e8ff",color:"#6a1b9a"}}>Direct Work</span>}
  </div>
  <div style={{fontSize:11,color:"var(--ink3)"}}>
    {fmtDMY(wo.order_date)} · {wo._source==="dw"?(wo.notes||"Direct work"):(`${wo.book_type||"—"}${wo.finish_type?" · "+wo.finish_type:""}${wo.total_sheets?" · "+wo.total_sheets+" sheets":""}`)}
  </div>
</div>
                  <span className={`badge ${wo.status==="completed"?"badge-green":wo.status==="invoiced"?"badge-purple":"badge-amber"}`}>{wo.status?.replace("_"," ")}</span>
                </div>
              ))}
              <div className="form-actions">
                <button className="btn btn-ghost" onClick={()=>setStep(1)}>← Back</button>
                <button className="btn btn-primary" disabled={selectedWOs.length===0} onClick={loadLines}>Review {selectedWOs.length>0?`(${selectedWOs.length} WO${selectedWOs.length>1?"s":""})`:""}  →</button>
              </div>
            </div>
          )}

          {step===3&&(
            <div>
              <div style={{marginBottom:12,display:"flex",gap:8,alignItems:"center"}}>
                <span style={{fontSize:13,color:"var(--ink2)"}}>Invoice for <strong>{customer?.name}</strong></span>
                <span style={{fontSize:12,color:"var(--ink3)"}}>· WOs: {selectedWOs.map(w=>w.wo_number).join(", ")}</span>
              </div>
              <div className="card" style={{marginBottom:16,overflow:"visible"}}>
                <table>
                  <thead><tr>
                    <th>Description</th><th>HSN</th><th style={{textAlign:"right"}}>Qty</th>
                    <th style={{textAlign:"right"}}>Rate (₹)</th><th style={{textAlign:"right"}}>GST%</th>
                    <th style={{textAlign:"right"}}>Amount (₹)</th>
                  </tr></thead>
                  <tbody>
                    {lines.map((l,i)=>(
                      <tr key={i}>
                       <td style={{fontWeight:500}}>{l.product_name}</td>
                        <td className="mono-text">{l.hsn_code||"—"}</td>
                        <td style={{textAlign:"right"}}>{l.quantity}</td>
                        <td style={{textAlign:"right"}} className="mono-text">{fmtN(l.unit_price)}</td>
                        <td style={{textAlign:"right"}}>{l.tax_rate}%</td>
                        <td style={{textAlign:"right",fontWeight:500}} className="mono-text">{fmtN(l.line_total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="totals-box" style={{marginBottom:16}}>
                <div className="totals-row"><span>Taxable amount</span><span>{fmt(baseTotal)}</span></div>
                <div className="totals-row"><span>CGST</span><span>{fmt(taxTotal/2)}</span></div>
                <div className="totals-row"><span>SGST</span><span>{fmt(taxTotal/2)}</span></div>
                <div className="totals-row grand"><span>Total</span><span>{fmt(grandTotal)}</span></div>
              </div>
              <div className="form-grid">
                <div className="form-group"><label>Due date (optional)</label><input type="date" value={dueDate} onChange={e=>setDueDate(e.target.value)}/></div>
                <div className="form-group"><label>Notes (optional)</label><input value={notes} onChange={e=>setNotes(e.target.value)} placeholder="Payment terms, remarks…"/></div>
              </div>
              <div className="form-actions">
                <button className="btn btn-ghost" onClick={()=>setStep(2)}>← Back</button>
                <button className="btn btn-primary" onClick={save} disabled={saving}>{saving?<span className="spinner"/>:"🧾"} {saving?"Creating…":"Create invoice"}</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Reconstructs an invoice's line items from its linked work orders /
// direct work orders. Module-level (not just InvoicesPage-local) so
// EditInvoiceModal can reuse it to recompute totals after a linked WO
// is edited.
const computeInvoiceLines = async (inv) => {
  const woIds = (inv.work_order_ids && inv.work_order_ids.length > 0)
    ? inv.work_order_ids
    : [inv.work_order_id];

  const [{data:wos}, {data:dws}] = await Promise.all([
    supabase.from("work_orders").select("id,wo_number,book_type,total_sheets").in("id", woIds),
    supabase.from("direct_work_orders").select("id,dw_number").in("id", woIds),
  ]);

  const woLines = await Promise.all((wos||[]).map(async (wo) => {
    const {data:items} = await supabase.from("wo_line_items")
      .select("quantity,unit_price,products(tax_rate)")
      .eq("work_order_id", wo.id);
    const woTotal = (items||[]).reduce((s,l)=>
      s+(parseFloat(l.quantity)||0)*(parseFloat(l.unit_price)||0),0);
    const avgTaxRate = (items||[]).length>0
      ? (items||[]).reduce((s,l)=>s+(parseFloat(l.products?.tax_rate)||0),0)/items.length
      : 18;
    const isBook = isBookType(wo.book_type);
    const quantity = isBook ? 1 : (parseInt(wo.total_sheets)||1);
    const unitPrice = isBook ? woTotal : (quantity>0?woTotal/quantity:woTotal);
    return {
      product_name: isBook ? "Photobook" : "Print",
      hsn_code:     "49119100",
      tax_rate:     Math.round(avgTaxRate),
      unit:         "nos",
      quantity,
      unit_price:   unitPrice,
      line_total:   woTotal,
    };
  }));

  // Direct work — same "total print count" treatment as a regular Print
  // Only work order, not a single qty-1 line.
  const dwLines = await Promise.all((dws||[]).map(async (dw) => {
    const {data:items} = await supabase.from("dw_line_items")
      .select("quantity,unit_price")
      .eq("dw_order_id", dw.id);
    const woTotal  = (items||[]).reduce((s,l)=>s+(parseFloat(l.quantity)||0)*(parseFloat(l.unit_price)||0),0);
    const totalQty = (items||[]).reduce((s,l)=>s+(parseFloat(l.quantity)||0),0) || 1;
    return {
      product_name: "Print",
      hsn_code:     "49119100",
      tax_rate:     18,
      unit:         "nos",
      quantity:     totalQty,
      unit_price:   woTotal/totalQty,
      line_total:   woTotal,
    };
  }));

  return { processedLines: [...woLines, ...dwLines], wos: [...(wos||[]), ...(dws||[])] };
};

// Re-derives subtotal/tax/total (and the CGST+SGST vs IGST split, using
// the invoice's already-fixed place-of-supply classification) from its
// linked work orders, and writes the result back onto the invoice row —
// used after editing a work order via "Edit invoice" so stored amounts
// (and any future e-invoice export) stay in sync with the WO's real content.
const recomputeInvoiceTotals = async (inv) => {
  const { processedLines } = await computeInvoiceLines(inv);
  const grandTotal = processedLines.reduce((s,l)=>s+l.line_total,0);
  const taxTotal    = processedLines.reduce((s,l)=>{const r=l.tax_rate||0;return s+l.line_total-(l.line_total*100/(100+r));},0);
  const baseTotal   = grandTotal - taxTotal;
  const isInterstate = !!inv.is_interstate;
  const update = {
    subtotal:    baseTotal,
    tax_amount:  taxTotal,
    total:       grandTotal,
    cgst_amount: isInterstate ? 0 : taxTotal/2,
    sgst_amount: isInterstate ? 0 : taxTotal/2,
    igst_amount: isInterstate ? taxTotal : 0,
    updated_at:  new Date().toISOString(),
  };
  const { error } = await supabase.from("invoices").update(update).eq("id", inv.id);
  if (error) throw error;
  return update;
};

// ── INVOICES PAGE ─────────────────────────────────────────────
function InvoicesPage({ toast }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState("");
  const [filter, setFilter]     = useState("all");
  const [modal, setModal]       = useState(null);
  const [printInv, setPrintInv] = useState(null);
  const [editInv, setEditInv]   = useState(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate]     = useState("");

  const load = useCallback(async () => {
    setLoading(true);
const data = await fetchAll("invoices", {select:"*, customers(name,phone,email,gstin,address_line1,address_line2,city,state,pincode,country)", eq:{is_deleted:false}, order:"created_at", ascending:false});
setInvoices(data); setLoading(false);
  }, []);

  useEffect(()=>{load();},[load]);

const openPrint = async (inv) => {
  const { processedLines, wos } = await computeInvoiceLines(inv);

  // If print total doesn't match invoice total (old invoices missing WO IDs),
  // show a warning and use invoice stored total as grand total override
  const printTotal = processedLines.reduce((s,l)=>s+l.line_total,0);
  const invoiceTotal = parseFloat(inv.total)||0;

  setPrintInv({
    ...inv,
    lines: processedLines,
    total_override: Math.abs(printTotal - invoiceTotal) > 1 ? invoiceTotal : null,
    work_orders: wos,
    balance_due: invoiceTotal - parseFloat(inv.amount_paid||0),
  });
};

const downloadEInvoice = async (inv) => {
  if (!inv.customers?.gstin) { toast("This customer has no GSTIN — e-invoice only applies to GST-registered customers.","error"); return; }
  if (!inv.place_of_supply_code) { toast("This invoice predates place-of-supply tracking — re-create it to enable e-invoice export.","error"); return; }
  const { processedLines } = await computeInvoiceLines(inv);
  const payload = buildEInvoiceJSON({ invoice: inv, company: COMPANY, customer: inv.customers, lines: processedLines });
  downloadEInvoiceJSON(payload, `${inv.invoice_number}.json`);
};

// Soft-deletes the invoice and frees its linked work order(s) back up —
// they revert to "completed" so they're editable again and show back up
// as available (not-yet-invoiced) when creating a new invoice.
const deleteInvoice = async (inv) => {
  if (!window.confirm(`Delete invoice ${inv.invoice_number}? Its work order(s) will become available to invoice again.`)) return;
  try {
    const woIds = (inv.work_order_ids && inv.work_order_ids.length > 0)
      ? inv.work_order_ids
      : [inv.work_order_id].filter(Boolean);
    if (woIds.length > 0) {
      await supabase.from("work_orders").update({status:"completed",updated_at:new Date().toISOString()}).in("id",woIds);
      await supabase.from("direct_work_orders").update({status:"completed",updated_at:new Date().toISOString()}).in("id",woIds);
    }
    const { error } = await supabase.from("invoices").update({is_deleted:true,updated_at:new Date().toISOString()}).eq("id",inv.id);
    if (error) throw error;
    toast(`Invoice ${inv.invoice_number} deleted.`,"error");
    load();
  } catch(e) { toast("Error deleting invoice: "+e.message,"error"); }
};

  const filtered = invoices.filter(i=>{
    const s=search.toLowerCase();
    return((i.invoice_number||"").toLowerCase().includes(s)||(i.customers?.name||"").toLowerCase().includes(s))&&
      (filter==="all"||i.status===filter)&&(!fromDate||i.invoice_date>=fromDate)&&(!toDate||i.invoice_date<=toDate);
  });

  // Karnataka-based company: CGST+SGST for in-state customers, IGST for
  // out-of-state — already computed and stored per-invoice at creation
  // time (invoice.is_interstate), so this just reads it back out.
  const downloadInvoiceCSV = () => {
    if (filtered.length===0) return alert("No invoices to download.");
    const headers=["Date","Invoice No.","Customer","GSTIN","Place of Supply","Tax Type","Sale Amount (Before GST)","CGST","SGST","IGST","Sale Amount (After GST)"];
    const rows=filtered.map(i=>[
      fmtDMY(i.invoice_date)||"",
      i.invoice_number||"",
      i.customers?.name||"",
      i.customers?.gstin||"",
      i.place_of_supply||"",
      i.is_interstate?"IGST":"CGST+SGST",
      (parseFloat(i.subtotal)||0).toFixed(2),
      (parseFloat(i.cgst_amount)||0).toFixed(2),
      (parseFloat(i.sgst_amount)||0).toFixed(2),
      (parseFloat(i.igst_amount)||0).toFixed(2),
      (parseFloat(i.total)||0).toFixed(2),
    ]);
    const csv=[headers,...rows].map(r=>r.map(v=>`"${String(v).replace(/"/g,'""')}"`).join(",")).join("\n");
    const blob=new Blob(["﻿"+csv],{type:"text/csv;charset=utf-8;"});
    const url=URL.createObjectURL(blob);
    const link=document.createElement("a");
    link.href=url;link.download=`invoices-${new Date().toISOString().slice(0,10)}.csv`;link.click();
    URL.revokeObjectURL(url);
  };

  const statusColors = {unpaid:"badge-red",partial:"badge-amber",paid:"badge-green",cancelled:"badge-gray"};
  const counts = {unpaid:invoices.filter(i=>i.status==="unpaid").length,partial:invoices.filter(i=>i.status==="partial").length,paid:invoices.filter(i=>i.status==="paid").length};
  const totalOutstanding = invoices.filter(i=>i.status!=="paid"&&i.status!=="cancelled").reduce((s,i)=>s+(parseFloat(i.total)||0)-(parseFloat(i.amount_paid)||0),0);

  return (
    <div>
      <div className="page-header">
        <div><div className="page-title">Invoices</div><div className="page-subtitle">GST tax invoices · {invoices.length} total</div></div>
        <button className="btn btn-primary" onClick={()=>setModal("new")}>+ New invoice</button>
      </div>
      <div className="stats-row">
        <div className="stat-card" style={{borderLeftColor:"var(--danger)"}}><div className="stat-value">{counts.unpaid}</div><div className="stat-label">Unpaid</div></div>
        <div className="stat-card" style={{borderLeftColor:"var(--warn)"}}><div className="stat-value">{counts.partial}</div><div className="stat-label">Partial</div></div>
        <div className="stat-card"><div className="stat-value">{counts.paid}</div><div className="stat-label">Paid</div></div>
        <div className="stat-card" style={{borderLeftColor:"var(--danger)"}}><div className="stat-value" style={{fontSize:21}}>{fmt(totalOutstanding)}</div><div className="stat-label">Outstanding</div></div>
      </div>
      <div style={{background:"#fff",border:"1px solid var(--paper3)",borderRadius:10,padding:"16px 20px",marginBottom:20,boxShadow:"var(--shadow)"}}>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12,marginBottom:12}}>
          <div className="form-group"><label>Search</label><div className="search-wrap" style={{maxWidth:"100%"}}><span className="search-icon">🔍</span><input placeholder="Invoice number or customer…" value={search} onChange={e=>setSearch(e.target.value)}/></div></div>
          <div className="form-group"><label>From date</label><input type="date" value={fromDate} onChange={e=>setFromDate(e.target.value)} style={{padding:"8px 12px",border:"1px solid var(--paper3)",borderRadius:8,fontFamily:"var(--mono)",fontSize:13,background:"var(--paper)",outline:"none"}}/></div>
          <div className="form-group"><label>To date</label><input type="date" value={toDate} onChange={e=>setToDate(e.target.value)} style={{padding:"8px 12px",border:"1px solid var(--paper3)",borderRadius:8,fontFamily:"var(--mono)",fontSize:13,background:"var(--paper)",outline:"none"}}/></div>
        </div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
          {["all","unpaid","partial","paid"].map(f=>(
            <button key={f} className={`filter-pill ${filter===f?"active":""}`} onClick={()=>setFilter(f)}>{f==="all"?"All":f.charAt(0).toUpperCase()+f.slice(1)}</button>
          ))}
          {(search||fromDate||toDate||filter!=="all")&&<button className="btn btn-ghost btn-sm" onClick={()=>{setSearch("");setFromDate("");setToDate("");setFilter("all");}}>✕ Clear</button>}
          <button className="btn btn-ghost btn-sm" style={{marginLeft:"auto"}} onClick={downloadInvoiceCSV}>⬇ CSV</button>
          <span style={{fontSize:12,color:"var(--ink3)"}}>{filtered.length} invoice{filtered.length!==1?"s":""}</span>
        </div>
      </div>
      <div className="card">
        <table>
          <thead><tr><th>Invoice no.</th><th>Customer</th><th>Date</th><th>Due date</th><th style={{textAlign:"right"}}>Total</th><th style={{textAlign:"right"}}>Paid</th><th style={{textAlign:"right"}}>Balance</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {loading&&<tr className="loading-row"><td colSpan={9}><span className="spinner"/></td></tr>}
            {!loading&&filtered.length===0&&<tr><td colSpan={9}><div className="empty-state"><div className="icon">🧾</div><p>No invoices yet.</p></div></td></tr>}
            {!loading&&filtered.map(inv=>(
              <tr key={inv.id}>
               <td>
  <span className="wo-number">{inv.invoice_number}</span>
  {inv.work_order_ids && inv.work_order_ids.length > 0
    ? <div style={{fontSize:11,color:"var(--ink3)",marginTop:3}}>{inv.work_order_ids.length} WO{inv.work_order_ids.length>1?"s":""}</div>
    : inv.work_order_id && <div style={{fontSize:11,color:"var(--ink3)",marginTop:3}}>1 WO</div>
  }
</td>
                <td style={{fontWeight:500}}>{inv.customers?.name||"—"}</td>
                <td className="mono-text" style={{fontSize:12}}>{fmtDMY(inv.invoice_date)}</td>
                <td className="mono-text" style={{fontSize:12}}>{inv.due_date||"—"}</td>
                <td style={{textAlign:"right"}} className="mono-text">{fmt(inv.total)}</td>
                <td style={{textAlign:"right"}} className="mono-text">{fmt(inv.amount_paid||0)}</td>
                <td style={{textAlign:"right",color:parseFloat(inv.total)-parseFloat(inv.amount_paid||0)>0?"var(--danger)":"var(--accent)"}} className="mono-text">{fmt(parseFloat(inv.total)-parseFloat(inv.amount_paid||0))}</td>
                <td><span className={`badge ${statusColors[inv.status]||"badge-gray"}`}>{inv.status}</span></td>
                <td style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                  <button className="btn btn-ghost btn-sm" onClick={()=>openPrint(inv)}>🖨 Print</button>
                  <button className="btn btn-ghost btn-sm" onClick={()=>setEditInv(inv)}>✎ Edit</button>
                  {inv.customers?.gstin&&<button className="btn btn-ghost btn-sm" onClick={()=>downloadEInvoice(inv)} title="Download e-Invoice JSON for GST portal upload">⬇ e-Inv</button>}
                  <button className="btn btn-danger btn-sm" onClick={()=>deleteInvoice(inv)}>🗑</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {modal==="new"&&<CreateInvoiceModal toast={toast} onClose={()=>setModal(null)} onSave={()=>{setModal(null);load();}}/>}
      {printInv&&<InvoicePrintView invoice={printInv} onClose={()=>setPrintInv(null)}/>}
      {editInv&&<EditInvoiceModal invoice={editInv} toast={toast} onClose={()=>setEditInv(null)} onChanged={()=>load()}/>}
    </div>
  );
}

// ── EDIT INVOICE (edit a linked work order; invoiced WOs can only be
// changed through here — direct edits are blocked) ────────────────
function EditInvoiceModal({ invoice, onClose, onChanged, toast }) {
  const [inv, setInv]           = useState(invoice);
  const [items, setItems]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [editRecord, setEditRecord] = useState(null); // {..., _source:"wo"|"dw"}
  const [busy, setBusy]         = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const woIds = (inv.work_order_ids && inv.work_order_ids.length > 0)
      ? inv.work_order_ids
      : [inv.work_order_id].filter(Boolean);
    const [{data:wos}, {data:dws}] = await Promise.all([
      supabase.from("work_orders").select("*, customers(id,name,type,phone)").in("id", woIds.length?woIds:["00000000-0000-0000-0000-000000000000"]),
      supabase.from("direct_work_orders").select("*, customers(id,name,type,phone)").in("id", woIds.length?woIds:["00000000-0000-0000-0000-000000000000"]),
    ]);
    setItems([
      ...(wos||[]).map(w=>({...w,_source:"wo"})),
      ...(dws||[]).map(d=>({...d,_source:"dw"})),
    ]);
    setLoading(false);
  }, [inv]);

  useEffect(()=>{load();},[load]);

  const handleWOSaved = async () => {
    setEditRecord(null);
    setBusy(true);
    try {
      await recomputeInvoiceTotals(inv);
      toast("Work order updated — invoice totals refreshed.","success");
    } catch(e) { toast("Work order saved, but refreshing invoice totals failed: "+e.message,"error"); }
    setBusy(false);
    load();
    onChanged && onChanged();
  };

  // Detaches one work order from this invoice — it goes back to "completed"
  // so it's available to add to a different invoice later, rather than
  // forcing the whole invoice to be deleted just to fix one wrong WO.
  const removeFromInvoice = async (item) => {
    const currentIds = (inv.work_order_ids && inv.work_order_ids.length > 0)
      ? inv.work_order_ids
      : [inv.work_order_id].filter(Boolean);
    if (currentIds.length <= 1) {
      alert("This is the only work order on this invoice — delete the whole invoice instead if you want to remove it.");
      return;
    }
    const label = item._source==="dw" ? item.dw_number : item.wo_number;
    if (!window.confirm(`Remove ${label} from invoice ${inv.invoice_number}? It will become available to invoice again.`)) return;
    setBusy(true);
    try {
      const newIds = currentIds.filter(id => id !== item.id);
      const { error } = await supabase.from("invoices").update({work_order_ids:newIds,updated_at:new Date().toISOString()}).eq("id", inv.id);
      if (error) throw error;
      if (item._source==="dw") {
        await supabase.from("direct_work_orders").update({status:"completed",updated_at:new Date().toISOString()}).eq("id", item.id);
      } else {
        await supabase.from("work_orders").update({status:"completed",updated_at:new Date().toISOString()}).eq("id", item.id);
      }
      const updatedInv = {...inv, work_order_ids:newIds};
      await recomputeInvoiceTotals(updatedInv);
      setInv(updatedInv);
      toast(`${label} removed from invoice — it's available to invoice again.`,"success");
    } catch(e) { toast("Error removing work order: "+e.message,"error"); }
    setBusy(false);
    load();
    onChanged && onChanged();
  };

  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal">
        <div className="modal-header">
          <div className="modal-title">Edit invoice {inv.invoice_number}</div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div style={{fontSize:13,color:"var(--ink3)",marginBottom:16}}>
            This invoice is already issued, so its work order(s) are locked from direct editing. Edit one here instead — the invoice's totals recalculate automatically afterward. Removing a work order takes it off this invoice and frees it up to be invoiced separately.
          </div>
          {(loading||busy)&&<div style={{textAlign:"center",padding:30}}><span className="spinner"/></div>}
          {!loading&&!busy&&items.length===0&&(
            <div style={{textAlign:"center",padding:30,color:"var(--ink3)",fontSize:13}}>No linked work orders found for this invoice.</div>
          )}
          {!loading&&!busy&&items.map(it=>(
            <div key={it.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 14px",border:"1px solid var(--paper3)",borderRadius:8,marginBottom:8}}>
              <div>
                <div style={{fontWeight:500}}>{it._source==="dw"?it.dw_number:it.wo_number}</div>
                <div style={{fontSize:12,color:"var(--ink3)"}}>{it._source==="dw"?"Direct work":`${it.book_type||"—"}${it.finish_type?" · "+it.finish_type:""}`}</div>
              </div>
              <div style={{display:"flex",gap:6}}>
                <button className="btn btn-ghost btn-sm" onClick={()=>setEditRecord(it)}>✎ Edit</button>
                <button className="btn btn-ghost btn-sm" style={{color:"var(--danger)"}} onClick={()=>removeFromInvoice(it)}>✕ Remove</button>
              </div>
            </div>
          ))}
        </div>
      </div>
      {editRecord&&editRecord._source==="wo"&&(
        <OrderForm mode="wo" record={editRecord} unlockInvoiced
          onClose={()=>setEditRecord(null)} onSave={handleWOSaved} toast={toast}/>
      )}
      {editRecord&&editRecord._source==="dw"&&(
        <DirectWorkModal record={editRecord} unlockInvoiced
          onClose={()=>setEditRecord(null)} onSave={handleWOSaved} toast={toast}/>
      )}
    </div>
  );
}

// ── ORDER FORM (shared WO + Estimate) ────────────────────────
function OrderForm({ mode, record, onClose, onSave, toast, sequences, estSequences, reloadSeqs, unlockInvoiced }) {
  const isEdit = !!record;
  const isWO   = mode === "wo";
  const [series, setSeries]             = useState(record?.series_code||"");
  const [customerQ, setCustomerQ]       = useState("");
  const [customer, setCustomer]         = useState(null);
  const [bookType, setBookType]         = useState(record?.book_type||"");
  const [finishType, setFinishType]     = useState(record?.finish_type||"");
const [totalSheets, setTotalSheets]   = useState(record?.total_sheets||"");
const [colorCount, setColorCount]     = useState(record?.color_count||"");
const [micron, setMicron]             = useState(record?.micron||"");
const [allVariants, setAllVariants]   = useState([]);
const [custVariantPrices, setCustVariantPrices] = useState({});

const getVariantPrice = useCallback((finishName, colorCnt, mic, woSize) => {
  if (!colorCnt || !mic || !woSize) return null;
  const paperType = FINISH_TO_PAPER_TYPE[finishName];
  if (!paperType) return null;
  const sizeGroup = getSizeGroup(woSize);
  const variant = allVariants.find(v =>
    v.paper_type===paperType && v.color_count===colorCnt &&
    v.micron===mic && v.size_group===sizeGroup
  );
  if (!variant) return null;
  const price = custVariantPrices[variant.id]!=null
    ? custVariantPrices[variant.id]
    : parseFloat(variant.base_price)||0;
  return { variant, price, hasCustPrice: custVariantPrices[variant.id]!=null };
}, [allVariants, custVariantPrices]);

const [size, setSize]                 = useState(record?.size||"");  
const [notes, setNotes]               = useState(record?.notes||"");
  const [orderDate, setOrderDate]       = useState(record?.order_date||new Date().toISOString().slice(0,10));
  const [estStatus, setEstStatus]       = useState(record?.status||"draft");
  const [lines, setLines]               = useState([]);
  const [saving, setSaving]             = useState(false);
  const [customers, setCustomers]       = useState([]);
  const [products, setProducts]         = useState([]);
  const [priceInfo, setPriceInfo]       = useState({});
  const [mixConfirmed, setMixConfirmed] = useState(false);
  const [availableEstimates, setAvailableEstimates] = useState([]);
  const [importedFrom, setImportedFrom] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [printRecord, setPrintRecord]   = useState(null);
  const [printLines, setPrintLines]     = useState([]);
  const [printThenClose, setPrintThenClose] = useState(false);

  const isMix = IS_MIX_FINISH(finishType);

  const makeDefaultRow = (slot) => ({slotKey:slot.slotKey,slotLabel:slot.slotLabel,isDefault:true,isMix:false,product_id:"",productQ:"",productName:"",quantity:1,unit_price:0,tax_rate:0,unit:"nos"});

  // Builds the default rows for a book type's slots, auto-selecting the
  // Cake Charges product when there's exactly one match — Cake book type
  // only ever uses one cake product, so there's no reason to make the user
  // search for and pick it every time. Used by both handleBookTypeChange and
  // handleFinishTypeChange, since the latter also rebuilds the default rows
  // (for the mix panel) and would otherwise wipe the auto-selection out.
  const buildDefaultLines = (slots) => {
    const newLines=slots.map(makeDefaultRow);
    const cakeSlotIdx=slots.findIndex(s=>s.slotKey==="cake");
    let cakeProduct=null;
    if(cakeSlotIdx!==-1){
      const matches=products.filter(p=>slots[cakeSlotIdx].filterFn(p));
      if(matches.length===1){
        cakeProduct=matches[0];
        const defaultPrice=(getSizeGroup(size)==="small"&&cakeProduct.base_price_small!=null)
          ? parseFloat(cakeProduct.base_price_small) : (parseFloat(cakeProduct.base_price)||0);
        newLines[cakeSlotIdx]={...newLines[cakeSlotIdx],product_id:cakeProduct.id,productName:cakeProduct.name,productQ:cakeProduct.name,unit:cakeProduct.unit,tax_rate:parseFloat(cakeProduct.tax_rate)||0,unit_price:defaultPrice,quantity:newLines[cakeSlotIdx].quantity||1};
      }
    }
    return { newLines, cakeSlotIdx, cakeProduct };
  };

  const applyDefaultLines = (slots) => {
    const { newLines, cakeSlotIdx, cakeProduct } = buildDefaultLines(slots);
    setLines(prev=>[...newLines,...prev.filter(l=>!l.isDefault)]);
    setPriceInfo(cakeProduct?{[cakeSlotIdx]:"base"}:{});
    if(cakeProduct&&customer?.id){
      const sizeGroup=getProductSizeGroup(size);
      lookupSpecialPrice(customer.id,cakeProduct.id,sizeGroup,colorCount,micron).then(special=>{
        if(!special)return;
        setLines(prev=>prev.map((l,i)=>i===cakeSlotIdx?{...l,unit_price:parseFloat(special.agreed_price)}:l));
        setPriceInfo(p=>({...p,[cakeSlotIdx]:"special"}));
        toast(`Special price for ${cakeProduct.name}`,"info");
      });
    }
  };

  const handleBookTypeChange = (newBT) => {
    setBookType(newBT); if(newBT==="Print Only")setFinishType("");
    applyDefaultLines(BOOK_TYPE_DEFAULTS[newBT]||[]);
    setMixConfirmed(false);
  };

  const handleFinishTypeChange = (newF) => {
    setFinishType(newF); setMixConfirmed(false);
    applyDefaultLines(BOOK_TYPE_DEFAULTS[bookType]||[]);
  };

  useEffect(()=>{
// Load all customers with pagination
const loadAllCustomers = async () =>
  fetchAll("customers", { select:"id,name,type,phone", eq:{is_active:true}, order:"name" });

Promise.all([
  loadAllCustomers(),
  supabase.from("products").select("id,name,unit,base_price,base_price_small,tax_rate").eq("is_active",true).order("name"),
  supabase.from("product_variants").select("*").eq("is_active",true),
]).then(([customers, p, v])=>{
  setCustomers(customers||[]);
  setProducts(p.data||[]);
  setAllVariants(v.data||[]);
});


if(record){
  const cust=record.customers||null;
  setCustomerQ(cust?.name||""); setCustomer(cust);
  if(isWO&&cust?.id)loadEstimates(cust.id);
  loadLines(record.id,record.book_type,isWO?"wo_line_items":"estimate_line_items",isWO?"work_order_id":"estimate_id");
  if(IS_MIX_FINISH(record.finish_type))setMixConfirmed(true);
  // Load customer variant prices for edit mode
  if(cust?.id){
    supabase.from("customer_pricing").select("variant_id,product_id,agreed_price")
      .eq("customer_id",cust.id)
      .then(({data:cp})=>{
        const varMap={};
        const prodMap={};
        (cp||[]).forEach(x=>{
          if(x.variant_id) varMap[x.variant_id]=parseFloat(x.agreed_price);
          if(x.product_id) prodMap[x.product_id]=parseFloat(x.agreed_price);
        });
        setCustVariantPrices(varMap);
        // Set priceInfo badges after a short delay to let lines load first
        setTimeout(()=>{
          setLines(prev=>prev.map((line,idx)=>{
            if(line.variant_id && varMap[line.variant_id]!=null) return line;
            if(line.product_id && prodMap[line.product_id]!=null) return line;
            return line;
          }));
          setPriceInfo(prev=>{
            const updated={...prev};
            // Will be set after lines are available — handled below
            return updated;
          });
        },300);
      });
  }
}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);

// Auto-fill paper line price when color, micron or size changes
// Auto-fill paper line price and quantity when color/micron/size/sheets changes
const prevAutoFill = useRef(null);
useEffect(() => {
  // When editing a saved order, variant/customer prices finishing their
  // load must not overwrite the price that was saved (or typed) — only a
  // real change to finish/size/color/micron re-prices, and a sheet-count
  // change updates just the quantity.
  const cur = {colorCount, micron, size, finishType, totalSheets};
  const prev = prevAutoFill.current;
  prevAutoFill.current = cur;
  let keepPrice = false;
  if (isEdit) {
    if (!prev) return;
    const repriced = prev.colorCount!==cur.colorCount || prev.micron!==cur.micron || prev.size!==cur.size || prev.finishType!==cur.finishType;
    if (!repriced && prev.totalSheets===cur.totalSheets) return;
    keepPrice = !repriced;
  }
  if (!colorCount || !micron || !size || !bookType) return;
  if (isMix) return;
  setLines(prev => prev.map(line => {
    if (line.slotKey !== "paper") return line;
    const baseFinish = finishType?.replace("+Mix","").trim();
    const result = getVariantPrice(baseFinish, colorCount, micron, size);
    if (!result) return line;
    return {
      ...line,
      variant_id:   result.variant.id,
      product_id:   result.variant.product_id || line.product_id,
      productName:  `${baseFinish} · ${colorCount} · ${micron}`,
      productQ:     `${baseFinish} · ${colorCount} · ${micron}`,
      unit_price:   keepPrice ? line.unit_price : result.price,
      quantity:     parseInt(totalSheets)||line.quantity||1,
    };
  }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [colorCount, micron, size, finishType, totalSheets, allVariants, custVariantPrices]);

// Re-price already-added regular product lines (Pad, Sheets, etc.) when
// size/color/micron change, so switching back to an earlier combination
// shows that combination's price again instead of leaving whatever was
// last resolved for the previous selection.
useEffect(() => {
  const targets = lines.map((l,i)=>({l,i})).filter(({l})=>l.product_id && !l.variant_id && !l.isMix && l.slotKey!=="paper");
  if (targets.length===0) return;
  const sizeGroup = getProductSizeGroup(size);
  let cancelled = false;
  (async () => {
    const updates = await Promise.all(targets.map(async ({l,i}) => {
      const product = products.find(p=>p.id===l.product_id);
      if (!product) return null;
      const defaultPrice = (getSizeGroup(size)==="small" && product.base_price_small!=null)
        ? parseFloat(product.base_price_small) : (parseFloat(product.base_price)||0);
      let price = defaultPrice, pType = "base";
      if (customer?.id) {
        const special = await lookupSpecialPrice(customer.id, product.id, sizeGroup, colorCount, micron);
        if (special) { price = parseFloat(special.agreed_price); pType = "special"; }
        else pType = "new";
      }
      return { i, price, pType };
    }));
    if (cancelled) return;
    setLines(prev => prev.map((l,i) => {
      const u = updates.find(x=>x && x.i===i);
      return u ? {...l, unit_price:u.price} : l;
    }));
    setPriceInfo(p => {
      const np = {...p};
      updates.forEach(u => { if (u) np[u.i] = u.pType; });
      return np;
    });
  })();
  return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [size, colorCount, micron, customer?.id]);

// Set special price badges when lines and customer prices are both loaded
useEffect(() => {
  if (!customer?.id) return;
  if (lines.length === 0) return;
  const newPriceInfo = {};
  lines.forEach((line, idx) => {
    if (line.variant_id && custVariantPrices[line.variant_id] != null) {
      newPriceInfo[idx] = "special";
    } else if (line.product_id && !line.variant_id) {
      // Check product-level pricing, scoped to the current size group +
      // color count + micron combination — a price saved for one
      // combination shouldn't badge a line at a different one.
      const sizeGroup = getProductSizeGroup(size);
      const cc = colorCount||null, mc = micron||null;
      supabase.from("customer_pricing")
        .select("size_group,color_count,micron")
        .eq("customer_id", customer.id)
        .eq("product_id", line.product_id)
        .then(({data}) => {
          const match = (data||[]).some(r =>
            (r.size_group===sizeGroup && r.color_count===cc && r.micron===mc) ||
            (r.size_group==null && r.color_count==null && r.micron==null)
          );
          if (match) setPriceInfo(p => ({...p, [idx]:"special"}));
        });
    }
  });
  if (Object.keys(newPriceInfo).length > 0) {
    setPriceInfo(p => ({...p, ...newPriceInfo}));
  }
}, [lines, custVariantPrices, customer?.id, size, colorCount, micron]);

const loadLines = async (id, wBookType, table, fkCol) => {
  const {data} = await supabase.from(table)
    .select("*, products(name,unit,base_price,tax_rate), product_variants(paper_type,color_count,micron,size_group)")
    .eq(fkCol, id).order("sort_order");
  if (!data) return;
  const slots = BOOK_TYPE_DEFAULTS[wBookType] || [];

  // Count paper lines to detect mix
  const paperLines = data.filter(l => {
    const pname = (l.products?.name||"").toLowerCase();
    return pname === "paper" || pname.startsWith("paper-") || l.product_variants;
  });
  const hasMix = paperLines.length > 1;

  let slotIndex = 0;
  const loadedLines = data.map((l) => {
    const productName = (l.products?.name||"");
    const pv = l.product_variants;

    // Build display name — use variant details if available
    const displayName = pv
      ? `${pv.paper_type} · ${pv.color_count} · ${pv.micron}`
      : productName;

    const isPaperLine = productName.toLowerCase()==="paper" ||
                        productName.toLowerCase().startsWith("paper-") ||
                        !!pv;

    if (hasMix && isPaperLine) {
      return {
        id:l.id, slotKey:"paper-mix", slotLabel:null,
        isDefault:true, isMix:true,
        product_id:l.product_id, variant_id:l.variant_id||null,
        productName:displayName, productQ:displayName,
        quantity:l.quantity, unit_price:l.unit_price,
        tax_rate:l.products?.tax_rate||0, unit:l.products?.unit||"nos"
      };
    }

    if (!hasMix && isPaperLine) {
      // Single paper line — find paper slot
      return {
        id:l.id, slotKey:"paper", slotLabel:"Paper",
        isDefault:true, isMix:false,
        product_id:l.product_id, variant_id:l.variant_id||null,
        productName:displayName, productQ:displayName,
        quantity:l.quantity, unit_price:l.unit_price,
        tax_rate:l.products?.tax_rate||0, unit:l.products?.unit||"nos"
      };
    }

    // Non-paper lines — match by slot position skipping paper slot if variant
    let slot = slots[slotIndex]||null;
    // Skip paper slot — already handled above
    while (slot?.slotKey==="paper") { slotIndex++; slot=slots[slotIndex]||null; }
    slotIndex++;

    return {
      id:l.id, slotKey:slot?.slotKey||null, slotLabel:slot?.slotLabel||null,
      isDefault:!!slot, isMix:false,
      product_id:l.product_id, variant_id:l.variant_id||null,
      productName:displayName, productQ:displayName,
      quantity:l.quantity, unit_price:l.unit_price,
      tax_rate:l.products?.tax_rate||0, unit:l.products?.unit||"nos"
    };
  });
  setLines(loadedLines);
};

  const loadEstimates = async (customerId) => {
    if(!isWO||!customerId)return;
    const {data}=await supabase.from("estimates").select("id,est_number,book_type,finish_type,total_sheets,order_date,notes,size,color_count,micron").eq("customer_id",customerId).eq("status","approved").is("converted_to_wo",null).eq("is_deleted",false).order("order_date",{ascending:false});
    setAvailableEstimates(data||[]);
  };

  const importEstimate = async (est) => {
    const {data:estLines}=await supabase.from("estimate_line_items")
      .select("*, products(id,name,unit,base_price,tax_rate), product_variants(paper_type,color_count,micron,size_group)")
      .eq("estimate_id",est.id).order("sort_order");
    setBookType(est.book_type||""); setFinishType(est.finish_type||""); setTotalSheets(est.total_sheets||""); setNotes(est.notes||""); setMixConfirmed(false);
    setSize(est.size||""); setColorCount(est.color_count||""); setMicron(est.micron||"");
    if(IS_MIX_FINISH(est.finish_type))setMixConfirmed(true);
    if(estLines&&estLines.length>0){
      const slots=BOOK_TYPE_DEFAULTS[est.book_type]||[];
      // Same classification as loadLines() — mix-paper lines are keyed by
      // variant_id, not product_id, and were previously dropped to a bare
      // "Paper" label because this only ever joined `products`.
      const paperLines = estLines.filter(l => {
        const pname = (l.products?.name||"").toLowerCase();
        return pname==="paper" || pname.startsWith("paper-") || l.product_variants;
      });
      const hasMix = paperLines.length > 1;
      let slotIndex = 0;
      const classified = estLines.map(l => {
        const productName = l.products?.name||"";
        const pv = l.product_variants;
        const displayName = pv ? `${pv.paper_type} · ${pv.color_count} · ${pv.micron}` : productName;
        const isPaperLine = productName.toLowerCase()==="paper" || productName.toLowerCase().startsWith("paper-") || !!pv;
        let base;
        if (hasMix && isPaperLine) base={slotKey:"paper-mix",slotLabel:null,isDefault:true,isMix:true};
        else if (!hasMix && isPaperLine) base={slotKey:"paper",slotLabel:"Paper",isDefault:true,isMix:false};
        else {
          let slot=slots[slotIndex]||null;
          while (slot?.slotKey==="paper") { slotIndex++; slot=slots[slotIndex]||null; }
          slotIndex++;
          base={slotKey:slot?.slotKey||null,slotLabel:slot?.slotLabel||null,isDefault:!!slot,isMix:false};
        }
        return {...base,product_id:l.product_id,variant_id:l.variant_id||null,productQ:displayName,productName:displayName,quantity:l.quantity,unit_price:parseFloat(l.unit_price)||0,tax_rate:l.products?.tax_rate||0,unit:l.products?.unit||"nos"};
      });
      const importedLines=await Promise.all(classified.map(async(line)=>{
        if(customer?.id&&line.product_id){const{data:cp}=await supabase.from("customer_pricing").select("agreed_price").eq("customer_id",customer.id).eq("product_id",line.product_id).single();if(cp)return{...line,unit_price:parseFloat(cp.agreed_price)};}
        else if(customer?.id&&line.variant_id){const{data:cp}=await supabase.from("customer_pricing").select("agreed_price").eq("customer_id",customer.id).eq("variant_id",line.variant_id).single();if(cp)return{...line,unit_price:parseFloat(cp.agreed_price)};}
        return line;
      }));
      setLines(importedLines);
    }
    setImportedFrom(est); setAvailableEstimates([]);
    toast(`Estimate ${est.est_number} imported`,"info");
  };

  // Special (customer-specific) pricing for regular products is keyed on
  // size group (small / 10x26 / standard) + color count + micron — an
  // exact match on all three wins; a fully-unscoped row (all three NULL,
  // from before this existed) is the fallback that applies at any
  // size/color/micron.
  const lookupSpecialPrice = async (customerId, productId, sizeGroup, colorCountVal, micronVal) => {
    const { data } = await supabase.from("customer_pricing")
      .select("agreed_price,size_group,color_count,micron")
      .eq("customer_id",customerId).eq("product_id",productId);
    if (!data || data.length===0) return null;
    const cc = colorCountVal||null, mc = micronVal||null;
    const exact = data.find(r=>r.size_group===sizeGroup && r.color_count===cc && r.micron===mc);
    if (exact) return exact;
    return data.find(r=>r.size_group==null && r.color_count==null && r.micron==null) || null;
  };

  const handleProductSelect = async (product, lineIdx) => {
    const sizeGroup = getProductSizeGroup(size);
    const defaultPrice = (getSizeGroup(size)==="small" && product.base_price_small!=null)
      ? parseFloat(product.base_price_small) : (parseFloat(product.base_price)||0);
    const newLines=[...lines];
    newLines[lineIdx]={...newLines[lineIdx],product_id:product.id,productName:product.name,productQ:product.name,unit:product.unit,tax_rate:parseFloat(product.tax_rate)||0,unit_price:defaultPrice,quantity:newLines[lineIdx].quantity||1};
    let pType="base";
    if(customer?.id){
      const special = await lookupSpecialPrice(customer.id, product.id, sizeGroup, colorCount, micron);
      if(special){newLines[lineIdx].unit_price=parseFloat(special.agreed_price);pType="special";toast(`Special price for ${product.name}`,"info");}else pType="new";
    }
    setLines(newLines); setPriceInfo(p=>({...p,[lineIdx]:pType}));
  };
const saveSpecialPrice = async (lineIdx) => {
  const line = lines[lineIdx];
  if (!customer?.id) return;

  const price = parseFloat(line.unit_price);
  if (!price) return alert("Please enter a price first.");

  // Case 1 — mix paper line with variant_id
  if (line.isMix && line.variant_id) {
    const { data: ex } = await supabase.from("customer_pricing").select("id").eq("customer_id",customer.id).eq("variant_id",line.variant_id).limit(1);
    if (ex?.length > 0) {
      const{error}=await supabase.from("customer_pricing").update({agreed_price:price,updated_at:new Date().toISOString()}).eq("customer_id",customer.id).eq("variant_id",line.variant_id);
      if(error){alert("Error: "+error.message);return;}
    } else {
const{error}=await supabase.from("customer_pricing").insert({customer_id:customer.id,product_id:null,variant_id:line.variant_id,agreed_price:price,updated_at:new Date().toISOString()});
      if(error){alert("Error: "+error.message);return;}
    }
    setPriceInfo(p=>({...p,[lineIdx]:"special"}));
    toast(`Special price saved for ${line.slotLabel}!`);
    setCustVariantPrices(p=>({...p,[line.variant_id]:price}));
    return;
  }

  // Case 2 — paper slot (non-mix) with color/micron/size selected
  if (line.slotKey==="paper" && colorCount && micron && size) {
    const baseFinish = finishType?.replace("+Mix","").trim();
    const result = getVariantPrice(baseFinish, colorCount, micron, size);
    if (result) {
      const { data: ex } = await supabase.from("customer_pricing").select("id").eq("customer_id",customer.id).eq("variant_id",result.variant.id).limit(1);
      if (ex?.length > 0) {
        const{error}=await supabase.from("customer_pricing").update({agreed_price:price,updated_at:new Date().toISOString()}).eq("customer_id",customer.id).eq("variant_id",result.variant.id);
        if(error){alert("Error: "+error.message);return;}
      } else {
const{error}=await supabase.from("customer_pricing").insert({customer_id:customer.id,product_id:null,variant_id:result.variant.id,agreed_price:price,updated_at:new Date().toISOString()});
        if(error){alert("Error: "+error.message);return;}
      }
      setPriceInfo(p=>({...p,[lineIdx]:"special"}));
      toast(`Special price saved for ${baseFinish} · ${colorCount} · ${micron}!`);
      setCustVariantPrices(p=>({...p,[result.variant.id]:price}));
      return;
    }
  }

  // Case 3 — regular product (Pad, Add Ons etc.) — scoped to the current
  // size group + color count + micron combination, so a price saved for
  // one combination doesn't leak into another.
  if (!line.product_id) return alert("No product selected.");
  const sizeGroup = getProductSizeGroup(size);
  const cc = colorCount||null, mc = micron||null;
  const { data: all } = await supabase.from("customer_pricing").select("id,size_group,color_count,micron").eq("customer_id",customer.id).eq("product_id",line.product_id);
  const existing = (all||[]).find(r=>r.size_group===sizeGroup && r.color_count===cc && r.micron===mc);
  if (existing) {
    const{error}=await supabase.from("customer_pricing").update({agreed_price:price,updated_at:new Date().toISOString()}).eq("id",existing.id);
    if(error){alert("Error: "+error.message);return;}
  } else {
    const{error}=await supabase.from("customer_pricing").insert({customer_id:customer.id,product_id:line.product_id,size_group:sizeGroup,color_count:cc,micron:mc,agreed_price:price,updated_at:new Date().toISOString()});
    if(error){alert("Error: "+error.message);return;}
  }
  setPriceInfo(p=>({...p,[lineIdx]:"special"}));
  const scopeParts=[sizeGroup,cc,mc].filter(Boolean);
  toast(scopeParts.length?`Special price saved for ${line.productName} (${scopeParts.join(" · ")})!`:"Special price saved!");
};

const handleMixConfirm = async (mixLines) => {
  setMixConfirmed(true);
  const enriched = mixLines.map(ml => {
    // Try variant pricing first
    const result = getVariantPrice(ml.slotLabel, colorCount, micron, size);
    if (result) {
      if (result.hasCustPrice) toast(`Special price for ${ml.slotLabel}`,"info");
      return {
        ...ml,
        variant_id:  result.variant.id,
        product_id:  result.variant.product_id || ml.product_id,
        productName: `${ml.slotLabel} · ${colorCount} · ${micron}`,
        productQ:    `${ml.slotLabel} · ${colorCount} · ${micron}`,
        unit_price:  result.price,
      };
    }
    return ml;
  });
  setLines(prev=>[...prev.filter(l=>l.isDefault&&l.slotKey!=="paper"),...enriched,...prev.filter(l=>!l.isDefault)]);
  toast(`${enriched.length} mix line${enriched.length>1?"s":""} added`,"success");
};

  const addLine = () => setLines(l=>[...l,{slotKey:null,slotLabel:null,isDefault:false,isMix:false,product_id:"",productQ:"",productName:"",quantity:1,unit_price:0,tax_rate:0,unit:"nos"}]);
  const removeLine = (idx) => {setLines(l=>l.filter((_,i)=>i!==idx));setPriceInfo(p=>{const n={...p};delete n[idx];return n;});};
  const updateLine = (idx,key,val) => setLines(l=>l.map((line,i)=>i===idx?{...line,[key]:val}:line));

  const grandTotal = lines.reduce((sum,l)=>sum+(parseFloat(l.quantity)||0)*(parseFloat(l.unit_price)||0),0);

const previewNumber = (code) => {
  const prefixes = {OUT:"O",CTR:"C",TPF:"T",PRV:"P"};
  const p = prefixes[code]||code;
  const seqMap = isWO ? sequences : estSequences;
  const s = seqMap[code];
  if (!s) return `${p}-AA-0001`;
  const num = (s.num_part||0) + 1;
  const alpha = s.alpha_part||"AA";
  return `${p}-${alpha}-${String(num).padStart(4,"0")}`;
};

const openPrint = () => {
  setPrintLines(lines.map((l) => ({
    slotLabel:   l.isMix ? null : (l.slotLabel || null),
    isMix:       l.isMix || false,
    productName: l.productName || l.productQ || "—",
    quantity:    l.quantity,
  })));
  setPrintRecord(record);
};

  const save = async (status, alsoPrint=false) => {
    if(isWO&&isEdit&&record.status==="invoiced"&&!unlockInvoiced)return alert("This work order is already invoiced — edit it from the invoice instead.");
    // Editing through the invoice must not un-invoice the work order — it
    // would otherwise reappear as available to invoice a second time.
    if(isWO&&isEdit&&unlockInvoiced&&record.status==="invoiced")status="invoiced";
    if(!series)return alert("Please select a series.");
    if(!customer?.id)return alert("Please select a customer.");
    if(!bookType)return alert("Please select a book type.");
    if(bookType!=="Print Only"&&!finishType&&!isEdit)return alert("Please select a lamination finish.");
    if(isMix&&!mixConfirmed&&!isEdit)return alert("Please complete the mix finish panel and click OK.");
    if(lines.length===0)return alert("Add at least one line item.");
    if(lines.some(l=>!l.product_id))return alert("All line items must have a product selected.");
    setSaving(true);
    try{
      let recordId=record?.id;
      let woNumber=record?.wo_number;
const payload={series_code:series,customer_id:customer.id,status,notes,order_date:orderDate,book_type:bookType,finish_type:bookType!=="Print Only"?finishType:null,total_sheets:parseInt(totalSheets)||null,size:size||null,color_count:colorCount||null,micron:micron||null};
      if(isWO){
        if(!isEdit){
const {data:numData, error:numErr} = await supabase.rpc("next_wo_number", {p_series: series});
if (numErr) throw numErr;
woNumber=String(numData);
const { data:d, error:e } = await supabase.from("work_orders").insert({...payload, wo_number:woNumber}).select().single();
          if(e)throw e; recordId=d.id;
          if(importedFrom)await supabase.from("estimates").update({converted_to_wo:recordId,status:"approved"}).eq("id",importedFrom.id);
        }else{
          const{error:e}=await supabase.from("work_orders").update({...payload,updated_at:new Date().toISOString()}).eq("id",recordId);
          if(e)throw e;
          await supabase.from("wo_line_items").delete().eq("work_order_id",recordId);
        }
       const{error:le}=await supabase.from("wo_line_items").insert(lines.map((l,i)=>({work_order_id:recordId,product_id:l.product_id,variant_id:l.variant_id||null,quantity:parseFloat(l.quantity)||1,unit_price:parseFloat(l.unit_price)||0,sort_order:i})));
        if(le)throw le;
      }else{
        if(!isEdit){
const {data:estNum, error:estNumErr} = await supabase.rpc("next_est_number", {p_series: series});
if (estNumErr) throw estNumErr;
const { data:d, error:e } = await supabase.from("estimates").insert({...payload, est_number:String(estNum)}).select().single();
          if(e)throw e; recordId=d.id;
        }else{
          const{error:e}=await supabase.from("estimates").update({...payload,updated_at:new Date().toISOString()}).eq("id",recordId);
          if(e)throw e;
          await supabase.from("estimate_line_items").delete().eq("estimate_id",recordId);
        }
const{error:le}=await supabase.from("estimate_line_items").insert(lines.map((l,i)=>({estimate_id:recordId,product_id:l.product_id,variant_id:l.variant_id||null,quantity:parseFloat(l.quantity)||1,unit_price:parseFloat(l.unit_price)||0,sort_order:i})));
        if(le)throw le;
      }

      if (reloadSeqs) reloadSeqs();

      if (alsoPrint && isWO) {
        // Build the print record straight from form state instead of the
        // (possibly still-null, for a new record) `record` prop — closing
        // the print view is what actually finishes the save flow here.
        setPrintLines(lines.map((l) => ({
          slotLabel:   l.isMix ? null : (l.slotLabel || null),
          isMix:       l.isMix || false,
          productName: l.productName || l.productQ || "—",
          quantity:    l.quantity,
        })));
        setPrintRecord({
          wo_number: woNumber, order_date: orderDate, series_code: series, status,
          customers: { name: customer?.name, phone: customer?.phone },
          book_type: bookType, finish_type: finishType, size, total_sheets: totalSheets, notes,
        });
        setPrintThenClose(true);
      } else {
        onSave();
      }
    }catch(e){alert("Error: "+e.message);}finally{setSaving(false);}
  };

  const COL="100px 1fr 80px 110px 80px 90px 32px";

  return (
    <>
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal modal-lg">
        <div className="modal-header">
          <div className="modal-title">{isEdit?`Edit ${isWO?record.wo_number:record.est_number}`:`New ${isWO?"work order":"estimate"}`}</div>
          <div style={{display:"flex",gap:8}}>
            {isWO&&isEdit&&<button className="btn btn-ghost btn-sm" onClick={openPrint}>🖨 Print</button>}
            <button className="modal-close" onClick={onClose}>✕</button>
          </div>
        </div>
        <div className="modal-body">
          {isWO&&isEdit&&record.status==="invoiced"&&!unlockInvoiced&&(
            <div style={{marginBottom:16,padding:"12px 16px",background:"var(--warn-l)",borderRadius:8,fontSize:13,color:"var(--warn)"}}>
              ⚠ This work order is already invoiced — changes here won't save. Edit it from the invoice instead.
            </div>
          )}
          {!isEdit&&(
            <div style={{marginBottom:20}}>
              <label style={{display:"block",marginBottom:8}}>Series *</label>
              <div className="wo-series-grid">
                {SERIES.map(s=>(
                  <div key={s.code} className={`series-card ${series===s.code?"selected":""}`} onClick={()=>setSeries(s.code)} style={series===s.code?{borderColor:s.color,background:s.color+"15"}:{}}>
                    <div className="series-code" style={series===s.code?{color:s.color}:{}}>{s.code}</div>
                    <div className="series-label">{s.label}</div>
                    {series===s.code&&<div className="series-next">{previewNumber(s.code)}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="form-grid" style={{marginBottom:20}}>
            <div className="form-group span2">
              <label>Customer *</label>
              <AutoComplete value={customerQ}
                onChange={v=>{setCustomerQ(v);if(!v){setCustomer(null);setAvailableEstimates([]);setImportedFrom(null);}}}
                onSelect={c=>{
  setCustomer(c);setCustomerQ(c.name);
  if(!isEdit){
    if(c.type==="outstation")setSeries("OUT");
    else if(c.type==="counter")setSeries("CTR");
    else if(c.type==="tpf")setSeries("TPF");
    else if(c.type==="prv")setSeries("PRV");
  }
  loadEstimates(c.id);
  // Load customer variant prices
  supabase.from("customer_pricing").select("variant_id,agreed_price")
    .eq("customer_id",c.id).not("variant_id","is",null)
    .then(({data})=>{
      const map={};
      (data||[]).forEach(cp=>{map[cp.variant_id]=parseFloat(cp.agreed_price);});
      setCustVariantPrices(map);
    });
}}
                items={customers} placeholder="Type to search customers…"
                getKey={c=>c.id} getLabel={c=>c.name}
                renderItem={c=><div><div style={{fontWeight:500}}>{c.name}</div><div className="item-sub">{c.type}{c.phone?" · "+c.phone:""}</div></div>}
              />
              {customer&&(
                <div style={{display:"flex",alignItems:"center",gap:8,marginTop:4}}>
                  <span className={`badge ${customer.type==="outstation"?"badge-amber":customer.type==="tpf"?"badge-blue":customer.type==="prv"?"badge-purple":"badge-green"}`}>{customer.type}</span>
                  {!isEdit&&<span style={{fontSize:11,color:"var(--ink3)"}}>Series auto-set — change above if needed</span>}
                </div>
              )}
            </div>
            <div className="form-group"><label>Date</label><input type="date" value={orderDate} onChange={e=>setOrderDate(e.target.value)}/></div>
            {!isWO&&<div className="form-group"><label>Status</label><select value={estStatus} onChange={e=>setEstStatus(e.target.value)}><option value="draft">Draft</option><option value="sent">Sent</option><option value="approved">Approved</option><option value="rejected">Rejected</option></select></div>}
            <div className="form-group"><label>Book type *</label>
              <select value={bookType} onChange={e=>handleBookTypeChange(e.target.value)}>
                <option value="">— Select book type —</option>
                <option value="Photobook">Photobook</option><option value="Cake">Cake</option>
                <option value="Print+Lamination">Print + Lamination</option><option value="Print Only">Print Only</option>
              </select>
            </div>
            {bookType&&bookType!=="Print Only"&&(
              <div className="form-group"><label>Lamination finish *</label>
                <select value={finishType} onChange={e=>handleFinishTypeChange(e.target.value)}>
                  <option value="">— Select lamination —</option>
                  {["NTG","NTG+Mix","Matt","Matt+Mix","Velvet","Velvet+Mix","Luster Paper","Tearable","Photo Matt"].map(f=><option key={f} value={f}>{f}</option>)}
                </select>
              </div>
            )}
<div className="form-group"><label>Total sheets</label><input type="number" min="1" step="1" value={totalSheets} onChange={e=>{setTotalSheets(e.target.value);setMixConfirmed(false);}} placeholder="e.g. 500"/></div>
<div className="form-group">
  <label>Size</label>
  <select value={size} onChange={e=>setSize(e.target.value)}>
    <option value="">— Select size —</option>
    {["12x30","12x36","15x24","18x24","16x24","10x26","9x24","8x24","12x18"].map(s=>(
      <option key={s} value={s}>{s}</option>
    ))}
  </select>
</div>
{bookType&&bookType!=="Print Only"&&(
  <>
    <div className="form-group">
      <label>Color count</label>
      <select value={colorCount} onChange={e=>setColorCount(e.target.value)}>
        <option value="">— Select color —</option>
        {COLOR_COUNTS.map(c=><option key={c} value={c}>{c}</option>)}
      </select>
    </div>
    <div className="form-group">
      <label>Micron</label>
      <select value={micron} onChange={e=>setMicron(e.target.value)}>
        <option value="">— Select micron —</option>
        {MICRONS.map(m=><option key={m} value={m}>{m}</option>)}
      </select>
    </div>
  </>
)}
{colorCount&&micron&&size&&(
  <div className="form-group span2">
    <div style={{padding:"8px 12px",background:"var(--accent-l)",borderRadius:8,fontSize:12,color:"var(--accent)"}}>
      ✓ Size group: <strong>{getSizeGroup(size)==="small"?"Small (8x24, 9x24, 12x18)":"Standard (12x24–18x24)"}</strong> · {colorCount} · {micron}
      {!finishType&&" — select lamination to auto-fill paper price"}
    </div>
  </div>
)}
            <div className="form-group"><label>Notes</label><input value={notes} onChange={e=>setNotes(e.target.value)} placeholder="Optional notes"/></div>
          </div>

          {isWO&&!isEdit&&!importedFrom&&availableEstimates.length>0&&(
            <div className="import-banner">
              <div className="import-banner-title">📋 {availableEstimates.length} approved estimate{availableEstimates.length>1?"s":""} available for {customer?.name}</div>
              {availableEstimates.map(est=>(
                <div key={est.id} className="est-option">
                  <div><div className="est-option-info">{est.est_number}</div><div className="est-option-meta">{est.book_type}{est.finish_type?" · "+est.finish_type:""}{est.total_sheets?" · "+est.total_sheets+" sheets":""} · {fmtDMY(est.order_date)}</div></div>
                  <button className="btn btn-info btn-sm" onClick={()=>importEstimate(est)}>⬇ Import</button>
                </div>
              ))}
            </div>
          )}

          {isWO&&importedFrom&&(
            <div style={{background:"var(--accent-l)",border:"1px solid var(--accent2)",borderRadius:10,padding:"10px 14px",marginBottom:16,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
              <span style={{fontSize:13,color:"var(--accent)",fontWeight:500}}>✓ Imported from {importedFrom.est_number}</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>{setImportedFrom(null);setBookType("");setFinishType("");setTotalSheets("");setLines([]);loadEstimates(customer?.id);}}>✕ Clear</button>
            </div>
          )}

          {isMix&&!mixConfirmed&&<div style={{marginBottom:20}}><MixPanel totalSheets={totalSheets} products={products} customer={customer} onConfirm={handleMixConfirm}/></div>}
          {isMix&&mixConfirmed&&(
            <div style={{marginBottom:16,padding:"10px 14px",background:"var(--accent-l)",borderRadius:8,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
              <div style={{fontSize:13,color:"var(--accent)",fontWeight:500}}>✓ Mix confirmed — {lines.filter(l=>l.isMix).map(l=>l.slotLabel).join(", ")}</div>
              <button className="btn btn-ghost btn-sm" onClick={()=>{setMixConfirmed(false);const slots=BOOK_TYPE_DEFAULTS[bookType]||[];setLines(prev=>[...prev.filter(l=>l.isDefault&&l.slotKey!=="paper"&&!l.isMix),...slots.filter(s=>s.slotKey==="paper").map(makeDefaultRow),...prev.filter(l=>!l.isDefault)]);}}>Edit mix</button>
            </div>
          )}

          <label style={{display:"block",marginBottom:8}}>Line items *</label>
          <div className="line-items-wrap">
            <div className="line-items-header" style={{gridTemplateColumns:COL}}>
              <div>Item type</div><div>Product / service</div><div>Qty</div><div>Unit price (₹)</div><div>GST %</div><div style={{textAlign:"right"}}>Total</div><div></div>
            </div>
            {lines.map((line,idx)=>(
              <div key={idx} className="line-item-row" style={{gridTemplateColumns:COL}}>
                <div style={{display:"flex",alignItems:"flex-start",paddingTop:6}}>
                  {line.isMix?<span className="slot-badge">Paper</span>:line.slotLabel?<span className="slot-badge">{line.slotLabel}</span>:<span className="slot-badge extra">Extra</span>}
                </div>
                <div>
{line.slotKey==="paper" && !line.isMix ? (
  <div>
    {line.productName
      ? <div style={{padding:"6px 10px",background:"var(--accent-l)",borderRadius:8,fontSize:13,color:"var(--accent)",fontWeight:500}}>
          ✓ {line.productName}
          {(!colorCount||!micron||!size)&&(
            <span style={{fontSize:11,color:"var(--warn)",marginLeft:8}}>
              ⚠ Select color, micron and size above to auto-fill price
            </span>
          )}
        </div>
      : <div style={{padding:"6px 10px",background:"var(--warn-l)",borderRadius:8,fontSize:12,color:"var(--warn)"}}>
          ⚠ Select Color count, Micron and Size above to auto-fill paper price
        </div>
    }
  </div>
) : (
  <AutoComplete value={line.productQ||""} onChange={v=>updateLine(idx,"productQ",v)} onSelect={p=>handleProductSelect(p,idx)}
    items={line.isMix
      ? products.filter(p=>p.name&&line.slotLabel?p.name.toLowerCase().startsWith("paper-")&&p.name.toLowerCase().includes(line.slotLabel.toLowerCase()):false)
      : getItemsForLine(line,bookType,products)}
    placeholder={line.slotLabel?`Search ${line.slotLabel}…`:"Search product…"}
    getKey={p=>p.id} getLabel={p=>p.name}
    renderItem={p=><div><div style={{fontWeight:500}}>{p.name}</div><div className="item-sub">{fmt(p.base_price)} / {p.unit}</div></div>}
  />
)}
                  {line.isMix&&line.mixSheets&&<div className="mix-confirmed-tag">{line.mixSheets} sheets · {line.slotLabel}</div>}
                  {line.product_id&&(
                    <div style={{marginTop:4,display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                      {priceInfo[idx]==="special"&&<span className="price-tag special">★ Special price</span>}
                      {priceInfo[idx]==="base"&&<span className="price-tag base">Base price</span>}
                      {priceInfo[idx]==="new"&&<span className="price-tag new">First time for this customer</span>}
                      {customer?.id&&<button className="btn btn-ghost btn-sm" style={{fontSize:11,padding:"2px 7px"}} onClick={()=>saveSpecialPrice(idx)}>★ Save as special</button>}
                    </div>
                  )}
                </div>
                <input type="number" min="0.001" step="any" value={line.quantity} onChange={e=>updateLine(idx,"quantity",e.target.value)}/>
                <input type="number" min="0" step="0.01" value={line.unit_price} onChange={e=>{updateLine(idx,"unit_price",e.target.value);if(priceInfo[idx]==="special")setPriceInfo(p=>({...p,[idx]:"new"}));}}/>
                <select value={line.tax_rate} onChange={e=>updateLine(idx,"tax_rate",e.target.value)}>{["0","5","12","18","28"].map(r=><option key={r} value={r}>{r}%</option>)}</select>
                <div className="line-total">{fmt((parseFloat(line.quantity)||0)*(parseFloat(line.unit_price)||0))}</div>
                <button className="btn btn-danger btn-sm" style={{padding:"4px 8px"}} onClick={()=>removeLine(idx)}>✕</button>
              </div>
            ))}
            <div className="add-line-btn" onClick={addLine}><span style={{fontSize:17}}>+</span> Add line item</div>
          </div>

          {lines.length>0&&<div className="totals-box"><div className="totals-row grand"><span>Order total</span><span>{fmt(grandTotal)}</span></div></div>}

          {isWO&&isEdit&&confirmDelete&&(
            <div style={{background:"var(--danger-l)",border:"1px solid var(--danger)",borderRadius:10,padding:"16px 20px",marginTop:16}}>
              <div style={{fontWeight:500,color:"var(--danger)",marginBottom:8,fontSize:14}}>⚠ Confirm deletion of {record?.wo_number}</div>
              <div style={{fontSize:13,color:"var(--ink2)",marginBottom:14}}>This will permanently remove the work order and all its line items.</div>
              <div style={{display:"flex",gap:10}}>
                <button className="btn btn-ghost btn-sm" onClick={()=>setConfirmDelete(false)}>Cancel</button>
                <button className="btn btn-danger" onClick={async()=>{
                  await supabase.from("wo_line_items").delete().eq("work_order_id",record.id);
                  await supabase.from("work_orders").update({is_deleted:true,updated_at:new Date().toISOString()}).eq("id",record.id);
                  toast(`Work order ${record.wo_number} deleted.`,"error"); onSave();
                }}>Yes, delete {record?.wo_number}</button>
              </div>
            </div>
          )}

          <div className="form-actions">
            <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
            {isWO?(
              <>
                {isEdit&&(record.status!=="invoiced"||unlockInvoiced)&&<button className="btn btn-danger" style={{marginRight:"auto"}} onClick={()=>setConfirmDelete(true)}>Delete work order</button>}
                <button className="btn btn-ghost" onClick={()=>save("open")} disabled={saving}>{saving?<span className="spinner"/>:null} Save as open</button>
                <button className="btn btn-ghost" onClick={()=>save("in_progress",true)} disabled={saving}>{saving?<span className="spinner"/>:"🖨"} {saving?"Saving…":"Save & print"}</button>
                <button className="btn btn-primary" onClick={()=>save("in_progress")} disabled={saving}>{saving?<span className="spinner"/>:"📋"} {saving?"Saving…":"Save & start"}</button>
              </>
            ):(
              <>
                <button className="btn btn-ghost" onClick={()=>save(estStatus||"draft")} disabled={saving}>{saving?<span className="spinner"/>:null} Save as {estStatus||"draft"}</button>
                <button className="btn btn-primary" onClick={()=>{setEstStatus("approved");save("approved");}} disabled={saving}>{saving?<span className="spinner"/>:"✓"} {saving?"Saving…":"Save & approve"}</button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
    {printRecord&&<PrintView record={printRecord} lines={printLines} onClose={()=>{
      setPrintRecord(null); setPrintLines([]);
      if (printThenClose) { setPrintThenClose(false); onSave(); }
    }}/>}
    </>
  );
}

// ── CUSTOMER MODAL ────────────────────────────────────────────
function CustomerModal({ customer, onClose, onSave }) {
 const [form, setForm] = useState(customer||{name:"",type:"counter",phone:"",email:"",address_line1:"",address_line2:"",city:"",state:"Karnataka",pincode:"",country:"India",gstin:""});
  const [saving, setSaving] = useState(false);
  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  const save = async () => {
    if(!form.name.trim())return alert("Customer name is required.");
    if(!form.state)return alert("State is required — it determines CGST/SGST vs IGST on invoices.");
    setSaving(true);
    try{
      if(customer?.id){const{error}=await supabase.from("customers").update({...form,updated_at:new Date().toISOString()}).eq("id",customer.id);if(error)throw error;}
      else{const{error}=await supabase.from("customers").insert(form);if(error)throw error;}
      onSave();
    }catch(e){alert("Error: "+e.message);}finally{setSaving(false);}
  };
  return(
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal">
        <div className="modal-header"><div className="modal-title">{customer?"Edit customer":"New customer"}</div><button className="modal-close" onClick={onClose}>✕</button></div>
        <div className="modal-body">
          <div className="form-grid">
            <div className="form-group span2"><label>Customer name *</label><input value={form.name} onChange={e=>set("name",e.target.value)} autoFocus/></div>
            <div className="form-group"><label>Type</label><select value={form.type} onChange={e=>set("type",e.target.value)}><option value="counter">Counter</option><option value="outstation">Outstation</option><option value="tpf">TPF</option><option value="prv">PRV</option></select></div>
            <div className="form-group"><label>Phone</label><input value={form.phone||""} onChange={e=>set("phone",e.target.value)}/></div>
            <div className="form-group"><label>Email</label><input value={form.email||""} onChange={e=>set("email",e.target.value)}/></div>
            <div className="form-group"><label>GSTIN</label><input value={form.gstin||""} onChange={e=>set("gstin",e.target.value)}/></div>
           <div className="form-group span2"><label>Address line 1</label><input value={form.address_line1||""} onChange={e=>set("address_line1",e.target.value)} placeholder="House / flat no., street, area"/></div>
<div className="form-group span2"><label>Address line 2</label><input value={form.address_line2||""} onChange={e=>set("address_line2",e.target.value)} placeholder="Landmark, locality (optional)"/></div>
<div className="form-group"><label>City</label><input value={form.city||""} onChange={e=>set("city",e.target.value)} placeholder="e.g. Bengaluru"/></div>
<div className="form-group"><label>State *</label>
  <select value={form.state||""} onChange={e=>set("state",e.target.value)}>
    <option value="">— Select state —</option>
    {INDIAN_STATES.map(s=><option key={s.code} value={s.name}>{s.name}</option>)}
  </select>
</div>
<div className="form-group"><label>Pincode</label><input value={form.pincode||""} onChange={e=>set("pincode",e.target.value)} placeholder="e.g. 560010"/></div>
<div className="form-group"><label>Country</label><input value={form.country||"India"} onChange={e=>set("country",e.target.value)} placeholder="India"/></div>
          </div>
          <div className="form-actions">
            <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={save} disabled={saving}>{saving?<span className="spinner"/>:"💾"} {saving?"Saving…":"Save customer"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
// ── CUSTOMER IMPORT MODAL ─────────────────────────────────────
function CustomerImportModal({ onClose, onSave, toast }) {
  const [step, setStep]         = useState(1); // 1=upload, 2=map, 3=preview, 4=result
  const [headers, setHeaders]   = useState([]);
  const [rows, setRows]         = useState([]);
  const [mapping, setMapping]   = useState({
    name:"", phone:"", email:"", address_line1:"", gstin:"", type:""
  });
  const [preview, setPreview]   = useState([]);
  const [result, setResult]     = useState(null);
  const [saving, setSaving]     = useState(false);
  const fileRef = useRef(null);

  const parseFile = (file) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      let text = e.target.result;
      // Handle Excel files via SheetJS
      if (file.name.endsWith(".xlsx") || file.name.endsWith(".xls")) {
        if (!window.XLSX) {
          await new Promise((res,rej)=>{
            const s=document.createElement("script");
            s.src="https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js";
            s.onload=res;s.onerror=rej;document.head.appendChild(s);
          });
        }
        const data = new Uint8Array(e.target.result);
        const wb = window.XLSX.read(data, {type:"array"});
        const ws = wb.Sheets[wb.SheetNames[0]];
        const jsonRows = window.XLSX.utils.sheet_to_json(ws, {header:1,raw:false});
        const hdrs = (jsonRows[0]||[]).map(h=>String(h||"").trim()).filter(h=>h);
        const dataRows = jsonRows.slice(1).map(row=>{
          const obj={};
          hdrs.forEach((h,i)=>{obj[h]=String(row[i]||"").trim();});
          return obj;
        }).filter(r=>Object.values(r).some(v=>v));
        setHeaders(hdrs); setRows(dataRows);
        // Auto-map columns
        autoMap(hdrs);
        setStep(2);
        return;
      }
      // CSV
      const lines = text.split(/\r?\n/).filter(l=>l.trim());
      const delim = [",","\t",";"].find(d=>lines[0].split(d).length>1)||",";
      const hdrs = lines[0].split(delim).map(h=>h.replace(/^"|"$/g,"").trim());
      const dataRows = lines.slice(1).map(line=>{
        const vals = line.split(delim).map(v=>v.replace(/^"|"$/g,"").trim());
        const obj={};
        hdrs.forEach((h,i)=>{obj[h]=vals[i]||"";});
        return obj;
      }).filter(r=>Object.values(r).some(v=>v));
      setHeaders(hdrs); setRows(dataRows);
      autoMap(hdrs);
      setStep(2);
    };
    if (file.name.endsWith(".xlsx")||file.name.endsWith(".xls")) {
      reader.readAsArrayBuffer(file);
    } else {
      reader.readAsText(file);
    }
  };

  const autoMap = (hdrs) => {
    const find = (...keywords) => hdrs.find(h => keywords.some(k=>h.toLowerCase().includes(k)))||"";
    setMapping({
      name:         find("name","customer"),
      phone:        find("phone","mobile","contact","cell"),
      email:        find("email","mail"),
      address_line1:find("address","addr"),
      gstin:        find("gst","gstin","tax"),
      type:         find("type","category"),
    });
  };

  const buildPreview = () => {
    if (!mapping.name) return alert("Please map at least the Name column.");
    if (!mapping.phone) return alert("Please map the Phone column — it is required for duplicate detection.");
    const p = rows.map(row=>({
      name:         (row[mapping.name]||"").trim(),
     phone: mapping.phone ? String(row[mapping.phone]||"").replace(/\.0+$/,"").trim() : "",
      email:        mapping.email        ? (row[mapping.email]||"").trim()        : "",
      address_line1:mapping.address_line1? (row[mapping.address_line1]||"").trim(): "",
      gstin:        mapping.gstin        ? (row[mapping.gstin]||"").trim()        : "",
      type:         mapping.type         ? (row[mapping.type]||"").trim().toLowerCase() : "counter",
     valid: !!(row[mapping.name]||"").trim(), // phone optional
    })).filter(r=>r.name);
    // Normalise type
    p.forEach(r=>{
      if (!["counter","outstation","tpf","prv"].includes(r.type)) r.type="counter";
    });
    setPreview(p);
    setStep(3);
  };

  const importCustomers = async () => {
  setSaving(true);
  let created=0, skipped=0, errors=[];

  const validRows = preview.filter(r => r.valid);

  // Process in batches of 50 — one by one to handle duplicate detection properly
  // but batch the non-duplicate-sensitive ones for speed
  const BATCH = 50;
  for (let i=0; i<validRows.length; i+=BATCH) {
    const batch = validRows.slice(i, i+BATCH);
    for (const cust of batch) {
      try {
// Check if customer with same name already exists
const { data: existing } = await supabase.from("customers")
  .select("id")
  .ilike("name", cust.name.trim())
  .limit(1);

if (existing && existing.length > 0) {
  skipped++;
  continue;
}

// Insert — ignore phone uniqueness for bulk import
// Strip phone if it would cause a duplicate
let phone = cust.phone||null;
if (phone) {
  const { data: phoneExists } = await supabase.from("customers")
    .select("id").eq("phone", phone).limit(1);
  if (phoneExists && phoneExists.length > 0) {
    phone = null; // clear duplicate phone, still create the customer
  }
}

const {error} = await supabase.from("customers").insert({
  name:          cust.name,
  phone:         phone,
  email:         cust.email||null,
  address_line1: cust.address_line1||null,
  gstin:         cust.gstin||null,
  type:          cust.type||"counter",
  is_active:     true,
});
if (error) {
  errors.push(`${cust.name}: ${error.message}`);
} else { created++; }
      } catch(e) { errors.push(`${cust.name}: ${e.message}`); }
    }
    // Update progress in result so user sees it moving
    setResult({created, skipped, errors, inProgress:true, total:validRows.length, done:Math.min(i+BATCH,validRows.length)});
  }

  setSaving(false);
  setResult({created, skipped, errors, inProgress:false, total:validRows.length, done:validRows.length});
  setStep(4);
};

  const sel = (k,v) => setMapping(m=>({...m,[k]:v}));

  const FIELD_LABELS = {
    name:"Customer name *", phone:"Phone * (unique)",
    email:"Email", address_line1:"Address line 1", gstin:"GST number", type:"Customer type"
  };

  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal modal-xl">
        <div className="modal-header">
          <div className="modal-title">Import customers</div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">

          {/* Step indicator */}
          <div style={{display:"flex",gap:8,marginBottom:20}}>
            {["Upload file","Map columns","Preview","Done"].map((s,i)=>(
              <div key={i} style={{display:"flex",alignItems:"center",gap:6,fontSize:12}}>
                <span style={{width:20,height:20,borderRadius:"50%",display:"inline-flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:500,
                  background:step>i+1?"var(--accent)":step===i+1?"var(--info)":"var(--paper3)",
                  color:step>=i+1?"#fff":"var(--ink3)"}}>
                  {step>i+1?"✓":i+1}
                </span>
                <span style={{color:step===i+1?"var(--ink)":"var(--ink3)",fontWeight:step===i+1?500:400}}>{s}</span>
                {i<3&&<span style={{color:"var(--paper3)"}}>›</span>}
              </div>
            ))}
          </div>

          {/* Step 1 — Upload */}
          {step===1&&(
            <div>
              <div style={{marginBottom:16,padding:"12px 16px",background:"var(--info-l)",borderRadius:8,fontSize:13,color:"var(--info)"}}>
                Prepare your file with these columns: <strong>Name, Phone, Email, Address, GST</strong>. Phone must be unique per customer. Supports CSV and Excel (.xlsx).
              </div>
              <div style={{border:"2px dashed var(--paper3)",borderRadius:10,padding:48,textAlign:"center",cursor:"pointer",background:"var(--paper)"}}
                onClick={()=>fileRef.current?.click()}>
                <div style={{fontSize:37,marginBottom:8}}>📂</div>
                <div style={{fontSize:14,fontWeight:500,color:"var(--ink2)"}}>Click to select CSV or Excel file</div>
                <div style={{fontSize:12,color:"var(--ink3)",marginTop:4}}>Drag and drop also works</div>
                <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls,.txt" style={{display:"none"}}
                  onChange={e=>parseFile(e.target.files[0])}/>
              </div>
              <div style={{marginTop:16,padding:"12px 16px",background:"var(--paper)",borderRadius:8,fontSize:12,color:"var(--ink3)"}}>
                <strong>Sample format:</strong><br/>
                Name, Phone, Email, Address, GST, Type<br/>
                Navrang Lab, 9876543210, lab@email.com, MG Road Mangalore, 29XXXXX, outstation
              </div>
            </div>
          )}

          {/* Step 2 — Map columns */}
          {step===2&&(
            <div>
              <div style={{marginBottom:12,padding:"10px 14px",background:"var(--paper)",borderRadius:8,fontSize:12,color:"var(--ink3)"}}>
                Detected {rows.length} rows · Columns: {headers.join(" · ")}
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
                {Object.keys(FIELD_LABELS).map(k=>(
                  <div key={k}>
                    <label style={{fontSize:11,letterSpacing:"0.6px",textTransform:"uppercase",color:"var(--ink3)",fontWeight:500,display:"block",marginBottom:5}}>
                      {FIELD_LABELS[k]}
                    </label>
                    <select value={mapping[k]||""} onChange={e=>sel(k,e.target.value)}
                      style={{padding:"8px 12px",border:"1px solid var(--paper3)",borderRadius:8,fontFamily:"var(--mono)",fontSize:13,color:"var(--ink)",background:"var(--paper)",outline:"none",width:"100%"}}>
                      <option value="">— Not mapped —</option>
                      {headers.map(h=><option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>
                ))}
              </div>
              <div style={{display:"flex",gap:10,justifyContent:"space-between",marginTop:20,paddingTop:16,borderTop:"1px solid var(--paper2)"}}>
                <button className="btn btn-ghost" onClick={()=>setStep(1)}>← Back</button>
                <button className="btn btn-primary" onClick={buildPreview}>Preview {rows.length} customers →</button>
              </div>
            </div>
          )}

          {/* Step 3 — Preview */}
          {step===3&&(
            <div>
              <div style={{marginBottom:12,fontSize:13,color:"var(--ink2)"}}>
                <strong style={{color:"var(--accent)"}}>{preview.filter(r=>r.valid).length} valid</strong> customers ready to import
                {preview.filter(r=>!r.valid).length>0&&<span style={{color:"var(--danger)",marginLeft:8}}> · {preview.filter(r=>!r.valid).length} will be skipped (missing name or phone)</span>}
              </div>
              <div style={{maxHeight:400,overflowY:"auto",border:"1px solid var(--paper3)",borderRadius:8,marginBottom:16}}>
                <table style={{width:"100%",borderCollapse:"collapse"}}>
                  <thead style={{position:"sticky",top:0}}>
                    <tr style={{background:"var(--paper2)",borderBottom:"1px solid var(--paper3)"}}>
                      <th style={{padding:"8px 14px",textAlign:"left",fontSize:11,letterSpacing:"0.8px",textTransform:"uppercase",color:"var(--ink3)",fontWeight:500}}>Name</th>
                      <th style={{padding:"8px 14px",textAlign:"left",fontSize:11,letterSpacing:"0.8px",textTransform:"uppercase",color:"var(--ink3)",fontWeight:500}}>Phone</th>
                      <th style={{padding:"8px 14px",textAlign:"left",fontSize:11,letterSpacing:"0.8px",textTransform:"uppercase",color:"var(--ink3)",fontWeight:500}}>Email</th>
                      <th style={{padding:"8px 14px",textAlign:"left",fontSize:11,letterSpacing:"0.8px",textTransform:"uppercase",color:"var(--ink3)",fontWeight:500}}>Address</th>
                      <th style={{padding:"8px 14px",textAlign:"left",fontSize:11,letterSpacing:"0.8px",textTransform:"uppercase",color:"var(--ink3)",fontWeight:500}}>GST</th>
                      <th style={{padding:"8px 14px",textAlign:"left",fontSize:11,letterSpacing:"0.8px",textTransform:"uppercase",color:"var(--ink3)",fontWeight:500}}>Type</th>
                      <th style={{padding:"8px 14px",textAlign:"left",fontSize:11,letterSpacing:"0.8px",textTransform:"uppercase",color:"var(--ink3)",fontWeight:500}}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.map((r,i)=>(
                      <tr key={i} style={{borderBottom:"1px solid var(--paper2)",background:r.valid?"#fff":"var(--danger-l)"}}>
                        <td style={{padding:"8px 14px",fontSize:13,fontWeight:500}}>{r.name}</td>
                        <td style={{padding:"8px 14px",fontSize:12,fontFamily:"var(--mono)"}}>{r.phone||"—"}</td>
                        <td style={{padding:"8px 14px",fontSize:12}}>{r.email||"—"}</td>
                        <td style={{padding:"8px 14px",fontSize:12,maxWidth:160,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{r.address_line1||"—"}</td>
                        <td style={{padding:"8px 14px",fontSize:12,fontFamily:"var(--mono)"}}>{r.gstin||"—"}</td>
                        <td style={{padding:"8px 14px"}}>
                          <span style={{display:"inline-block",padding:"2px 8px",borderRadius:12,fontSize:11,fontWeight:500,
                            background:r.type==="outstation"?"var(--warn-l)":r.type==="tpf"?"var(--info-l)":r.type==="prv"?"#f3e8ff":"var(--accent-l)",
                            color:r.type==="outstation"?"var(--warn)":r.type==="tpf"?"var(--info)":r.type==="prv"?"#6a1b9a":"var(--accent)"}}>
                            {r.type}
                          </span>
                        </td>
                        <td style={{padding:"8px 14px"}}>
                          {!r.valid&&<span style={{fontSize:11,color:"var(--danger)"}}>⚠ Skip</span>}
                          {r.valid&&<span style={{fontSize:11,color:"var(--accent)"}}>✓</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{display:"flex",gap:10,justifyContent:"space-between"}}>
                <button className="btn btn-ghost" onClick={()=>setStep(2)}>← Back</button>
                <button className="btn btn-primary" onClick={importCustomers} disabled={saving||preview.filter(r=>r.valid).length===0}>
                  {saving?<span className="spinner"/>:"👤"} {saving?"Importing…":`Import ${preview.filter(r=>r.valid).length} customers`}
                </button>
              </div>
            </div>
          )}

          {/* Step 4 — Result */}
          {step===4&&result&&(
  <div style={{textAlign:"center",padding:"20px 0"}}>
    {result.inProgress
      ? <div style={{fontSize:49,marginBottom:12}}>⏳</div>
      : <div style={{fontSize:49,marginBottom:12}}>{result.errors.length===0?"✅":"⚠️"}</div>
    }
    <div style={{fontFamily:"var(--serif)",fontSize:23,fontWeight:300,marginBottom:8}}>
      {result.inProgress
        ? `Importing… ${result.done} of ${result.total}`
        : `${result.created} customer${result.created!==1?"s":""} imported`}
    </div>
    {result.inProgress&&(
      <div style={{background:"var(--paper2)",borderRadius:8,height:8,maxWidth:400,margin:"0 auto 16px",overflow:"hidden"}}>
        <div style={{background:"var(--accent)",height:"100%",width:`${Math.round(result.done/result.total*100)}%`,transition:"width 0.3s"}}/>
      </div>
    )}
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12,marginBottom:20,maxWidth:400,margin:"0 auto 20px"}}>
                <div style={{background:"var(--accent-l)",borderRadius:8,padding:"12px",textAlign:"center"}}>
                  <div style={{fontSize:25,fontWeight:500,color:"var(--accent)"}}>{result.created}</div>
                  <div style={{fontSize:11,color:"var(--ink3)",textTransform:"uppercase",marginTop:4}}>Created</div>
                </div>
                <div style={{background:"var(--warn-l)",borderRadius:8,padding:"12px",textAlign:"center"}}>
                  <div style={{fontSize:25,fontWeight:500,color:"var(--warn)"}}>{result.skipped}</div>
                  <div style={{fontSize:11,color:"var(--ink3)",textTransform:"uppercase",marginTop:4}}>Skipped (duplicate)</div>
                </div>
                <div style={{background:result.errors.length>0?"var(--danger-l)":"var(--paper2)",borderRadius:8,padding:"12px",textAlign:"center"}}>
                  <div style={{fontSize:25,fontWeight:500,color:result.errors.length>0?"var(--danger)":"var(--ink3)"}}>{result.errors.length}</div>
                  <div style={{fontSize:11,color:"var(--ink3)",textTransform:"uppercase",marginTop:4}}>Errors</div>
                </div>
              </div>
              {result.errors.length>0&&(
                <div style={{background:"var(--danger-l)",borderRadius:8,padding:"12px 16px",textAlign:"left",marginBottom:16,maxHeight:150,overflowY:"auto"}}>
                  {result.errors.map((e,i)=><div key={i} style={{fontSize:12,color:"var(--danger)",marginBottom:4}}>{e}</div>)}
                </div>
              )}
              <div style={{display:"flex",gap:10,justifyContent:"center"}}>
                {result.created>0&&<button className="btn btn-primary" onClick={onSave}>✓ Done</button>}
                <button className="btn btn-ghost" onClick={onClose}>Close</button>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
// ── CUSTOMERS PAGE ────────────────────────────────────────────
function CustomersPage({ toast }) {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [modal, setModal] = useState(null);
const [importModal, setImportModal] = useState(false);
const [, setImportResult] = useState(null);
const load = useCallback(async () => {
  setLoading(true);
  const data = await fetchAll("customers", {select:"*", eq:{is_active:true}, order:"name"});
  setCustomers(data); setLoading(false);
}, []);
  useEffect(()=>{load();},[load]);
  const deactivate = async(id)=>{
    if(!window.confirm("Deactivate this customer?"))return;
    await supabase.from("customers").update({is_active:false}).eq("id",id);
    toast("Customer deactivated.");load();
  };
  const typeBadge=(t)=>t==="outstation"?"badge-amber":t==="tpf"?"badge-blue":t==="prv"?"badge-purple":"badge-green";
  const filtered=customers.filter(c=>{const s=search.toLowerCase();return(c.name.toLowerCase().includes(s)||(c.phone||"").includes(s))&&(filter==="all"||c.type===filter);});
  return(
    <div>
      <div className="page-header"><div><div className="page-title">Customers</div><div className="page-subtitle">Manage your customer master list</div></div><div style={{display:"flex",gap:8}}>
  <button className="btn btn-ghost" onClick={()=>setImportModal(true)}>⬆ Import CSV</button>
  <button className="btn btn-primary" onClick={()=>setModal("new")}>+ New customer</button>
</div></div>
      <div className="stats-row">
        <div className="stat-card"><div className="stat-value">{customers.length}</div><div className="stat-label">Total</div></div>
        <div className="stat-card" style={{borderLeftColor:"var(--warn)"}}><div className="stat-value">{customers.filter(c=>c.type==="outstation").length}</div><div className="stat-label">Outstation</div></div>
        <div className="stat-card"><div className="stat-value">{customers.filter(c=>c.type==="counter").length}</div><div className="stat-label">Counter</div></div>
        <div className="stat-card" style={{borderLeftColor:"#0d5bb8"}}><div className="stat-value">{customers.filter(c=>c.type==="tpf"||c.type==="prv").length}</div><div className="stat-label">TPF / PRV</div></div>
      </div>
      <div className="toolbar">
        <div className="search-wrap"><span className="search-icon">🔍</span><input placeholder="Search…" value={search} onChange={e=>setSearch(e.target.value)}/></div>
        {["all","counter","outstation","tpf","prv"].map(f=>(
          <button key={f} className={`filter-pill ${filter===f?"active":""}`} onClick={()=>setFilter(f)}>{f==="all"?"All":f==="tpf"?"TPF":f==="prv"?"PRV":f.charAt(0).toUpperCase()+f.slice(1)}</button>
        ))}
      </div>
      <div className="card">
        <table>
          <thead><tr><th>Name</th><th>Type</th><th>Phone</th><th>Email</th><th>GSTIN</th><th></th></tr></thead>
          <tbody>
            {loading&&<tr className="loading-row"><td colSpan={6}><span className="spinner"/></td></tr>}
            {!loading&&filtered.length===0&&<tr><td colSpan={6}><div className="empty-state"><div className="icon">👤</div><p>No customers found.</p></div></td></tr>}
            {!loading&&filtered.map(c=>(
              <tr key={c.id}>
                <td style={{fontWeight:500}}>{c.name}</td>
                <td><span className={`badge ${typeBadge(c.type)}`}>{c.type}</span></td>
                <td className="mono-text">{c.phone||"—"}</td>
                <td className="mono-text">{c.email||"—"}</td>
                <td className="mono-text" style={{fontSize:12}}>{c.gstin||"—"}</td>
                <td><div style={{display:"flex",gap:6,justifyContent:"flex-end"}}>
                  <button className="btn btn-ghost btn-sm" onClick={()=>setModal(c)}>Edit</button>
                  <button className="btn btn-danger btn-sm" onClick={()=>deactivate(c.id)}>Remove</button>
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
{importModal && (
  <CustomerImportModal
    onClose={()=>{ setImportModal(false); setImportResult(null); }}
    onSave={()=>{ setImportModal(false); setImportResult(null); load(); toast("Customers imported!"); }}
    toast={toast}
  />
)}
      {modal&&<CustomerModal customer={modal==="new"?null:modal} onClose={()=>setModal(null)} onSave={()=>{setModal(null);load();toast(modal==="new"?"Customer added!":"Customer updated!");}}/>}
    </div>
  );
}

// ── PRODUCT MODAL ─────────────────────────────────────────────
function ProductModal({ product, onClose, onSave }) {
  const [form, setForm] = useState(product||{name:"",description:"",unit:"nos",base_price:"",base_price_small:"",hsn_code:"",tax_rate:"18"});
  const [saving, setSaving] = useState(false);
  const set=(k,v)=>setForm(f=>({...f,[k]:v}));
  const save=async()=>{
    if(!form.name.trim())return alert("Product name is required.");
    setSaving(true);
    const payload={...form,base_price:parseFloat(form.base_price)||0,base_price_small:form.base_price_small===""||form.base_price_small==null?null:parseFloat(form.base_price_small),tax_rate:parseFloat(form.tax_rate)||0,updated_at:new Date().toISOString()};
    try{
      if(product?.id){const{error}=await supabase.from("products").update(payload).eq("id",product.id);if(error)throw error;}
      else{const{error}=await supabase.from("products").insert(payload);if(error)throw error;}
      onSave();
    }catch(e){alert("Error: "+e.message);}finally{setSaving(false);}
  };
  return(
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal">
        <div className="modal-header"><div className="modal-title">{product?"Edit product":"New product"}</div><button className="modal-close" onClick={onClose}>✕</button></div>
        <div className="modal-body">
          <div className="form-grid">
            <div className="form-group span2"><label>Product / service name *</label><input value={form.name} onChange={e=>set("name",e.target.value)} autoFocus/></div>
            <div className="form-group span2"><label>Description</label><textarea value={form.description||""} onChange={e=>set("description",e.target.value)}/></div>
            <div className="form-group"><label>Base price (₹) *</label><input type="number" min="0" step="0.01" value={form.base_price} onChange={e=>set("base_price",e.target.value)} placeholder="0.00"/></div>
            <div className="form-group"><label>Small size price (₹)</label><input type="number" min="0" step="0.01" value={form.base_price_small??""} onChange={e=>set("base_price_small",e.target.value)} placeholder="Same as base price if blank"/><div style={{fontSize:11,color:"var(--ink3)",marginTop:4}}>Used when the work order size is 8x24, 9x24, or 12x18. Leave blank if this product's price doesn't vary by size.</div></div>
            <div className="form-group"><label>Unit</label><select value={form.unit} onChange={e=>set("unit",e.target.value)}>{["nos","kg","ltr","mtr","hrs","set","box","pcs","pair"].map(u=><option key={u} value={u}>{u}</option>)}</select></div>
            <div className="form-group"><label>HSN / SAC code</label><input value={form.hsn_code||""} onChange={e=>set("hsn_code",e.target.value)}/></div>
            <div className="form-group"><label>GST rate (%)</label><select value={form.tax_rate} onChange={e=>set("tax_rate",e.target.value)}>{["0","5","12","18","28"].map(r=><option key={r} value={r}>{r}%</option>)}</select></div>
          </div>
          <div className="form-actions">
            <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={save} disabled={saving}>{saving?<span className="spinner"/>:"💾"} {saving?"Saving…":"Save product"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── PRODUCT VARIANTS MODAL ────────────────────────────────────
function ProductVariantsModal({ product, onClose, toast }) {
  const [variants, setVariants]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [prices, setPrices]       = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.from("product_variants")
      .select("*").eq("product_id", product.id).eq("is_active", true);

    // Build all 56 combinations
    const allCombos = [];
    for (const pt of PAPER_TYPES)
      for (const cc of COLOR_COUNTS)
        for (const m of MICRONS)
          for (const sg of SIZE_GROUPS)
            allCombos.push({ paper_type:pt, color_count:cc, micron:m, size_group:sg });

    // Match existing variants to combos
    const existingMap = {};
    (data||[]).forEach(v => {
      const key = `${v.paper_type}|${v.color_count}|${v.micron}|${v.size_group}`;
      existingMap[key] = v;
    });

    const merged = allCombos.map(c => {
      const key = `${c.paper_type}|${c.color_count}|${c.micron}|${c.size_group}`;
      return existingMap[key] || { ...c, product_id:product.id, base_price:0, id:null };
    });

    setVariants(merged);
    const p = {};
    merged.forEach((v,i) => { p[i] = v.base_price||0; });
    setPrices(p);
    setLoading(false);
  }, [product.id]);

  useEffect(() => { load(); }, [load]);

  const saveAll = async () => {
    setSaving(true);
    try {
      for (let i=0; i<variants.length; i++) {
        const v = variants[i];
        const price = parseFloat(prices[i])||0;
        if (v.id) {
          await supabase.from("product_variants").update({
            base_price:price, updated_at:new Date().toISOString()
          }).eq("id", v.id);
        } else {
          await supabase.from("product_variants").insert({
            product_id:  product.id,
            paper_type:  v.paper_type,
            color_count: v.color_count,
            micron:      v.micron,
            size_group:  v.size_group,
            base_price:  price,
          });
        }
      }
      toast("All variant prices saved!","success");
      load();
    } catch(e) { alert("Error: "+e.message); }
    finally { setSaving(false); }
  };

  // Group by paper type for display
  const grouped = {};
  PAPER_TYPES.forEach(pt => { grouped[pt] = variants.filter(v=>v.paper_type===pt); });

  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal modal-lg" style={{maxWidth:860}}>
        <div className="modal-header">
          <div className="modal-title">Paper variants — {product.name}</div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div style={{marginBottom:16,padding:"10px 14px",background:"var(--info-l)",borderRadius:8,fontSize:12,color:"var(--info)"}}>
            Set base prices for all 56 combinations. Per-customer prices can be overridden in the work order form. Size group: <strong>Small</strong> = 8x24, 9x24, 12x18 · <strong>Standard</strong> = 12x24, 12x30, 12x36, 15x24, 16x24, 18x24
          </div>

          {loading && <div style={{textAlign:"center",padding:30,color:"var(--ink3)"}}>Loading…</div>}

          {!loading && PAPER_TYPES.map(pt => (
            <div key={pt} style={{marginBottom:20}}>
              <div style={{fontWeight:500,fontSize:14,marginBottom:8,padding:"6px 10px",background:"var(--paper2)",borderRadius:6}}>{pt}</div>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
                <thead>
                  <tr style={{background:"var(--paper)",borderBottom:"1px solid var(--paper3)"}}>
                    <th style={{padding:"6px 10px",textAlign:"left",fontSize:11,color:"var(--ink3)",textTransform:"uppercase",letterSpacing:"0.6px"}}>Color</th>
                    <th style={{padding:"6px 10px",textAlign:"left",fontSize:11,color:"var(--ink3)",textTransform:"uppercase",letterSpacing:"0.6px"}}>Micron</th>
                    <th style={{padding:"6px 10px",textAlign:"left",fontSize:11,color:"var(--ink3)",textTransform:"uppercase",letterSpacing:"0.6px"}}>Size group</th>
                    <th style={{padding:"6px 10px",textAlign:"right",fontSize:11,color:"var(--ink3)",textTransform:"uppercase",letterSpacing:"0.6px"}}>Base price (₹/sheet)</th>
                  </tr>
                </thead>
                <tbody>
                  {grouped[pt]?.map((v,_) => {
                    const idx = variants.indexOf(v);
                    return (
                      <tr key={idx} style={{borderBottom:"1px solid var(--paper2)"}}>
                        <td style={{padding:"6px 10px"}}>
                          <span style={{display:"inline-block",padding:"2px 8px",borderRadius:10,fontSize:11,fontWeight:500,
                            background:v.color_count==="7 Color"?"#f3e8ff":"var(--info-l)",
                            color:v.color_count==="7 Color"?"#6a1b9a":"var(--info)"}}>
                            {v.color_count}
                          </span>
                        </td>
                        <td style={{padding:"6px 10px",fontSize:12,color:"var(--ink2)"}}>{v.micron}</td>
                        <td style={{padding:"6px 10px"}}>
                          <span style={{display:"inline-block",padding:"2px 8px",borderRadius:10,fontSize:11,fontWeight:500,
                            background:v.size_group==="small"?"var(--warn-l)":"var(--accent-l)",
                            color:v.size_group==="small"?"var(--warn)":"var(--accent)"}}>
                            {v.size_group==="small"?"Small (8x24, 9x24, 12x18)":"Standard (12x24–18x24)"}
                          </span>
                        </td>
                        <td style={{padding:"6px 10px",textAlign:"right"}}>
                          <input type="number" min="0" step="0.01"
                            value={prices[idx]||""}
                            onChange={e=>setPrices(p=>({...p,[idx]:e.target.value}))}
                            placeholder="0.00"
                            style={{padding:"5px 8px",border:"1px solid var(--paper3)",borderRadius:6,fontFamily:"var(--mono)",fontSize:13,width:100,textAlign:"right",outline:"none",background:"var(--paper)"}}/>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}

          <div className="form-actions">
            <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={saveAll} disabled={saving}>
              {saving?<span className="spinner"/>:"💾"} {saving?"Saving…":"Save all prices"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── PRODUCTS PAGE ─────────────────────────────────────────────
function ProductsPage({ toast }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(null);
const [variantModal, setVariantModal] = useState(null);
  const load=useCallback(async()=>{
    setLoading(true);
    const{data}=await supabase.from("products").select("*").eq("is_active",true).order("name");
    setProducts(data||[]);setLoading(false);
  },[]);
  useEffect(()=>{load();},[load]);
  const deactivate=async(id)=>{
    if(!window.confirm("Deactivate this product?"))return;
    await supabase.from("products").update({is_active:false}).eq("id",id);
    toast("Product deactivated.");load();
  };
  const filtered=products.filter(p=>p.name.toLowerCase().includes(search.toLowerCase())||(p.hsn_code||"").includes(search));
  return(
    <div>
      <div className="page-header"><div><div className="page-title">Products & Services</div><div className="page-subtitle">Tip: name mix-finish paper products like "Paper-Glossy A4"</div></div><button className="btn btn-primary" onClick={()=>setModal("new")}>+ New product</button></div>
      <div className="toolbar"><div className="search-wrap"><span className="search-icon">🔍</span><input placeholder="Search by name or HSN…" value={search} onChange={e=>setSearch(e.target.value)}/></div></div>
      <div className="card">
        <table>
          <thead><tr><th>Name</th><th>Unit</th><th>Base price</th><th>Small size price</th><th>HSN</th><th>GST</th><th></th></tr></thead>
          <tbody>
            {loading&&<tr className="loading-row"><td colSpan={7}><span className="spinner"/></td></tr>}
            {!loading&&filtered.length===0&&<tr><td colSpan={7}><div className="empty-state"><div className="icon">📦</div><p>No products yet.</p></div></td></tr>}
            {!loading&&filtered.map(p=>(
              <tr key={p.id}>
                <td><div style={{fontWeight:500}}>{p.name}</div>{p.description&&<div style={{fontSize:12,color:"var(--ink3)",marginTop:2}}>{p.description.slice(0,60)}{p.description.length>60?"…":""}</div>}</td>
                <td><span className="badge badge-gray">{p.unit}</span></td>
                <td className="mono-text" style={{fontWeight:500}}>{fmt(p.base_price)}</td>
                <td className="mono-text" style={{color:p.base_price_small!=null?"var(--ink)":"var(--ink3)"}}>{p.base_price_small!=null?fmt(p.base_price_small):"—"}</td>
                <td className="mono-text">{p.hsn_code||"—"}</td>
                <td><span className={`badge ${parseFloat(p.tax_rate)>0?"badge-amber":"badge-gray"}`}>{p.tax_rate}%</span></td>
                <td><div style={{display:"flex",gap:6,justifyContent:"flex-end"}}>
                 <button className="btn btn-ghost btn-sm" onClick={()=>setModal(p)}>Edit</button>
{p.name==="Paper"&&<button className="btn btn-info btn-sm" onClick={()=>setVariantModal(p)}>⚙ Variants</button>}
<button className="btn btn-danger btn-sm" onClick={()=>deactivate(p.id)}>Remove</button>
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {modal&&<ProductModal product={modal==="new"?null:modal} onClose={()=>setModal(null)} onSave={()=>{setModal(null);load();toast(modal==="new"?"Product added!":"Product updated!");}}/>}
{variantModal&&<ProductVariantsModal product={variantModal} onClose={()=>setVariantModal(null)} toast={toast}/>}
    </div>
  );
}

// ── WORK ORDERS PAGE ──────────────────────────────────────────
function WorkOrdersPage({ toast, sequences, estSequences, reloadSeqs }) {
  const [orders, setOrders]         = useState([]);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState("");
  const [filter, setFilter]         = useState("all");
  const [modal, setModal]           = useState(null);
  const [fromDate, setFromDate]     = useState("");
  const [toDate, setToDate]         = useState("");
  const [customerF, setCustomerF]   = useState("");
  const [showDailyTotal, setShowDailyTotal] = useState(false);
const [customers, setCustomers]   = useState([]);

const load = useCallback(async () => {
  setLoading(true);
  let all = [];
  let from = 0;
  while (true) {
    const {data, error} = await supabase.from("work_orders")
      .select("*, customers(id,name,type)")
      .eq("is_deleted", false)
      .order("created_at", {ascending:false})
      .range(from, from+999);
    if (error||!data||data.length===0) break;
    all = [...all,...data];
    if (data.length<1000) break;
    from += 1000;
  }
  setOrders(all); setLoading(false);
}, []);
useEffect(()=>{
  load();
  fetchAll("customers",{select:"id,name,type",eq:{is_active:true},order:"name"})
    .then(data=>setCustomers(data));
},[load]);

  const updateStatus=async(id,status)=>{
    await supabase.from("work_orders").update({status,updated_at:new Date().toISOString()}).eq("id",id);
    toast(`Status updated to ${status}.`);load();
  };

  const filtered=orders.filter(o=>{
    const s=search.toLowerCase();
    return((o.wo_number||"").toLowerCase().includes(s)||(o.customers?.name||"").toLowerCase().includes(s))&&
      (filter==="all"||o.status===filter||o.series_code===filter||(filter==="not_invoiced"&&o.status!=="invoiced"))&&
      (!customerF||(o.customers?.name||"").toLowerCase().includes(customerF.toLowerCase()))&&
      (!fromDate||o.order_date>=fromDate)&&(!toDate||o.order_date<=toDate);
  });

  const statusColors={open:"badge-blue",in_progress:"badge-amber",completed:"badge-green",invoiced:"badge-purple",cancelled:"badge-red"};
  const seriesColors={OUT:"#9a4a00",CTR:"#1f6e4a",TPF:"#0d5bb8",PRV:"#6a1b9a"};
  const counts={open:orders.filter(o=>o.status==="open").length,in_progress:orders.filter(o=>o.status==="in_progress").length,completed:orders.filter(o=>o.status==="completed").length};

const downloadCSV = async (data) => {
  if (data.length===0) return alert("No orders to download.");
  const woIds = data.map(o=>o.id);
const {data:lineItems} = await supabase.from("wo_line_items")
  .select("*, products(name,unit), product_variants(paper_type,color_count,micron,size_group)")
  .in("work_order_id",woIds)
  .order("work_order_id").order("sort_order");
  const linesByWo={};
  (lineItems||[]).forEach(l=>{if(!linesByWo[l.work_order_id])linesByWo[l.work_order_id]=[];linesByWo[l.work_order_id].push(l);});

  const headers=["Date","Order Number","Customer","Product","Qty","Rate (₹)","Amount (₹)","Notes"];
  const COLS=headers.length;
  const emptyRow=()=>Array(COLS).fill("");
  const rows=[];

  const ordersByDate={};
  data.forEach(o=>{const d=o.order_date||"Unknown";if(!ordersByDate[d])ordersByDate[d]=[];ordersByDate[d].push(o);});

  Object.keys(ordersByDate).sort().forEach(date=>{
    const dayOrders=ordersByDate[date]; let dayTotal=0;
    dayOrders.forEach(o=>{
      const lines=linesByWo[o.id]||[];
      const custName=o.customers?.name||"";
      const notes=o.notes||"";
      let orderTotal=0;
      if(lines.length===0){
        rows.push([fmtDMY(o.order_date)||"",o.wo_number||"",custName,"","","","",notes]);
      } else {
        lines.forEach((l,i)=>{
          const qty=parseFloat(l.quantity)||0;
          const rate=parseFloat(l.unit_price)||0;
          const lt=qty*rate;
          orderTotal+=lt;
          const pv = l.product_variants;
const productName = pv
  ? `${pv.paper_type} · ${pv.color_count} · ${pv.micron}`
  : (l.products?.name||"");
// Date/Order Number/Customer/Notes only on the first line of a
// multi-line order — matches the blank-means-same-order convention
// used across this app's other imports/exports.
rows.push([i===0?(fmtDMY(o.order_date)||""):"",i===0?(o.wo_number||""):"",i===0?custName:"",productName,qty,rate.toFixed(2),lt.toFixed(2),i===0?notes:""]);
        });
      }
      dayTotal+=orderTotal;
      if(showDailyTotal){const otr=emptyRow();otr[2]=`ORDER TOTAL — ${o.wo_number}`;otr[6]=orderTotal.toFixed(2);rows.push(otr);}
      rows.push(emptyRow());
    });
    if(showDailyTotal){const dtr=emptyRow();dtr[2]=`DAILY TOTAL — ${date}`;dtr[6]=dayTotal.toFixed(2);rows.push(dtr);rows.push(emptyRow());}
  });

  const csv=[headers,...rows].map(r=>r.map(v=>`"${String(v).replace(/"/g,'""')}"`).join(",")).join("\n");
  const blob=new Blob(["\uFEFF"+csv],{type:"text/csv;charset=utf-8;"});
  const url=URL.createObjectURL(blob);
  const link=document.createElement("a");
  link.href=url;link.download=`work-orders-${new Date().toISOString().slice(0,10)}.csv`;link.click();
  URL.revokeObjectURL(url);
};

  return(
    <div>
      <div className="page-header"><div><div className="page-title">Work Orders</div><div className="page-subtitle">All series · {orders.length} total</div></div><button className="btn btn-primary" onClick={()=>setModal("new")}>+ New work order</button></div>
      <div className="stats-row">
        <div className="stat-card" style={{borderLeftColor:"#0d5bb8"}}><div className="stat-value">{counts.open}</div><div className="stat-label">Open</div></div>
        <div className="stat-card" style={{borderLeftColor:"var(--warn)"}}><div className="stat-value">{counts.in_progress}</div><div className="stat-label">In progress</div></div>
        <div className="stat-card"><div className="stat-value">{counts.completed}</div><div className="stat-label">Completed</div></div>
        <div className="stat-card" style={{borderLeftColor:"#6a1b9a"}}><div className="stat-value">{orders.length}</div><div className="stat-label">Total</div></div>
      </div>
      <div style={{background:"#fff",border:"1px solid var(--paper3)",borderRadius:10,padding:"16px 20px",marginBottom:20,boxShadow:"var(--shadow)"}}>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:12,marginBottom:12}}>
          <div className="form-group"><label>Search WO / customer</label><div className="search-wrap" style={{maxWidth:"100%"}}><span className="search-icon">🔍</span><input placeholder="WO number or name…" value={search} onChange={e=>setSearch(e.target.value)}/></div></div>
          <div className="form-group"><label>Customer name</label>
  <AutoComplete
    value={customerF}
    onChange={v=>{ setCustomerF(v); if(!v) setCustomerF(""); }}
    onSelect={c=>setCustomerF(c.name)}
    items={customers}
    placeholder="Type customer name…"
    getKey={c=>c.id}
    getLabel={c=>c.name}
    renderItem={c=><div><div style={{fontWeight:500}}>{c.name}</div><div className="item-sub">{c.type}</div></div>}
  />
</div>
          <div className="form-group"><label>From date</label><input type="date" value={fromDate} onChange={e=>setFromDate(e.target.value)} style={{padding:"8px 12px",border:"1px solid var(--paper3)",borderRadius:8,fontFamily:"var(--mono)",fontSize:13,background:"var(--paper)",outline:"none"}}/></div>
          <div className="form-group"><label>To date</label><input type="date" value={toDate} onChange={e=>setToDate(e.target.value)} style={{padding:"8px 12px",border:"1px solid var(--paper3)",borderRadius:8,fontFamily:"var(--mono)",fontSize:13,background:"var(--paper)",outline:"none"}}/></div>
        </div>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:8}}>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            {["all","not_invoiced","open","in_progress","completed","invoiced"].map(f=>(
              <button key={f} className={`filter-pill ${filter===f?"active":""}`} onClick={()=>setFilter(f)}>{f==="all"?"All":f==="not_invoiced"?"Not invoiced":f==="in_progress"?"In progress":f.charAt(0).toUpperCase()+f.slice(1)}</button>
            ))}
            {SERIES.map(s=>(
              <button key={s.code} className={`filter-pill ${filter===s.code?"active":""}`} style={filter===s.code?{background:s.color,borderColor:s.color,color:"#fff"}:{}} onClick={()=>setFilter(s.code)}>{s.code}</button>
            ))}
          </div>
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            {(search||customerF||fromDate||toDate||filter!=="all")&&<button className="btn btn-ghost btn-sm" onClick={()=>{setSearch("");setCustomerF("");setFromDate("");setToDate("");setFilter("all");}}>✕ Clear</button>}
            <span style={{fontSize:12,color:"var(--ink3)"}}>{filtered.length} order{filtered.length!==1?"s":""}</span>
            <label style={{display:"flex",alignItems:"center",gap:6,fontSize:12,color:"var(--ink2)",cursor:"pointer",userSelect:"none"}}>
              <input type="checkbox" checked={showDailyTotal} onChange={e=>setShowDailyTotal(e.target.checked)} style={{width:13,height:13,cursor:"pointer",accentColor:"var(--accent)"}}/>
              Daily totals
            </label>
            <button className="btn btn-ghost btn-sm" onClick={()=>downloadCSV(filtered)}>⬇ CSV</button>
          </div>
        </div>
      </div>
      <div className="card">
        <table>
          <thead><tr><th>WO number</th><th>Customer</th><th>Series</th><th>Book type</th><th>Lamination</th><th>Size</th><th>Color</th><th>Micron</th><th>Sheets</th><th>Date</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody>
            {loading&&<tr className="loading-row"><td colSpan={9}><span className="spinner"/></td></tr>}
            {!loading&&filtered.length===0&&<tr><td colSpan={9}><div className="empty-state"><div className="icon">📋</div><p>No work orders yet.</p></div></td></tr>}
            {!loading&&filtered.map(o=>(
              <tr key={o.id}>
                <td><span className="wo-number">{o.wo_number}</span></td>
                <td><div style={{fontWeight:500}}>{o.customers?.name||"—"}</div><div style={{fontSize:11,color:"var(--ink3)"}}>{o.customers?.type}</div></td>
                <td><span className="badge" style={{background:seriesColors[o.series_code]+"20",color:seriesColors[o.series_code]}}>{o.series_code}</span></td>
                <td>{o.book_type?<span className="badge badge-blue">{o.book_type}</span>:<span style={{color:"var(--ink3)"}}>—</span>}</td>
                <td>{o.finish_type?<span className="badge badge-gray">{o.finish_type}</span>:<span style={{color:"var(--ink3)"}}>—</span>}</td>
<td className="mono-text" style={{fontSize:12}}>{o.size||"—"}</td>
<td style={{fontSize:12}}>{o.color_count?<span className="badge badge-blue">{o.color_count}</span>:"—"}</td>
<td style={{fontSize:12}}>{o.micron?<span className="badge badge-gray">{o.micron}</span>:"—"}</td>
<td className="mono-text" style={{fontSize:12}}>{o.total_sheets?o.total_sheets.toLocaleString("en-IN"):"—"}</td>
                <td className="mono-text" style={{fontSize:12}}>{fmtDMY(o.order_date)}</td>
                <td><span className={`badge ${statusColors[o.status]||"badge-gray"}`}>{o.status?.replace("_"," ")}</span></td>
                <td><div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                  <button className="btn btn-ghost btn-sm" onClick={()=>setModal(o)}>{o.status==="invoiced"?"View":"Edit"}</button>
                  {o.status==="open"&&<button className="btn btn-ghost btn-sm" onClick={()=>updateStatus(o.id,"in_progress")}>▶ Start</button>}
                  {o.status==="in_progress"&&<button className="btn btn-primary btn-sm" onClick={()=>updateStatus(o.id,"completed")}>✓ Complete</button>}
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {modal&&<OrderForm mode="wo" record={modal==="new"?null:modal} onClose={()=>setModal(null)} toast={toast} sequences={sequences} estSequences={estSequences} reloadSeqs={reloadSeqs} onSave={()=>{setModal(null);load();toast(modal==="new"?"Work order created!":"Work order updated!");}}/>}
    </div>
  );
}

// ── PARTYWISE REPORT ──────────────────────────────────────────
// One customer search that returns that party's work orders AND direct work
// in a single list, oldest first (by order date, then time created). Typing
// matches by name (substring, like the existing customer filters); picking a
// name from the dropdown narrows to exactly that customer.
function PartywisePage({ toast, sequences, estSequences, reloadSeqs }) {
  const [customers, setCustomers] = useState([]);
  const [text, setText]           = useState("");
  const [picked, setPicked]       = useState(null);
  const [fromDate, setFromDate]   = useState("");
  const [toDate, setToDate]       = useState("");
  const [filter, setFilter]       = useState("all");
  const [typeFilter, setTypeFilter] = useState(""); // customer type: counter / outstation / tpf / prv
  const [amounts, setAmounts]     = useState(null); // order id -> total, null while loading
  const [wos, setWos]             = useState([]);
  const [dws, setDws]             = useState([]);
  const [loading, setLoading]     = useState(false);
  const [woModal, setWoModal]     = useState(null);
  const [dwModal, setDwModal]     = useState(null);
  const [page, setPage]           = useState(1);
  const [showDailyTotal, setShowDailyTotal] = useState(false);
  const reqId = useRef(0);
  const PAGE_SIZE = 50;

  useEffect(()=>{
    fetchAll("customers",{select:"id,name,type",eq:{is_active:true},order:"name"}).then(setCustomers);
  },[]);

  // Nothing is fetched until a full date range is chosen. With a customer
  // any range is fine; with no customer ("all parties") the range is capped
  // at 31 days so a search can't pull in the whole history.
  const MAX_ALL_DAYS = 31;
  const datesReady = !!fromDate && !!toDate && fromDate<=toDate;
  const rangeDays = datesReady ? Math.round((new Date(toDate)-new Date(fromDate))/86400000)+1 : 0;
  const hasCustomer = !!picked || text.trim().length>=2;
  const allParties = !picked && text.trim()==="";
  const ready = datesReady && (hasCustomer || (allParties && rangeDays<=MAX_ALL_DAYS));

  const load = useCallback(async () => {
    const my = ++reqId.current;
    if (!ready) { setWos([]); setDws([]); setLoading(false); return; }
    setLoading(true);
    const fetchFor = async (table, custCols) => {
      let all = [], from = 0;
      while (true) {
        let q = supabase.from(table).select(`*, customers!inner(${custCols})`).eq("is_deleted", false);
        if (typeFilter) q = q.eq("customers.type", typeFilter);
        if (picked) q = q.eq("customer_id", picked.id);
        else if (text.trim()) q = q.ilike("customers.name", `%${text.trim()}%`);
        q = q.gte("order_date", fromDate).lte("order_date", toDate);
        const {data, error} = await q.order("created_at", {ascending:true}).range(from, from+999);
        if (error) throw error;
        if (!data || data.length===0) break;
        all = [...all, ...data];
        if (data.length<1000) break;
        from += 1000;
      }
      return all;
    };
    try {
      const [w, d] = await Promise.all([
        fetchFor("work_orders","id,name,type"),
        fetchFor("direct_work_orders","id,name,type,phone"),
      ]);
      if (my!==reqId.current) return; // a newer search superseded this one
      setWos(w); setDws(d);
      // Order totals (sum of line items) for the on-screen amounts and total.
      setAmounts(null);
      const sumLines = async (table, fk, ids) => {
        const m = {};
        const chunks = [];
        for (let i=0;i<ids.length;i+=100) chunks.push(ids.slice(i,i+100));
        await Promise.all(chunks.map(async (c) => {
          let from = 0;
          while (true) {
            const {data, error} = await supabase.from(table).select(`${fk},quantity,unit_price`).in(fk, c).range(from, from+999);
            if (error) throw error;
            (data||[]).forEach(l=>{ m[l[fk]] = (m[l[fk]]||0) + (parseFloat(l.quantity)||0)*(parseFloat(l.unit_price)||0); });
            if (!data || data.length<1000) break;
            from += 1000;
          }
        }));
        return m;
      };
      const [wm, dm] = await Promise.all([
        sumLines("wo_line_items","work_order_id",w.map(o=>o.id)),
        sumLines("dw_line_items","dw_order_id",d.map(o=>o.id)),
      ]);
      if (my!==reqId.current) return;
      setAmounts({...wm, ...dm});
    } catch(e) {
      if (my===reqId.current) toast("Search failed: "+e.message,"error");
    }
    if (my===reqId.current) setLoading(false);
  }, [ready, picked, text, fromDate, toDate, typeFilter, toast]);

  useEffect(()=>{
    const t = setTimeout(load, picked ? 0 : 350);
    return ()=>clearTimeout(t);
  },[load, picked]);

  useEffect(()=>{ setPage(1); },[wos, dws, filter]);

  const statusMatch = (o) => filter==="all" || o.status===filter || (filter==="not_invoiced" && o.status!=="invoiced");
  // Oldest first: order date, then the time the record was created.
  const rows = [
    ...wos.map(o=>({...o,_src:"wo"})),
    ...dws.map(o=>({...o,_src:"dw"})),
  ].filter(statusMatch).sort((a,b)=>
    (a.order_date||"").localeCompare(b.order_date||"") || (a.created_at||"").localeCompare(b.created_at||"")
  );
  const woCount = rows.filter(r=>r._src==="wo").length;
  const dwCount = rows.length - woCount;
  // Totals follow whatever is filtered (customer, type, dates, status).
  const amountOf = (r) => (amounts && amounts[r.id]) || 0;
  const woTotal = rows.filter(r=>r._src==="wo").reduce((t,r)=>t+amountOf(r),0);
  const dwTotal = rows.filter(r=>r._src==="dw").reduce((t,r)=>t+amountOf(r),0);
  const grandTotal = woTotal + dwTotal;

  const updateStatus = async (r, status) => {
    const table = r._src==="dw" ? "direct_work_orders" : "work_orders";
    await supabase.from(table).update({status, updated_at:new Date().toISOString()}).eq("id", r.id);
    toast(`Status updated to ${status.replace("_"," ")}.`);
    load();
  };

  const statusColors={open:"badge-blue",in_progress:"badge-amber",completed:"badge-green",invoiced:"badge-purple",cancelled:"badge-red"};
  const seriesColors={OUT:"#9a4a00",CTR:"#1f6e4a",TPF:"#0d5bb8",PRV:"#6a1b9a"};

  // Same layout as the Work Orders CSV (blank date/number/customer on an
  // order's extra lines; optional order and daily totals), but covering work
  // orders and direct work together, so a daily total spans both.
  const downloadCSV = async () => {
    if (rows.length===0) return alert("No orders to download.");
    const chunk = (arr,n)=>{const o=[];for(let i=0;i<arr.length;i+=n)o.push(arr.slice(i,i+n));return o;};
    const linesById = {};
    for (const ids of chunk(rows.filter(r=>r._src==="wo").map(r=>r.id),100)) {
      const {data} = await supabase.from("wo_line_items")
        .select("*, products(name,unit), product_variants(paper_type,color_count,micron,size_group)")
        .in("work_order_id",ids).order("sort_order");
      (data||[]).forEach(l=>{(linesById[l.work_order_id]=linesById[l.work_order_id]||[]).push(l);});
    }
    for (const ids of chunk(rows.filter(r=>r._src==="dw").map(r=>r.id),100)) {
      const {data} = await supabase.from("dw_line_items")
        .select("*, products(name,unit)")
        .in("dw_order_id",ids).order("sort_order");
      (data||[]).forEach(l=>{(linesById[l.dw_order_id]=linesById[l.dw_order_id]||[]).push(l);});
    }

    const headers=["Date","Order Number","Customer","Product","Qty","Rate (₹)","Amount (₹)","Notes"];
    const COLS=headers.length;
    const emptyRow=()=>Array(COLS).fill("");
    const out=[];
    const ordersByDate={};
    rows.forEach(o=>{const d=o.order_date||"Unknown";(ordersByDate[d]=ordersByDate[d]||[]).push(o);});

    Object.keys(ordersByDate).sort().forEach(date=>{
      let dayTotal=0;
      ordersByDate[date].forEach(o=>{
        const num = o._src==="dw" ? o.dw_number : o.wo_number;
        const lines=linesById[o.id]||[];
        const custName=o.customers?.name||"";
        const notes=o.notes||"";
        let orderTotal=0;
        if(lines.length===0){
          out.push([fmtDMY(o.order_date)||"",num||"",custName,"","","","",notes]);
        } else {
          lines.forEach((l,i)=>{
            const qty=parseFloat(l.quantity)||0;
            const rate=parseFloat(l.unit_price)||0;
            const lt=qty*rate;
            orderTotal+=lt;
            const pv=l.product_variants;
            const productName = pv ? `${pv.paper_type} · ${pv.color_count} · ${pv.micron}` : (l.products?.name||l.description||"");
            out.push([i===0?(fmtDMY(o.order_date)||""):"",i===0?(num||""):"",i===0?custName:"",productName,qty,rate.toFixed(2),lt.toFixed(2),i===0?notes:""]);
          });
        }
        dayTotal+=orderTotal;
        if(showDailyTotal){const r=emptyRow();r[2]=`ORDER TOTAL — ${num}`;r[6]=orderTotal.toFixed(2);out.push(r);}
        out.push(emptyRow());
      });
      if(showDailyTotal){const r=emptyRow();r[2]=`DAILY TOTAL — ${date}`;r[6]=dayTotal.toFixed(2);out.push(r);out.push(emptyRow());}
    });

    const csv=[headers,...out].map(r=>r.map(v=>`"${String(v).replace(/"/g,'""')}"`).join(",")).join("\n");
    const blob=new Blob(["﻿"+csv],{type:"text/csv;charset=utf-8;"});
    const url=URL.createObjectURL(blob);
    const link=document.createElement("a");
    const who=(picked?.name||text.trim()||"all-customers").replace(/[^A-Za-z0-9]+/g,"-").replace(/^-|-$/g,"");
    link.href=url;link.download=`partywise-${who}-${new Date().toISOString().slice(0,10)}.csv`;link.click();
    URL.revokeObjectURL(url);
  };

  const totalPages = Math.max(1, Math.ceil(rows.length/PAGE_SIZE));
  const curPage = Math.min(page, totalPages);
  const shown = rows.slice((curPage-1)*PAGE_SIZE, curPage*PAGE_SIZE);
  const COLSPAN = 13;
  // Same OUT / CTR / TPF / PRV pills as the Work Orders page; they filter by
  // the customer's type, so direct work (which has no series) is filtered too.
  const TYPE_OF = { OUT: "outstation", CTR: "counter", TPF: "tpf", PRV: "prv" };

  return (
    <div>
      <div className="page-header"><div><div className="page-title">Partywise</div><div className="page-subtitle">One customer or all customers · work orders and direct work in one list, oldest first</div></div></div>

      <div style={{background:"#fff",border:"1px solid var(--paper3)",borderRadius:10,padding:"16px 20px",marginBottom:20,boxShadow:"var(--shadow)"}}>
        <div style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr",gap:12,marginBottom:12}}>
          <div className="form-group"><label>Customer</label>
            <AutoComplete
              value={text}
              onChange={v=>{ setText(v); setPicked(null); }}
              onSelect={c=>{ setText(c.name); setPicked(c); }}
              items={customers}
              placeholder="Type customer name…"
              getKey={c=>c.id}
              getLabel={c=>c.name}
              renderItem={c=><div><div style={{fontWeight:500}}>{c.name}</div><div className="item-sub">{c.type}</div></div>}
            />
          </div>
          <div className="form-group"><label>From date</label><input type="date" value={fromDate} onChange={e=>setFromDate(e.target.value)} style={{padding:"8px 12px",border:"1px solid var(--paper3)",borderRadius:8,fontFamily:"var(--mono)",fontSize:13,background:"var(--paper)",outline:"none"}}/></div>
          <div className="form-group"><label>To date</label><input type="date" value={toDate} onChange={e=>setToDate(e.target.value)} style={{padding:"8px 12px",border:"1px solid var(--paper3)",borderRadius:8,fontFamily:"var(--mono)",fontSize:13,background:"var(--paper)",outline:"none"}}/></div>
        </div>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:8}}>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            {["all","not_invoiced","open","in_progress","completed","invoiced"].map(f=>(
              <button key={f} className={`filter-pill ${filter===f?"active":""}`} onClick={()=>setFilter(f)}>{f==="all"?"All statuses":f==="not_invoiced"?"Not invoiced":f==="in_progress"?"In progress":f.charAt(0).toUpperCase()+f.slice(1)}</button>
            ))}
            {SERIES.map(sr=>{
              const t = TYPE_OF[sr.code], on = typeFilter===t;
              return <button key={sr.code} className={`filter-pill ${on?"active":""}`} style={on?{background:seriesColors[sr.code],borderColor:seriesColors[sr.code],color:"#fff"}:{}} onClick={()=>setTypeFilter(on?"":t)} title={`Only ${t} customers (click again to clear)`}>{sr.code}</button>;
            })}
          </div>
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            {(text||fromDate||toDate||typeFilter||filter!=="all")&&<button className="btn btn-ghost btn-sm" onClick={()=>{setText("");setPicked(null);setFromDate("");setToDate("");setTypeFilter("");setFilter("all");}}>✕ Clear</button>}
            {ready&&!loading&&<span style={{fontSize:12,color:"var(--ink3)"}}>{woCount} work order{woCount!==1?"s":""} · {dwCount} direct work</span>}
            {ready&&!loading&&<span style={{fontSize:13,fontWeight:600,fontFamily:"var(--mono)",background:"var(--accent-l)",color:"var(--accent)",padding:"4px 10px",borderRadius:8}} title={amounts?`Work orders ${fmt(woTotal)} · Direct work ${fmt(dwTotal)}`:""}>Total {amounts?fmt(grandTotal):"…"}</span>}
            <label style={{display:"flex",alignItems:"center",gap:6,fontSize:12,color:"var(--ink2)",cursor:"pointer",userSelect:"none"}}>
              <input type="checkbox" checked={showDailyTotal} onChange={e=>setShowDailyTotal(e.target.checked)} style={{width:13,height:13,cursor:"pointer",accentColor:"var(--accent)"}}/>
              Daily totals
            </label>
            <button className="btn btn-ghost btn-sm" onClick={downloadCSV}>⬇ CSV</button>
          </div>
        </div>
      </div>

      {!ready&&(
        <div className="card"><div className="empty-state"><div className="icon">🔎</div><p>{
          !datesReady ? ((fromDate && toDate && fromDate>toDate)
            ? "The From date is after the To date — please correct the date range."
            : "Choose a From and To date. Pick a customer too, or leave the customer empty to see everyone's orders for those dates (up to 31 days).")
          : (!hasCustomer && !allParties) ? "Type at least 2 letters of the customer name (or pick one from the list), or clear the box to see all customers."
          : `For all customers, please choose a range of up to ${MAX_ALL_DAYS} days (you chose ${rangeDays}) — or pick a customer to use a longer range.`
        }</p></div></div>
      )}

      {ready&&(
        <>
          <div className="card">
            <table>
              <thead><tr><th>Date</th><th>Type</th><th>Number</th><th>Customer</th><th>Series</th><th>Book type</th><th>Lamination</th><th>Size</th><th>Color</th><th>Micron</th><th>Sheets</th><th style={{textAlign:"right"}}>Amount</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>
                {loading&&<tr className="loading-row"><td colSpan={COLSPAN+1}><span className="spinner"/></td></tr>}
                {!loading&&rows.length===0&&<tr><td colSpan={COLSPAN+1}><div className="empty-state"><div className="icon">📋</div><p>No work orders or direct work for this customer.</p></div></td></tr>}
                {!loading&&shown.map(o=>{
                  const isDW = o._src==="dw";
                  return (
                  <tr key={o._src+o.id}>
                    <td className="mono-text" style={{fontSize:12}}>{fmtDMY(o.order_date)}</td>
                    <td><span className={`badge ${isDW?"badge-amber":"badge-blue"}`}>{isDW?"Direct":"Work order"}</span></td>
                    <td><span className="wo-number">{(isDW?o.dw_number:o.wo_number)||"—"}</span></td>
                    <td><div style={{fontWeight:500}}>{o.customers?.name||"—"}</div><div style={{fontSize:11,color:"var(--ink3)"}}>{o.customers?.type}</div></td>
                    {isDW ? (
                      <td colSpan={7} style={{fontSize:12,color:"var(--ink3)",maxWidth:320,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{o.notes||"—"}</td>
                    ) : (<>
                      <td><span className="badge" style={{background:seriesColors[o.series_code]+"20",color:seriesColors[o.series_code]}}>{o.series_code}</span></td>
                      <td>{o.book_type?<span className="badge badge-blue">{o.book_type}</span>:<span style={{color:"var(--ink3)"}}>—</span>}</td>
                      <td>{o.finish_type?<span className="badge badge-gray">{o.finish_type}</span>:<span style={{color:"var(--ink3)"}}>—</span>}</td>
                      <td className="mono-text" style={{fontSize:12}}>{o.size||"—"}</td>
                      <td style={{fontSize:12}}>{o.color_count?<span className="badge badge-blue">{o.color_count}</span>:"—"}</td>
                      <td style={{fontSize:12}}>{o.micron?<span className="badge badge-gray">{o.micron}</span>:"—"}</td>
                    </>)}
                    {!isDW&&<td className="mono-text" style={{fontSize:12}}>{o.total_sheets?o.total_sheets.toLocaleString("en-IN"):"—"}</td>}
                    <td className="mono-text" style={{fontSize:12,textAlign:"right",fontWeight:500}}>{amounts?fmt(amountOf(o)):"…"}</td>
                    <td><span className={`badge ${statusColors[o.status]||"badge-gray"}`}>{o.status?.replace("_"," ")}</span></td>
                    <td><div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                      <button className="btn btn-ghost btn-sm" onClick={()=>isDW?setDwModal(o):setWoModal(o)}>{o.status==="invoiced"?"View":"Edit"}</button>
                      {o.status==="open"&&<button className="btn btn-ghost btn-sm" onClick={()=>updateStatus(o,"in_progress")}>▶ Start</button>}
                      {o.status==="in_progress"&&<button className="btn btn-primary btn-sm" onClick={()=>updateStatus(o,"completed")}>✓ Complete</button>}
                    </div></td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {!loading&&rows.length>PAGE_SIZE&&(
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginTop:14,padding:"0 4px"}}>
              <span style={{fontSize:12,color:"var(--ink3)"}}>Showing {(curPage-1)*PAGE_SIZE+1}–{Math.min(curPage*PAGE_SIZE,rows.length)} of {rows.length}</span>
              <div style={{display:"flex",gap:6,alignItems:"center"}}>
                <button className="btn btn-ghost btn-sm" disabled={curPage<=1} style={{opacity:curPage<=1?0.4:1}} onClick={()=>setPage(p=>Math.max(1,p-1))}>← Prev</button>
                <span style={{fontSize:12,color:"var(--ink3)",fontFamily:"var(--mono)"}}>Page {curPage} of {totalPages}</span>
                <button className="btn btn-ghost btn-sm" disabled={curPage>=totalPages} style={{opacity:curPage>=totalPages?0.4:1}} onClick={()=>setPage(p=>Math.min(totalPages,p+1))}>Next →</button>
              </div>
            </div>
          )}
        </>
      )}

      {woModal&&<OrderForm mode="wo" record={woModal} onClose={()=>setWoModal(null)} toast={toast} sequences={sequences} estSequences={estSequences} reloadSeqs={reloadSeqs} onSave={()=>{setWoModal(null);load();toast("Work order updated!");}}/>}
      {dwModal&&<DirectWorkModal record={dwModal} onClose={()=>setDwModal(null)} toast={toast} onSave={()=>{setDwModal(null);load();toast("Updated!");}}/>}
    </div>
  );
}

// ── ESTIMATES PAGE ────────────────────────────────────────────
function EstimatesPage({ toast, sequences, estSequences, reloadSeqs }) {
  const [estimates, setEstimates] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState("");
  const [filter, setFilter]       = useState("all");
  const [modal, setModal]         = useState(null);
  const [fromDate, setFromDate]   = useState("");
  const [toDate, setToDate]       = useState("");

  const load=useCallback(async()=>{
    setLoading(true);
const data = await fetchAll("estimates", {select:"*, customers(id,name,type)", eq:{is_deleted:false}, order:"created_at", ascending:false});
    setEstimates(data||[]);setLoading(false);
  },[]);
  useEffect(()=>{load();},[load]);

  const filtered=estimates.filter(e=>{
    const s=search.toLowerCase();
    return((e.est_number||"").toLowerCase().includes(s)||(e.customers?.name||"").toLowerCase().includes(s))&&
      (filter==="all"||e.status===filter||e.series_code===filter)&&
      (!fromDate||e.order_date>=fromDate)&&(!toDate||e.order_date<=toDate);
  });

  const statusColors={draft:"badge-gray",sent:"badge-blue",approved:"badge-green",rejected:"badge-red"};
  const seriesColors={OUT:"#9a4a00",CTR:"#1f6e4a",TPF:"#0d5bb8",PRV:"#6a1b9a"};
  const counts={draft:estimates.filter(e=>e.status==="draft").length,sent:estimates.filter(e=>e.status==="sent").length,approved:estimates.filter(e=>e.status==="approved"&&!e.converted_to_wo).length};

  return(
    <div>
      <div className="page-header"><div><div className="page-title">Estimates</div><div className="page-subtitle">Quotes · {estimates.length} total</div></div><button className="btn btn-primary" onClick={()=>setModal("new")}>+ New estimate</button></div>
      <div className="stats-row">
        <div className="stat-card" style={{borderLeftColor:"var(--ink3)"}}><div className="stat-value">{counts.draft}</div><div className="stat-label">Draft</div></div>
        <div className="stat-card" style={{borderLeftColor:"#0d5bb8"}}><div className="stat-value">{counts.sent}</div><div className="stat-label">Sent</div></div>
        <div className="stat-card"><div className="stat-value">{counts.approved}</div><div className="stat-label">Approved (pending WO)</div></div>
        <div className="stat-card" style={{borderLeftColor:"#6a1b9a"}}><div className="stat-value">{estimates.filter(e=>e.converted_to_wo).length}</div><div className="stat-label">Converted to WO</div></div>
      </div>
      <div style={{background:"#fff",border:"1px solid var(--paper3)",borderRadius:10,padding:"16px 20px",marginBottom:20,boxShadow:"var(--shadow)"}}>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12,marginBottom:12}}>
          <div className="form-group"><label>Search</label><div className="search-wrap" style={{maxWidth:"100%"}}><span className="search-icon">🔍</span><input placeholder="Estimate number or customer…" value={search} onChange={e=>setSearch(e.target.value)}/></div></div>
          <div className="form-group"><label>From date</label><input type="date" value={fromDate} onChange={e=>setFromDate(e.target.value)} style={{padding:"8px 12px",border:"1px solid var(--paper3)",borderRadius:8,fontFamily:"var(--mono)",fontSize:13,background:"var(--paper)",outline:"none"}}/></div>
          <div className="form-group"><label>To date</label><input type="date" value={toDate} onChange={e=>setToDate(e.target.value)} style={{padding:"8px 12px",border:"1px solid var(--paper3)",borderRadius:8,fontFamily:"var(--mono)",fontSize:13,background:"var(--paper)",outline:"none"}}/></div>
        </div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
          {["all","draft","sent","approved","rejected"].map(f=>(
            <button key={f} className={`filter-pill ${filter===f?"active":""}`} onClick={()=>setFilter(f)}>{f==="all"?"All":f.charAt(0).toUpperCase()+f.slice(1)}</button>
          ))}
          {SERIES.map(s=>(
            <button key={s.code} className={`filter-pill ${filter===s.code?"active":""}`} style={filter===s.code?{background:s.color,borderColor:s.color,color:"#fff"}:{}} onClick={()=>setFilter(s.code)}>{s.code}</button>
          ))}
          {(search||fromDate||toDate||filter!=="all")&&<button className="btn btn-ghost btn-sm" onClick={()=>{setSearch("");setFromDate("");setToDate("");setFilter("all");}}>✕ Clear</button>}
          <span style={{fontSize:12,color:"var(--ink3)",marginLeft:"auto"}}>{filtered.length} estimate{filtered.length!==1?"s":""}</span>
        </div>
      </div>
      <div className="card">
        <table>
          <thead><tr><th>Est. number</th><th>Customer</th><th>Series</th><th>Book type</th><th>Lamination</th><th>Sheets</th><th>Date</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {loading&&<tr className="loading-row"><td colSpan={9}><span className="spinner"/></td></tr>}
            {!loading&&filtered.length===0&&<tr><td colSpan={9}><div className="empty-state"><div className="icon">📄</div><p>No estimates yet.</p></div></td></tr>}
            {!loading&&filtered.map(e=>(
              <tr key={e.id}>
                <td><span className="wo-number">{e.est_number}</span>{e.converted_to_wo&&<div style={{fontSize:11,color:"var(--accent)",marginTop:3}}>✓ Converted to WO</div>}</td>
                <td><div style={{fontWeight:500}}>{e.customers?.name||"—"}</div><div style={{fontSize:11,color:"var(--ink3)"}}>{e.customers?.type}</div></td>
                <td><span className="badge" style={{background:seriesColors[e.series_code]+"20",color:seriesColors[e.series_code]}}>{e.series_code}</span></td>
                <td>{e.book_type?<span className="badge badge-blue">{e.book_type}</span>:<span style={{color:"var(--ink3)"}}>—</span>}</td>
                <td>{e.finish_type?<span className="badge badge-gray">{e.finish_type}</span>:<span style={{color:"var(--ink3)"}}>—</span>}</td>
                <td className="mono-text" style={{fontSize:12}}>{e.total_sheets?e.total_sheets.toLocaleString("en-IN"):"—"}</td>
                <td className="mono-text" style={{fontSize:12}}>{fmtDMY(e.order_date)}</td>
                <td><span className={`badge ${statusColors[e.status]||"badge-gray"}`}>{e.status}</span></td>
                <td><button className="btn btn-ghost btn-sm" onClick={()=>setModal(e)}>Edit</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {modal&&<OrderForm mode="estimate" record={modal==="new"?null:modal} onClose={()=>setModal(null)} toast={toast} sequences={sequences} estSequences={estSequences} reloadSeqs={reloadSeqs} onSave={()=>{setModal(null);load();toast(modal==="new"?"Estimate created!":"Estimate updated!");}}/>}
    </div>
  );
}

// ── DASHBOARD ─────────────────────────────────────────────────
function DashboardPage() {
  const [counts, setCounts] = useState({customers:0,products:0,orders:0,open:0,estimates:0,invoices:0});
  useEffect(()=>{
    Promise.all([
      supabase.from("customers").select("id",{count:"exact",head:true}).eq("is_active",true),
      supabase.from("products").select("id",{count:"exact",head:true}).eq("is_active",true),
      supabase.from("work_orders").select("id",{count:"exact",head:true}).eq("is_deleted",false),
      supabase.from("work_orders").select("id",{count:"exact",head:true}).eq("status","open").eq("is_deleted",false),
      supabase.from("estimates").select("id",{count:"exact",head:true}).eq("is_deleted",false),
      supabase.from("invoices").select("id",{count:"exact",head:true}).eq("is_deleted",false),
    ]).then(([c,p,w,o,e,i])=>setCounts({customers:c.count||0,products:p.count||0,orders:w.count||0,open:o.count||0,estimates:e.count||0,invoices:i.count||0}));
  },[]);
  return(
    <div>
      <div className="page-header"><div><div className="page-title">Dashboard</div><div className="page-subtitle">Super Foto DG X Press · {getCurrentFYLabel()}</div></div></div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:16,marginBottom:28}}>
        {[{label:"Customers",value:counts.customers,color:"var(--accent2)"},{label:"Products",value:counts.products,color:"var(--warn)"},{label:"Work orders",value:counts.orders,color:"#0d5bb8"},{label:"Open orders",value:counts.open,color:"#b7101d"},{label:"Estimates",value:counts.estimates,color:"#6a1b9a"},{label:"Invoices",value:counts.invoices,color:"var(--accent)"}].map(s=>(
          <div key={s.label} className="stat-card" style={{borderLeftColor:s.color}}>
            <div className="stat-value">{s.value}</div>
            <div className="stat-label">{s.label}</div>
          </div>
        ))}
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:20}}>
        <div className="card" style={{padding:24}}>
          <div style={{fontSize:12,color:"var(--ink3)",letterSpacing:"0.6px",textTransform:"uppercase",marginBottom:14}}>Series</div>
          {SERIES.map(s=>(
            <div key={s.code} style={{display:"flex",alignItems:"center",gap:12,marginBottom:10,padding:"10px 14px",background:"var(--paper)",borderRadius:8,borderLeft:`3px solid ${s.color}`}}>
              <span style={{fontFamily:"var(--mono)",fontWeight:500,minWidth:44,color:s.color}}>{s.code}</span>
              <span style={{fontSize:12,color:"var(--ink3)"}}>{s.label}</span>
            </div>
          ))}
        </div>
        <div className="card" style={{padding:24}}>
          <div style={{fontSize:12,color:"var(--ink3)",letterSpacing:"0.6px",textTransform:"uppercase",marginBottom:14}}>Build progress</div>
          {[
            {label:"Database schema",done:true},{label:"Customer master",done:true},{label:"Product master",done:true},
            {label:"Customer pricing engine",done:true},{label:"Work order entry",done:true},{label:"Estimates module",done:true},
            {label:"Invoice generation",done:true},{label:"Payments & reconciliation",done:false},{label:"Reports & ledger",done:false},
          ].map(s=>(
            <div key={s.label} style={{display:"flex",alignItems:"center",gap:10,marginBottom:7}}>
              <span style={{fontSize:14}}>{s.done?"✅":"⬜"}</span>
              <span style={{fontSize:13,color:s.done?"var(--accent)":"var(--ink3)"}}>{s.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── ROOT APP ──────────────────────────────────────────────────
function App() {
  const [page, setPage] = useState("dashboard");
  const { toasts, show: toast } = useToast();
const [sequences, setSequences]       = useState({});
const [estSequences, setEstSequences] = useState({});

const loadSequences = useCallback(()=>{
  supabase.from("sequences").select("series_code,alpha_part,num_part,current_number").then(({data})=>{
    const m={}; (data||[]).forEach(x=>{m[x.series_code]=x;}); setSequences(m);
  });
  supabase.from("estimate_sequences").select("series_code,alpha_part,num_part,current_number").then(({data})=>{
    const m={}; (data||[]).forEach(x=>{m[x.series_code]=x;}); setEstSequences(m);
  });
},[]);

useEffect(()=>{ loadSequences(); },[loadSequences]);

const { user, logout, setChangePwModal } = useAuth();
  

  const NAV=[
    {id:"dashboard",  icon:"◼",label:"Dashboard",  section:"Overview"},
    {id:"work-orders",icon:"📋",label:"Work orders",section:"Operations"},
{id:"direct-work", icon:"🛒", label:"Direct work", section:null},
    {id:"estimates",  icon:"📄",label:"Estimates",  section:null},
{id:"sales", icon:"📈", label:"Sales", section:null},
   {id:"invoices",   icon:"🧾",label:"Invoices",    section:"Accounting"},
{id:"payments",   icon:"💳",label:"Payments",    section:null},
{id:"ledger",     icon:"📒",label:"Ledger",      section:null},
{id:"recon",      icon:"📊",label:"Recon",        section:null},
{id:"purchases",   icon:"🛍️",label:"Purchases",    section:null},
{id:"partywise",  icon:"🔎",label:"Partywise",    section:"Reports"},
    {id:"customers",  icon:"👤",label:"Customers",  section:"Master data"},
    {id:"products",   icon:"📦",label:"Products",   section:null},
{id:"unmatched", icon:"⚠️", label:"Unmatched", section:null},
{id:"historical", icon:"📥", label:"Historical data", section:"Migration"},
{id:"temp-ledger", icon:"📑", label:"Temp Ledger", section:null},
{id:"job-sales", icon:"📊", label:"Job Sales", section:null},
{id:"users", icon:"👥", label:"Users", section:"System"},

  ];
  let lastSection=null;

  return(
    <>
      <style>{css}</style>
      <div className="app">
        <aside className="sidebar">
          <div className="sidebar-brand"><h1>WorkOrder<br/>Manager</h1><p>{getCurrentFYLabel()}</p></div>
          <nav className="sidebar-nav">
            {NAV.filter(item=>canAccess(user?.role||"user", item.id)).map(item=>(
              <div key={item.id}>
                {item.section&&item.section!==lastSection&&(()=>{lastSection=item.section;return<div className="nav-section">{item.section}</div>;})()}
                <div className={`nav-item ${page===item.id?"active":""}`} onClick={()=>setPage(item.id)}>
                  <span className="nav-icon">{item.icon}</span>{item.label}
                </div>
              </div>
            ))}
          </nav>
 <div className="sidebar-footer">
  <div style={{marginBottom:8}}>
    <div style={{fontWeight:500,fontSize:13,color:"var(--ink)"}}>{user?.fullName||user?.username}</div>
    <div style={{fontSize:11,color:"var(--ink3)",textTransform:"capitalize",marginTop:1}}>{user?.role}</div>
  </div>
  <div style={{display:"flex",gap:6}}>
    <button style={{padding:"5px 8px",fontSize:11,borderRadius:6,cursor:"pointer",border:"1px solid var(--paper3)",background:"transparent",color:"var(--ink2)",fontFamily:"var(--mono)",flex:1}} onClick={()=>setChangePwModal(true)}>🔑 Password</button>
    <button style={{padding:"5px 8px",fontSize:11,borderRadius:6,cursor:"pointer",border:"1px solid var(--danger-l)",background:"transparent",color:"var(--danger)",fontFamily:"var(--mono)",flex:1}} onClick={logout}>Sign out</button>
  </div>
</div>
        </aside>
        <main className="main">
          {page==="dashboard"   &&<DashboardPage/>}
{page==="work-orders" && <WorkOrdersPage toast={toast} sequences={sequences} estSequences={estSequences} reloadSeqs={loadSequences}/>}
{page==="estimates"   && <EstimatesPage  toast={toast} sequences={sequences} estSequences={estSequences} reloadSeqs={loadSequences}/>}
{page==="sales" && <SalesPage toast={toast}/>}
          {page==="invoices"    &&<InvoicesPage   toast={toast}/>}
{page==="payments"    &&<PaymentsPage   toast={toast}/>}
{page==="ledger"      &&<LedgerPage     toast={toast}/>}
{page==="recon"       &&<ReconPage      toast={toast}/>}
{page==="purchases"   &&<PurchasesPage  toast={toast}/>}
{page==="unmatched" &&<UnmatchedCreditsPage toast={toast}/>}
          {page==="customers"   &&<CustomersPage  toast={toast}/>}
          {page==="products"    &&<ProductsPage   toast={toast}/>}
{page==="historical" && <HistoricalImportPage toast={toast}/>}
{page==="temp-ledger" && <TempLedgerPage toast={toast}/>}
{page==="job-sales" && <JobSalesPage toast={toast}/>}
{page==="direct-work" && <DirectWorkPage toast={toast}/>}
{page==="partywise" && <PartywisePage toast={toast} sequences={sequences} estSequences={estSequences} reloadSeqs={loadSequences}/>}
{page==="users" && <UsersPage toast={toast}/>}
        </main>
        <div className="toast-container">
          {toasts.map(t=><div key={t.id} className={`toast toast-${t.type}`}>{t.msg}</div>)}
        </div>
      </div>
    </>
  );
}
export default function AppWithAuth() {
  return (
    <AuthProvider toast={null}>
      <App />
    </AuthProvider>
  );
}